import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useEffect } from "react";

import { format } from "date-fns";
import { Calendar as CalendarIcon, CircleDashed } from "lucide-react";
import { ro } from "date-fns/locale";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useStore } from "@/store/store";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { api_update_profile } from "@/lib/chat";
import { Avatar } from "@/components/ui/Robo/Avatar";
function UserSetup() {
  const [date, setDate] = useState<Date[]>();
  const { setUserProfile } = useStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const formSchema = z.object({
    name: z.string().min(2, {
      message: "Numele trebuie sa aiba cel putin 2 caractere.",
    }),
    birthday: z.date({
      required_error: "Introdu o data de nastere valida.",
    }),
    class: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      class: "1",
    },
  });

  const classes = [1, 2, 3, 4, 5, 6, 7, 8];

  const { setValue, watch } = form;

  function handleDateSelect(selectedDate) {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();

    // Create a new Date object with the UTC equivalent of the local date
    const dateWithoutTimezone = new Date(Date.UTC(year, month, day));

    // Set the date and value using the adjusted Date object
    setDate(dateWithoutTimezone);
    setValue("birthday", dateWithoutTimezone, { shouldValidate: true });
  }

  const birthday = watch("birthday");

  useEffect(() => {
    if (birthday) {
      setDate(birthday);
    }
  }, [birthday]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    setIsLoading(true);
    api_update_profile(values, (data, error) => {
      if (data) {
        setUserProfile(data);
      }
      setIsLoading(false);
      window.location.href = "/";
    });
  }
  return (
    <div className="flex min-h-screen bg-x-blue-700 w-full">
      <div className="bg-white p-7 z-10 h-full flex min-h-screen items-center w-full md:w-[400px]">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8 w-full"
          >
            <div className="space-y-3">
              <div className="flex items-center gap-3 justify-center">
                <Avatar />{" "}
                <h1 className="text-3xl font-bold">Bine ai venit!</h1>
              </div>
              <div>
                <p className="text-lg">
                  Pentru a continua, te rugam sa completezi profilul.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nume</FormLabel>
                    <FormControl>
                      <Input placeholder="Andrei" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="birthday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zi de nastere </FormLabel>
                    <FormControl>
                      <div>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full justify-start text-left font-normal",
                                !date && "text-muted-foreground"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {date ? (
                                format(date, "dd MMM yyyy")
                              ) : (
                                <span>Selecteaza o zi</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent
                            align="start"
                            side="top"
                            className="w-auto p-0"
                          >
                            <DayPicker
                              locale={ro}
                              classNames={{
                                caption_label:
                                  "flex items-center text-sm font-medium",
                                dropdown: "rdp-dropdown bg-card",
                                dropdown_icon: "ml-2",
                                dropdown_year: "rdp-dropdown_year ml-3",
                                button: "",
                                button_reset: "",
                              }}
                              mode="single"
                              selected={date}
                              onSelect={handleDateSelect}
                              captionLayout="dropdown-buttons"
                              fromYear={2000}
                              toYear={new Date().getFullYear()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="class"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clasa</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Clasa">
                            {field.value}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map((item) => (
                            <SelectItem key={item} value={item.toString()}>
                              {item}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button disabled={isLoading} className="w-full gap-2" type="submit">
              <CircleDashed
                className={(!isLoading && "hidden") || "animate-spin"}
              />
              <span className={(isLoading && "hidden") || ""}>Salveaza</span>
            </Button>
          </form>
        </Form>
      </div>
      <div className="flex-1">
        <div className="max-h-screen h-screen items-end flex justify-end">
          <svg
            className="animated max-h-[700px] w-full"
            id="freepik_stories-learning"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 500 500"
            version="1.1"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <style
              dangerouslySetInnerHTML={{
                __html:
                  "svg#freepik_stories-learning:not(.animated) .animable {opacity: 0;}svg#freepik_stories-learning.animated #freepik--background-complete--inject-2 {animation: 1s 1 forwards cubic-bezier(.36,-0.01,.5,1.38) slideDown;animation-delay: 0s;}svg#freepik_stories-learning.animated #freepik--Elements--inject-2 {animation: 3s Infinite  linear floating;animation-delay: 0s;}            @keyframes slideDown {                0% {                    opacity: 0;                    transform: translateY(-30px);                }                100% {                    opacity: 1;                    transform: translateY(0);                }            }                    @keyframes floating {                0% {                    opacity: 1;                    transform: translateY(0px);                }                50% {                    transform: translateY(-10px);                }                100% {                    opacity: 1;                    transform: translateY(0px);                }            }        .animator-hidden { display: none; }",
              }}
            />
            <g
              id="freepik--background-complete--inject-2"
              className="animable"
              style={{ transformOrigin: "250px 193.89px" }}
            >
              <rect
                x="46.11"
                y="315.74"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "59.35px 321.335px",
                }}
                id="elt7i1thqwufm"
                className="animable"
              />
              <rect
                x="31.34"
                y="329.85"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "44.58px 335.445px",
                }}
                id="el5dil336bk7x"
                className="animable"
              />
              <rect
                x="366.38"
                y="147.56"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "379.62px 153.155px",
                }}
                id="elqumclsl8b5o"
                className="animable"
              />
              <rect
                x="351.6"
                y="161.67"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "364.84px 167.265px",
                }}
                id="el486954133cg"
                className="animable"
              />
              <rect
                x="336.89"
                y="147.56"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "350.13px 153.155px",
                }}
                id="elmijdscyylcf"
                className="animable"
              />
              <rect
                x="442.18"
                y="329.85"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "455.42px 335.445px",
                }}
                id="elscktp9pem2"
                className="animable"
              />
              <rect
                x="427.41"
                y="46.74"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "440.65px 52.335px",
                }}
                id="elewzyqzdukzr"
                className="animable"
              />
              <rect
                x="442.18"
                y="60.85"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "455.42px 66.445px",
                }}
                id="el09644p9rh96j"
                className="animable"
              />
              <rect
                x="46.11"
                y="46.74"
                width="26.48"
                height="11.19"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "59.35px 52.335px",
                }}
                id="elk99oznhqwvb"
                className="animable"
              />
              <rect
                x="49.91"
                y="145.59"
                width="172.13"
                height="10.22"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "135.975px 150.7px",
                }}
                id="el4shta48puns"
                className="animable"
              />
              <rect
                x="62.98"
                y="155.82"
                width="10.22"
                height="33.8"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "68.09px 172.72px",
                }}
                id="eljx29suhyei"
                className="animable"
              />
              <rect
                x="62.98"
                y="155.82"
                width="10.22"
                height="10.45"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "68.09px 161.045px",
                }}
                id="elqcho937v6a"
                className="animable"
              />
              <rect
                x="198.76"
                y="155.82"
                width="10.22"
                height="33.8"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "203.87px 172.72px",
                }}
                id="elzk3b3rkhgvm"
                className="animable"
              />
              <rect
                x="198.76"
                y="155.82"
                width="10.22"
                height="10.45"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "203.87px 161.045px",
                }}
                id="elxoqcm6dg85f"
                className="animable"
              />
              <g id="el34esinulmay">
                <rect
                  x="69.05"
                  y="104.82"
                  width="65.14"
                  height="16.4"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "101.62px 113.02px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elkz08ri6g3lp"
                />
              </g>
              <g id="el9a2duw2kn1u">
                <rect
                  x="98.04"
                  y="72.35"
                  width="7.16"
                  height="16.4"
                  style={{
                    fill: "rgb(219, 219, 219)",
                    transformOrigin: "101.62px 80.55px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elhrtzxh6pd3w"
                />
              </g>
              <g id="eldztkho5d6d8">
                <rect
                  x="100.29"
                  y="86.43"
                  width="2.66"
                  height="16.4"
                  style={{
                    fill: "rgb(219, 219, 219)",
                    transformOrigin: "101.62px 94.63px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="eliy79a1lfft"
                />
              </g>
              <g id="elo99vlmsogdr">
                <rect
                  x="99.06"
                  y="124.25"
                  width="7.16"
                  height="18.45"
                  style={{
                    fill: "rgb(219, 219, 219)",
                    transformOrigin: "102.64px 133.475px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elaqhqhlc7t4"
                />
              </g>
              <g id="elbkadmg9bbfh">
                <rect
                  x="77.56"
                  y="101.87"
                  width="75.98"
                  height="11.46"
                  style={{
                    fill: "rgb(219, 219, 219)",
                    transformOrigin: "115.55px 107.6px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="el0n6l1qbukqvd"
                />
              </g>
              <g id="el5u2gmpvmbd6">
                <rect
                  x="100.89"
                  y="87.93"
                  width="29.22"
                  height="6.88"
                  style={{
                    fill: "rgb(235, 235, 235)",
                    transformOrigin: "115.5px 91.37px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elnu5prrb2a3"
                />
              </g>
              <g id="elqixpew2ptdq">
                <rect
                  x="133.8"
                  y="109.09"
                  width="75.98"
                  height="11.46"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "171.79px 114.82px",
                    transform: "rotate(44.67deg)",
                  }}
                  className="animable"
                  id="elpti4wi1olij"
                />
              </g>
              <g id="el7up6ctpdg4m">
                <rect
                  x="161.16"
                  y="115.26"
                  width="29.22"
                  height="6.88"
                  style={{
                    fill: "rgb(219, 219, 219)",
                    transformOrigin: "175.77px 118.7px",
                    transform: "rotate(44.67deg)",
                  }}
                  className="animable"
                  id="elkt5skwjs3bb"
                />
              </g>
              <g id="el4u7aroxoct">
                <rect
                  x="102.15"
                  y="107.02"
                  width="57.72"
                  height="19.47"
                  style={{
                    fill: "rgb(166, 166, 166)",
                    transformOrigin: "131.01px 116.755px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elj9tsx8pscoa"
                />
              </g>
              <g id="ellp463ar4n7b">
                <rect
                  x="128.32"
                  y="127.07"
                  width="5.38"
                  height="19.47"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "131.01px 136.805px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elzy66pg4ytd9"
                />
              </g>
              <g id="el0wni2tfjvh59">
                <rect
                  x="129.97"
                  y="119.8"
                  width="2.09"
                  height="19.47"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "131.015px 129.535px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="el2gn71yooq1h"
                />
              </g>
              <g id="elvu6wipk7zzm">
                <rect
                  x="59.57"
                  y="111.76"
                  width="54.24"
                  height="13.45"
                  style={{
                    fill: "rgb(166, 166, 166)",
                    transformOrigin: "86.69px 118.485px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elzp8ah1bumt"
                />
              </g>
              <g id="elhpp0gg2o2mq">
                <rect
                  x="84.17"
                  y="130.61"
                  width="5.06"
                  height="13.45"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "86.7px 137.335px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elfwhrr3zryyf"
                />
              </g>
              <g id="el9honmfi1spm">
                <rect
                  x="84.17"
                  y="98.35"
                  width="5.06"
                  height="13.45"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "86.7px 105.075px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="ellxx9gtqmw7a"
                />
              </g>
              <g id="el1cnvz4thz2h">
                <rect
                  x="85.71"
                  y="105.32"
                  width="1.96"
                  height="13.45"
                  style={{
                    fill: "rgb(199, 199, 199)",
                    transformOrigin: "86.69px 112.045px",
                    transform: "rotate(90deg)",
                  }}
                  className="animable"
                  id="elvjrulpd8hxf"
                />
              </g>
            </g>
            <g
              id="freepik--Table--inject-2"
              className="animable"
              style={{ transformOrigin: "250px 420.175px" }}
            >
              <polygon
                points="458.68 420.17 406.51 420.42 354.34 420.5 250 420.67 145.66 420.51 93.49 420.42 41.32 420.17 93.49 419.93 145.66 419.84 250 419.68 354.34 419.84 406.51 419.93 458.68 420.17"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "250px 420.175px",
                }}
                id="elk6b1f0ywyhn"
                className="animable"
              />
            </g>
            <g
              id="freepik--Elements--inject-2"
              className="animable"
              style={{ transformOrigin: "337.557px 201.987px" }}
            >
              <path
                d="M302,349.34s4.52-45.41-8-61.62c-12.12-15.75-48.85-19.59-55.29-43.35-6.1-22.5,15.54-29.32,13.87-47.54s-46.75-24.49-42.45-77.31c4.16-51,69.55-82,119-32.07,23.11,23.32,24.44,55.36,49.61,66.45s49-11.88,73.76,5.38,12.1,55.43-25.69,63.7c-30.68,6.71-43.83,7.86-51.4,21.41-8.83,15.81,2.28,23.79-5.41,36.78-7.21,12.16-30.45,16.35-43.68,27.07-16.11,13-19.5,40.86-19.5,40.86Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "337.557px 205.743px",
                }}
                id="el5e3vnpaet2"
                className="animable"
              />
              <g id="eldv607eb0uud">
                <g
                  style={{
                    opacity: "0.5",
                    transformOrigin: "337.557px 205.743px",
                  }}
                  className="animable"
                  id="eleud8bf6j6vj"
                >
                  <path
                    d="M302,349.34s4.52-45.41-8-61.62c-12.12-15.75-48.85-19.59-55.29-43.35-6.1-22.5,15.54-29.32,13.87-47.54s-46.75-24.49-42.45-77.31c4.16-51,69.55-82,119-32.07,23.11,23.32,24.44,55.36,49.61,66.45s49-11.88,73.76,5.38,12.1,55.43-25.69,63.7c-30.68,6.71-43.83,7.86-51.4,21.41-8.83,15.81,2.28,23.79-5.41,36.78-7.21,12.16-30.45,16.35-43.68,27.07-16.11,13-19.5,40.86-19.5,40.86Z"
                    style={{
                      fill: "rgb(255, 255, 255)",
                      transformOrigin: "337.557px 205.743px",
                    }}
                    id="el4p60csriguh"
                    className="animable"
                  />
                </g>
              </g>
              <path
                d="M238,213.31a85.94,85.94,0,0,0-5.76,14.83,34.27,34.27,0,0,0-1,14.2,27.51,27.51,0,0,0,1.88,6.72,38.21,38.21,0,0,0,3.41,6.46c2.69,4.27,6.19,8.28,9.81,12.64a34.21,34.21,0,0,1-14.67-8.65,29.53,29.53,0,0,1-5.4-7.19c-.35-.68-.71-1.36-1-2.07l-.84-2.16a32.22,32.22,0,0,1-1.08-4.54,27.79,27.79,0,0,1,.17-9.24,30.44,30.44,0,0,1,2.94-8.48,34,34,0,0,1,5-7.13A27.13,27.13,0,0,1,238,213.31Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "234.692px 240.735px",
                }}
                id="elvdv5og0kqhn"
                className="animable"
              />
              <g id="elaxibqs7isgi">
                <path
                  d="M238,213.31a85.94,85.94,0,0,0-5.76,14.83,34.27,34.27,0,0,0-1,14.2,27.51,27.51,0,0,0,1.88,6.72,38.21,38.21,0,0,0,3.41,6.46c2.69,4.27,6.19,8.28,9.81,12.64a34.21,34.21,0,0,1-14.67-8.65,29.53,29.53,0,0,1-5.4-7.19c-.35-.68-.71-1.36-1-2.07l-.84-2.16a32.22,32.22,0,0,1-1.08-4.54,27.79,27.79,0,0,1,.17-9.24,30.44,30.44,0,0,1,2.94-8.48,34,34,0,0,1,5-7.13A27.13,27.13,0,0,1,238,213.31Z"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    opacity: "0.5",
                    transformOrigin: "234.692px 240.735px",
                  }}
                  className="animable"
                  id="ellak9j3woash"
                />
              </g>
              <path
                d="M358.8,295.6c3.52-1.64,6.69-3,9.55-4.48a28,28,0,0,0,7.19-5,18.55,18.55,0,0,0,4.32-7A89,89,0,0,0,382.42,269a17.87,17.87,0,0,1,3.66,11,16.5,16.5,0,0,1-1.23,6.23,16.71,16.71,0,0,1-3.63,5.47,19.38,19.38,0,0,1-11,5.4,22.27,22.27,0,0,1-5.82.08A17.23,17.23,0,0,1,358.8,295.6Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "372.44px 283.167px",
                }}
                id="el6a0z4zxp9y"
                className="animable"
              />
              <g id="el9oazk23bdit">
                <path
                  d="M358.8,295.6c3.52-1.64,6.69-3,9.55-4.48a28,28,0,0,0,7.19-5,18.55,18.55,0,0,0,4.32-7A89,89,0,0,0,382.42,269a17.87,17.87,0,0,1,3.66,11,16.5,16.5,0,0,1-1.23,6.23,16.71,16.71,0,0,1-3.63,5.47,19.38,19.38,0,0,1-11,5.4,22.27,22.27,0,0,1-5.82.08A17.23,17.23,0,0,1,358.8,295.6Z"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    opacity: "0.5",
                    transformOrigin: "372.44px 283.167px",
                  }}
                  className="animable"
                  id="eljvvlibqblsg"
                />
              </g>
              <path
                d="M306.61,61.59a30.77,30.77,0,0,1,9.63,1,40.33,40.33,0,0,1,4.64,1.48c.77.28,1.53.59,2.28.89s1.48.69,2.21,1.07A56.1,56.1,0,0,1,341,77.81a64.61,64.61,0,0,1,6,7.75l1.31,2a17,17,0,0,1,1.23,2.1c.74,1.44,1.44,2.9,2.13,4.36a31.41,31.41,0,0,1,1.78,4.48c.52,1.53,1,3,1.47,4.58s.68,3.14,1,4.71.49,3.16.64,4.75c-2-2.6-3.54-5.28-5.19-7.87q-1.33-1.91-2.58-3.81c-.43-.63-.82-1.28-1.23-1.91s-.9-1.22-1.33-1.84q-1.33-1.81-2.6-3.66c-.4-.62-.9-1.18-1.36-1.77L340.77,90A41.75,41.75,0,0,0,338,86.56l-2.89-3.33a110.43,110.43,0,0,0-13-11.87A95.62,95.62,0,0,0,306.61,61.59Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "331.585px 87.0421px",
                }}
                id="elwbftjk0aozc"
                className="animable"
              />
              <g id="elry4ykx673p">
                <path
                  d="M306.61,61.59a30.77,30.77,0,0,1,9.63,1,40.33,40.33,0,0,1,4.64,1.48c.77.28,1.53.59,2.28.89s1.48.69,2.21,1.07A56.1,56.1,0,0,1,341,77.81a64.61,64.61,0,0,1,6,7.75l1.31,2a17,17,0,0,1,1.23,2.1c.74,1.44,1.44,2.9,2.13,4.36a31.41,31.41,0,0,1,1.78,4.48c.52,1.53,1,3,1.47,4.58s.68,3.14,1,4.71.49,3.16.64,4.75c-2-2.6-3.54-5.28-5.19-7.87q-1.33-1.91-2.58-3.81c-.43-.63-.82-1.28-1.23-1.91s-.9-1.22-1.33-1.84q-1.33-1.81-2.6-3.66c-.4-.62-.9-1.18-1.36-1.77L340.77,90A41.75,41.75,0,0,0,338,86.56l-2.89-3.33a110.43,110.43,0,0,0-13-11.87A95.62,95.62,0,0,0,306.61,61.59Z"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    opacity: "0.5",
                    transformOrigin: "331.585px 87.0421px",
                  }}
                  className="animable"
                  id="elp6yk27c1y4"
                />
              </g>
              <path
                d="M221.79,149.17c.07,0,1.14-.44,2.67-1.23a16.88,16.88,0,0,0,1.17,2,.66.66,0,0,0,.89.13.64.64,0,0,0,.13-.89,13.71,13.71,0,0,1-1.07-1.82c.79-.44,1.64-1,2.52-1.56,4.33-3,7-6.45,7.76-10.07.69-3.3-.48-6.61-1.84-9.83,1.17-.44,2.36-.84,3.51-1.22,3-1,5.65-1.89,7.35-3.23a5.07,5.07,0,0,0,1.54-1.84,7.45,7.45,0,0,0,.62-2.12h0s0-.08,0-.13v0c.3-2.44-.51-5-1.39-7.49l.19,0c5.74.76,11.69,1.55,15-3.91A11.09,11.09,0,0,0,262,97.61a32.93,32.93,0,0,0-2-6c5.8.13,11.74.13,15.63-6.09,2.61-4.19,1.45-10.64-3.47-19.18-.33-.56-.65-1.11-1-1.65l1.35-.23c6.07-1,11.3-1.88,12.55-5.4a.62.62,0,0,0-.38-.8.63.63,0,0,0-.81.38c-1,2.82-6.13,3.67-11.57,4.58l-1.87.31A85.84,85.84,0,0,0,264,54.86a.62.62,0,0,0-.89-.07.63.63,0,0,0-.06.89A86.3,86.3,0,0,1,269,63.74c-6.48,1.19-12.9,3.05-14.63,8.85v0c-1.69,5.71.82,11.38,3.25,16.86.12.27.24.53.35.8-5-.07-10.05.3-13.56,5.15-3.16,4.36-2,8.82-.62,12.86-4.72-.55-9.33-.57-12.3,3.36a7.85,7.85,0,0,0-1.47,3.19c-.69,3.31.81,6.81,2.28,10.22l0,.09c-3.93,1.59-7.5,3.78-8.93,7.71a19.2,19.2,0,0,0-.72,2.54,18.33,18.33,0,0,0-.07,7.11v0a.5.5,0,0,0,0,.12h0a20.64,20.64,0,0,0,.57,2.26c.22.68.46,1.31.7,1.86-1.5.78-2.56,1.19-2.59,1.21a.61.61,0,0,0-.39.46.54.54,0,0,0,0,.35A.63.63,0,0,0,221.79,149.17Zm48-84.28c2.61,4.18,5.13,9.15,5.75,13.5l-16.08-10.2C262.18,66.45,266,65.57,269.75,64.89Zm-14,7.65A8.09,8.09,0,0,1,258.31,69l17.33,11a8.56,8.56,0,0,1-1.16,4.84c-.16.25-.31.48-.48.71ZM255.3,79a13.93,13.93,0,0,1,.07-5.15l17.85,12.68A10.43,10.43,0,0,1,270,89a.57.57,0,0,0-.23-.28L255.58,79.1A.75.75,0,0,0,255.3,79Zm3.49,10a51.32,51.32,0,0,1-3.14-8.31l13,8.85c-2.75.93-5.86.86-9.08.8h-.23C259.18,89.87,259,89.43,258.79,89Zm-8.71,3.56c2.55-1,5.44-1,8.46-1,1.47,3.41,2.66,6.7,2.44,9.76Zm-4.21,3.08a10.05,10.05,0,0,1,2.95-2.49l.06.06,11.82,9.53.06,0a8.73,8.73,0,0,1-1,2.52,7.23,7.23,0,0,1-.68,1Zm-1.42,4.14a.62.62,0,0,0-.58-.13,9,9,0,0,1,1.25-3l13.07,10.53a7.54,7.54,0,0,1-3.36,1.66Zm-.75,1,9.49,8.29a35.35,35.35,0,0,1-7.17-.48l-.82-.11C244.31,105.94,243.47,103.35,243.7,100.78ZM236,109.89c2.38-.85,5.27-.64,8.31-.26a20.8,20.8,0,0,1,1.59,6.57Zm-3.45,2.53a7.52,7.52,0,0,1,2.1-1.91l.09.07,11,7a6,6,0,0,1-.46,1.43,3.7,3.7,0,0,1-.86,1.12l-12.53-6.77A7.69,7.69,0,0,1,232.51,112.42Zm8.22,9.7-9.28-3a8.57,8.57,0,0,1,0-4.52L243.27,121a16.64,16.64,0,0,1-2.46,1.16Zm-7.26,2.45c-.59-1.36-1.16-2.7-1.6-4l7,2.28-1.77.6c-1.19.4-2.4.8-3.6,1.25Zm-.63,1.75a30.87,30.87,0,0,1,1.56,4.4l-8.85.74C227.15,129.07,229.88,127.53,232.84,126.32Zm-8.23,7c.07-.19.14-.37.22-.55a.75.75,0,0,0,.21,0l9.61-.81a10.7,10.7,0,0,1,.14,2.18l-10.95,2A17.08,17.08,0,0,1,224.61,133.3Zm-1,4.1a.75.75,0,0,0,.26,0l10.7-2a10.8,10.8,0,0,1-.92,2.61.4.4,0,0,0-.15,0l-9.79,3.54A16.22,16.22,0,0,1,223.66,137.4Zm.76,7.09c-.17-.54-.31-1.07-.43-1.6l8.67-3.13a24.15,24.15,0,0,1-7.62,6.42C224.83,145.67,224.61,145.1,224.42,144.49Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "252.969px 102.41px",
                }}
                id="elo3zn2lb2t4f"
                className="animable"
              />
              <path
                d="M271.4,136.12l-.13,0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "271.335px 136.12px",
                }}
                id="el7ktogphjb6f"
                className="animable"
              />
              <g id="elpe2aqusopbe">
                <g
                  style={{
                    opacity: "0.7",
                    transformOrigin: "298.965px 88.6077px",
                  }}
                  className="animable"
                  id="elu7cnle6q0bn"
                >
                  <path
                    d="M321.74,100.91a22.67,22.67,0,1,1-27.1-23.73l1.29-17.93-1.83-.12.24-3.59,15.5,1-.23,3.6L307.77,60,306.47,78A22.67,22.67,0,0,1,321.74,100.91Z"
                    style={{
                      fill: "rgb(255, 255, 255)",
                      transformOrigin: "299.119px 88.8065px",
                    }}
                    id="el3gpc0406beg"
                    className="animable"
                  />
                  <path
                    d="M321.74,100.91a22.71,22.71,0,0,1-13.92,19.51,23.09,23.09,0,0,1-12.41,1.49,22.94,22.94,0,0,1-18.27-15.8,23.26,23.26,0,0,1-.3-12.63,22.8,22.8,0,0,1,6.45-10.9,23.18,23.18,0,0,1,11.26-5.86l-.38.43.62-9,.68-9,.43.49-1.83-.12-.45,0,0-.45.25-3.6,0-.44.43,0c5.17.35,10.33.77,15.5,1.16l.31,0,0,.31-.27,3.59,0,.27-.27,0-1.83-.13.27-.24L306.55,78l-.05-.08a22.73,22.73,0,0,1,15.24,23Zm0,0a22.63,22.63,0,0,0-15.29-22.86l-.06,0V78L307.52,60v-.25l.26,0,1.83.1-.29.25.2-3.6.29.33c-5.17-.29-10.34-.56-15.51-.89l.46-.41-.22,3.59-.42-.48,1.83.11.46,0,0,.47-.61,9-.67,9,0,.36-.35.07a22.18,22.18,0,0,0-10.84,5.57,22.42,22.42,0,0,0,23.88,37.08,22.61,22.61,0,0,0,14-19.39Z"
                    style={{
                      fill: "rgb(38, 50, 56)",
                      transformOrigin: "298.965px 88.6077px",
                    }}
                    id="el1jkkk6mcn8a"
                    className="animable"
                  />
                </g>
              </g>
              <path
                d="M317.68,100.64a18.61,18.61,0,1,1-37.13-2.45,18.37,18.37,0,0,1,2.09-7.4,41.12,41.12,0,0,0,11.46,3.76c8,1.38,14.09-2.68,21.07-4.55A18.6,18.6,0,0,1,317.68,100.64Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "299.099px 104.22px",
                }}
                id="el0zac72j86by"
                className="animable"
              />
              <path
                d="M280.55,98.19s.33-12.87,13.69-16.33"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "287.395px 90.025px",
                }}
                id="ely6sr3c4i8x"
                className="animable"
              />
              <path
                d="M287.22,166.06c0,.21.07.13.08.16C287.27,165.88,287.2,166.07,287.22,166.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.258px 166.118px",
                }}
                id="elxq6h7zf8vmm"
                className="animable"
              />
              <polygon
                points="286.88 162.76 286.92 162.81 286.82 162.33 286.88 162.76"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.87px 162.57px",
                }}
                id="el8o7vlsd1kvg"
                className="animable"
              />
              <path
                d="M286.73,159.66a11.4,11.4,0,0,0,.06,1.67l0,.1.11.24C286.83,161,286.72,160.13,286.73,159.66Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.811px 160.665px",
                }}
                id="ellvcmcm8vbg9"
                className="animable"
              />
              <path
                d="M286.92,158c-.05.1-.06-.36,0,.38v-.07C286.93,158.24,286.92,158.15,286.92,158Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.902px 158.165px",
                }}
                id="eloqn02vizpvg"
                className="animable"
              />
              <path
                d="M286.93,158.3c0,.33,0-.21,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.93px 158.33px",
                }}
                id="ela58xe21pizh"
                className="animable"
              />
              <path
                d="M286.83,156.8l.06.43c0-.1,0-.48,0-.34C286.89,156.45,286.86,156.54,286.83,156.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.86px 156.906px",
                }}
                id="el6p9v8exr0s7"
                className="animable"
              />
              <path
                d="M286.72,156.39l0,.23A.44.44,0,0,0,286.72,156.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.728px 156.505px",
                }}
                id="elhamij5sf5ie"
                className="animable"
              />
              <path
                d="M286.81,157.09l-.06-.47c0,.06-.06.07,0,.21C286.73,156.8,286.77,156.89,286.81,157.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.767px 156.855px",
                }}
                id="els45i096zoi"
                className="animable"
              />
              <path
                d="M286.8,157.36c0,.24,0,.55-.08.12C286.78,158.3,286.86,158.18,286.8,157.36Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.771px 157.699px",
                }}
                id="elsr1j8mqpt2"
                className="animable"
              />
              <path
                d="M286.59,159.5l0,.33.06.27A5.84,5.84,0,0,0,286.59,159.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.62px 159.8px",
                }}
                id="el1m3zjjj8r8l"
                className="animable"
              />
              <path
                d="M286.76,154.11s-.07.15-.1.19l0,.31A1.91,1.91,0,0,1,286.76,154.11Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.71px 154.36px",
                }}
                id="elr3884i7racj"
                className="animable"
              />
              <path
                d="M286.66,154.3l-.07-.48C286.61,154.25,286.63,154.32,286.66,154.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.625px 154.062px",
                }}
                id="elwe67gnx99wq"
                className="animable"
              />
              <polygon
                points="286.57 156.32 286.71 157.1 286.56 156.17 286.57 156.32"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.635px 156.635px",
                }}
                id="elq41n0kcd32"
                className="animable"
              />
              <polygon
                points="286.67 152.3 286.75 152.56 286.63 152.09 286.67 152.3"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.69px 152.325px",
                }}
                id="elnpznfrgesl"
                className="animable"
              />
              <path
                d="M286.6,151.1c.06.19.07-.28.11-.07a6,6,0,0,0-.13-.86A5.48,5.48,0,0,1,286.6,151.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.645px 150.658px",
                }}
                id="el4cmhjs92im7"
                className="animable"
              />
              <path
                d="M286.5,149.75c0,.14.05.27.08.42A3.1,3.1,0,0,0,286.5,149.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.54px 149.96px",
                }}
                id="elvphnf0bruh"
                className="animable"
              />
              <path
                d="M286.56,153a.68.68,0,0,0,.06-.37l-.06.19Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.592px 152.815px",
                }}
                id="elfrn9uuza9fa"
                className="animable"
              />
              <path
                d="M286.55,152.83h0l0-.41Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.55px 152.625px",
                }}
                id="elai20scb92da"
                className="animable"
              />
              <path
                d="M286.57,153.06V153A.09.09,0,0,0,286.57,153.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.567px 153.03px",
                }}
                id="el87igyktndau"
                className="animable"
              />
              <path
                d="M286.58,155.07c-.06-.27-.13-.85-.17-.29C286.47,155,286.49,154.66,286.58,155.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.495px 154.807px",
                }}
                id="elmwzyosbr95"
                className="animable"
              />
              <path
                d="M286.48,153.3v.5s.05-.1.07,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.515px 153.55px",
                }}
                id="elyddylyeqyr"
                className="animable"
              />
              <polygon
                points="286.45 153.05 286.49 153.3 286.48 153.11 286.45 153.05"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.47px 153.175px",
                }}
                id="elyxgy7txotor"
                className="animable"
              />
              <path
                d="M286.47,150.62a4.88,4.88,0,0,1-.07-.72c0,.29,0,.59,0,.87C286.44,150.81,286.49,150.93,286.47,150.62Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.437px 150.363px",
                }}
                id="elufe2bv302bo"
                className="animable"
              />
              <path
                d="M286.51,151.7v0S286.49,151.7,286.51,151.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.506px 151.7px",
                }}
                id="elzg5iymjcipm"
                className="animable"
              />
              <polygon
                points="286.26 147.25 286.25 147.29 286.27 147.3 286.26 147.25"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.26px 147.275px",
                }}
                id="elmv5t567v1c"
                className="animable"
              />
              <path
                d="M286.41,150.82a1.45,1.45,0,0,0,0,.47c0-.17,0-.34,0-.52C286.41,150.75,286.4,150.75,286.41,150.82Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.4px 151.024px",
                }}
                id="el9njgfhjb4xt"
                className="animable"
              />
              <path
                d="M286.43,149c0,.11,0,.24,0,.37A1.74,1.74,0,0,0,286.43,149Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.435px 149.185px",
                }}
                id="el5o3xihgtop2"
                className="animable"
              />
              <path
                d="M286.52,151.57v0C286.53,151.53,286.53,151.54,286.52,151.57Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.524px 151.557px",
                }}
                id="elgyy0ngb6ph6"
                className="animable"
              />
              <path
                d="M286.52,151.57c0-.11,0-.19-.06-.28a3.6,3.6,0,0,0,0,.36A.19.19,0,0,1,286.52,151.57Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.488px 151.47px",
                }}
                id="elernu6vjlrg"
                className="animable"
              />
              <path
                d="M285.74,146.47a2.18,2.18,0,0,1,.12,1.09c.07.49.29.16.39-.27,0,0-.06,0-.12-.51,0,.18,0,1.11-.12.69a2.32,2.32,0,0,1,0-.86c-.07.27-.17-.84-.29-.69l.07.58S285.75,146.3,285.74,146.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.985px 146.855px",
                }}
                id="el9nazxl070c"
                className="animable"
              />
              <path
                d="M286.29,148.24l0,.2a5.08,5.08,0,0,1,.06-.71c-.05-.34-.08-.41-.1-.43l0,1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.3px 147.87px",
                }}
                id="el5ilppn134xm"
                className="animable"
              />
              <path
                d="M286.36,149.37c-.09-.16-.14-.65-.21-.35a3.68,3.68,0,0,0,.14.78c-.07-1,0,.28.06-.43,0,.19,0,.37,0,.53v-.48h0s0,.06,0,0v0l0,0c0-.29,0-.05,0,0h0l-.09-1C286.31,148.65,286.33,148.91,286.36,149.37Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.255px 149.16px",
                }}
                id="el8v1kmldkxf"
                className="animable"
              />
              <path
                d="M286.22,154.27s0,0,0,0S286.22,154.15,286.22,154.27Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.22px 154.243px",
                }}
                id="elsavnosf83t"
                className="animable"
              />
              <path
                d="M286.25,153.87a1.24,1.24,0,0,1,.05.36c0,.2,0,0-.06,0s.08.48.09,0v-.07a4.07,4.07,0,0,1,.09-.84A1.62,1.62,0,0,0,286.25,153.87Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.323px 153.882px",
                }}
                id="eloltbw19zozm"
                className="animable"
              />
              <path
                d="M286.28,151.59l-.07-.81,0,.42,0,0C286.21,151.64,286.25,151.54,286.28,151.59Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.245px 151.185px",
                }}
                id="elv4e496mfn9l"
                className="animable"
              />
              <polygon
                points="286.3 147.15 286.38 147.25 286.25 146.79 286.3 147.15"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.315px 147.02px",
                }}
                id="elzks8gy2tm1"
                className="animable"
              />
              <polygon
                points="286.23 146.1 286.31 146.13 286.25 145.86 286.23 146.1"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.27px 145.995px",
                }}
                id="ellx4nmzah6s"
                className="animable"
              />
              <path
                d="M252.08,145.49l0,.24A1.66,1.66,0,0,1,252.08,145.49Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.078px 145.61px",
                }}
                id="elapyc6ynk3ni"
                className="animable"
              />
              <path
                d="M252,143.76l0,0C252,143.8,252,143.83,252,143.76Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252px 143.781px",
                }}
                id="elgv1w2k41dsd"
                className="animable"
              />
              <path
                d="M273.08,132.39c-.36,0-.75-.12-.7,0l.11.07C272.73,132.39,272.92,132.39,273.08,132.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "272.728px 132.398px",
                }}
                id="ellrzehvmabpm"
                className="animable"
              />
              <path
                d="M255.37,133.74h.1l.23-.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "255.535px 133.7px",
                }}
                id="el7iaw2sxo9vm"
                className="animable"
              />
              <path
                d="M255.14,134.06l-.46,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "254.91px 134.06px",
                }}
                id="el12b9c15k8uag"
                className="animable"
              />
              <path
                d="M252.63,154.91l.09.27C252.7,154.75,252.67,154.94,252.63,154.91Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.675px 155.037px",
                }}
                id="elf5ldza7e5ob"
                className="animable"
              />
              <path
                d="M285.67,135.3c0-.3,0-.54,0-.75C285.65,134.92,285.6,135.21,285.67,135.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.652px 134.925px",
                }}
                id="elpop9u197j58"
                className="animable"
              />
              <path
                d="M251.24,144.85c0,.15,0,.21,0,.2S251.25,144.82,251.24,144.85Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "251.242px 144.949px",
                }}
                id="elyeq5iiinnzg"
                className="animable"
              />
              <path
                d="M285.63,133.69v.16a6.76,6.76,0,0,1,.05.7A2.79,2.79,0,0,0,285.63,133.69Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.662px 134.12px",
                }}
                id="el5pumdpspw08"
                className="animable"
              />
              <path
                d="M273.92,166.45H274S274,166.45,273.92,166.45Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "273.96px 166.45px",
                }}
                id="ele9gmhik38m"
                className="animable"
              />
              <path
                d="M283.92,166.72l-.41,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.715px 166.72px",
                }}
                id="elepblr605pp"
                className="animable"
              />
              <path
                d="M286.06,141.85c0,.23,0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.06px 141.901px",
                }}
                id="elgw6yi6zwbu9"
                className="animable"
              />
              <path
                d="M285.81,143c0-.05,0-.09,0-.09A.19.19,0,0,1,285.81,143Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.813px 142.955px",
                }}
                id="elnb1jslopt7"
                className="animable"
              />
              <path
                d="M284.35,132.28h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.35px 132.28px",
                }}
                id="eli339sgr5um"
                className="animable"
              />
              <path
                d="M286,142.19c0-.09,0-.18,0-.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286px 142.045px",
                }}
                id="elhqpqhygvgic"
                className="animable"
              />
              <path
                d="M286.07,142.14v.2S286.09,142.34,286.07,142.14Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.074px 142.24px",
                }}
                id="elrhyoazh41ca"
                className="animable"
              />
              <path
                d="M286,141.54c0,.13,0,.25,0,.36,0,0,0-.06,0-.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286px 141.72px",
                }}
                id="elwolrqrou4f"
                className="animable"
              />
              <path
                d="M255.62,167.58h0C255.89,167.54,255.8,167.55,255.62,167.58Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "255.705px 167.567px",
                }}
                id="eljjj6p75ajk"
                className="animable"
              />
              <path
                d="M267.3,133.47h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "267.3px 133.47px",
                }}
                id="elzimsc5i0lpa"
                className="animable"
              />
              <path
                d="M266.5,133.57a1.93,1.93,0,0,1,.79-.1l-.28-.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.895px 133.415px",
                }}
                id="el2opa8cfkalp"
                className="animable"
              />
              <path
                d="M252.3,149.62l0-.57c.15.68.14,1.17.21.64-.25-.07-.14-2.19-.38-2.26l0-.37c0,.1,0,.12,0,.22.08-.72-.2-.52-.21-1.46,0-.32,0-.55.07-.33l-.1-.72c0-.21,0-.16,0,0l-.07-1.11h0c-.05-.15-.12-.51-.16-.68s0,.14.06.07l-.09-.61c0-.25.14.25.15.67.21-.58-.09-.5,0-1.74l.06.58a8.25,8.25,0,0,0,.1-1.49c0,.16-.15,1.32-.24.66a3.55,3.55,0,0,0-.06-.79c.08,0,0-.36,0-.79l.11,0a2.82,2.82,0,0,0-.18-.7,1.19,1.19,0,0,1,.14-.77c0-.85-.11.59-.18-.41a14.22,14.22,0,0,0-.18-2c0-.49.08.08.1-.1l-.11-.43c0-.1,0-.07,0-.05-.05-.36-.11,0-.17-.58-.06,0,0-.22,0-.37l.4,0a3.36,3.36,0,0,0,.63-.06c.18,0-.08.07,0,.11,1-.14.83-.11,1.63-.27l.69,0c-.51-.06.13-.19.69-.3l-.44-.08.9-.1c.11,0,0,.05-.13.11l.16,0c.33,0,.16.1-.06.18l-.33-.06c-.22.08-.42.17-.37.23l.29,0c-.06,0-.09,0-.06.05a.89.89,0,0,0,.26-.06l.21.12,0-.06,1.18,0a21.3,21.3,0,0,1,2.89-.5c-.33.1-.06.15-.08.26a10.88,10.88,0,0,0-1.72.11c.36,0,.28.05.19.07l1.23-.16c0,.09.27.05.59.07-.25-.16.92-.16,1.41-.24,0,0-.08.12-.69.14.68.1.86-.25,1.53-.17a.93.93,0,0,0-.29,0c.35-.07,1.2-.07,1,0H263a16.2,16.2,0,0,0,3.16-.3.75.75,0,0,1-.18.22l1.75-.59c-.15.13.46.41-.36.52a5.29,5.29,0,0,0,1-.06c-.16,0-.41-.16-.19-.19a8.16,8.16,0,0,0,1.17.14c-.24,0,0-.23.4-.24-.06,0,0,.2-.1.23l.94-.25c-.23.05,0,.17.19.22-.1,0,.82,0,1.07-.07l-.4,0c1.07,0,1-.33,2.11-.37-.13,0-.31.26.23.26a7,7,0,0,1,2-.66l.18,0,.72-.1a22.92,22.92,0,0,1-2.37.72,2.77,2.77,0,0,0,.71,0c.22,0-.32.09-.5.11l1.41,0c-.07-.11,1.06-.17,1-.27l-1.51.2c-.07-.11.86-.38,1.85-.41.26,0-.18.22-.15.26a5.35,5.35,0,0,1,1-.1l-.42.09a10.34,10.34,0,0,1,1.56-.13c.23,0,.7.12.73.05-.38-.12-.47-.53-.23-.64.08,0,1.21.07,1.48.12s-.56.28-.23.35a1,1,0,0,1,.3-.1c.24,0-.09.11.43.07.08-.13,1.06,0,.24-.12.51-.06.55,0,1.15,0-.2-.07.52-.29,1-.35,0,0,.09.06.15.08l.72,0c0,.61,0,1.09,0,1.82,0,.09,0,.56-.1.36a2.86,2.86,0,0,1,.1.57c-.08.59-.05-.67-.16-.22a5.7,5.7,0,0,1,.2,1.2,4.7,4.7,0,0,0-.09-1.42c.15,0,.34.73.46,1.75.08.81-.08.36,0,.5,0,1.53.32,1.29.28,2.56-.07.51-.19,0-.25.14-.07-.66,0-1,0-1.46v.55a4.27,4.27,0,0,1-.08-.89c-.06.43.12,2.58.1,3.76,0,.21.09.57.12.39l0-.37c.06-.34.14.7.15.22a9.45,9.45,0,0,1,.18,1.76l-.1.14a2,2,0,0,0,0-.66c.06.29-.09,1.09.07,1.4l.05-.5a12.12,12.12,0,0,1,.22,1.74c.09-.29,0-.14.08-.59-.14.39-.07-1.59-.21-1a12.45,12.45,0,0,0,.12-1.39s-.06.21-.07-.31c0,0,0-.08,0-.11v0h0a1,1,0,0,1,0,.27c-.09-.25-.13-1.09-.19-1.37s.05.13.08,0l-.08-.42c.07,0,.09-.83.17-.1l0-.66a7.26,7.26,0,0,0,0-1.25c0-.31-.12-.78,0-.51l-.1-1h0a28,28,0,0,1-.15-3.26c0-.18,0-.35-.05-.55a3.25,3.25,0,0,1,0-.33c0-.18,0-.37,0-.59l.05-.22,0-.44-1.23,0a1.47,1.47,0,0,0-.51.05,17.38,17.38,0,0,0-2.92.07l0,.09c-.54,0-1.05.07-1.71.14l.05.08c-.57.09-1.47,0-2.37.07a1.18,1.18,0,0,1,.45,0c-1.09-.12-2.27.24-3.25.16a2.52,2.52,0,0,1-.59,0h0l-.34,0,.28,0-.75.07-.12-.08h-.07c-.27,0-.21.05-.07.1l-.36.07c-.23-.12-.73.09-1,0-.19.08-1.41.12-1.55.28,0,0-.09,0,.06,0a4.39,4.39,0,0,0-.9.06l-.14-.1-.67.17c-.42,0-.59-.07-.18-.17-1,.17-.81.09-1.64.25l0-.07a5.36,5.36,0,0,1-1,.09c-.68-.1-3.33.15-5.06.11.34.19-.81,0-.87.11-.11,0-.35-.06-.07-.11a13.52,13.52,0,0,0-1.93.15c-.11,0,.18-.08.09-.1a1.46,1.46,0,0,1-.37,0c-.09,0,.13-.05.27-.08-.91.06-1,.21-1.05.35a2.14,2.14,0,0,0-1,.09c-.16,0-.43-.07.13-.12a9.52,9.52,0,0,0-1.95.15,1.87,1.87,0,0,0-.53,0c-.73,0-.68,0-1.65.07h.27a.89.89,0,0,1-.34.08c0,.13,0,.27,0,.43s0,.38,0,.54h0c0,1.29.07.55,0,1.82l.08-.07c.19.11,0,1.13.06,1.86l0-.27a8.2,8.2,0,0,1-.19,1.54c0,.09,0-.14-.05-.26,0,1.06.12.13.18.91,0,.15-.16-.18-.14.35a19,19,0,0,1,.19,2.26c0-.16.08-.32.13,0a10.34,10.34,0,0,0,.07,2c0,.07,0,.1,0,.1.06.43.13,1.39.22,1.64-.27.41.28,2.66,0,2.66l.08,1.32,0,0a5.91,5.91,0,0,0,.18,1.43c0,.2,0,.06-.05,0a9.67,9.67,0,0,0,.21,2.38l-.08.11c.06.28-.05,1.78.11,3,0,0,0,0,0-.13.17,1.35-.08,3.26.26,3.74l.12.89c0-1.15.15.17.18-.31,0,.46,0,1.18-.08.62v-.08c-.18-.2,0,1.44-.1,1.41l.09,2.15c0,.82.05,1.64.08,2.45l2.73-.11-.26-.11.75,0c.2,0,.27.08-.27.1a2.14,2.14,0,0,0,.91-.13c1,0-.4.17-.08.16l.43,0h-.1a4.05,4.05,0,0,1,1-.16c.4,0,.21.07.37.07,0,0,.05,0,.24,0l.94,0-.38-.08c.41-.07.88,0,.44-.13.65.15,3.5-.11,3.9-.08a12.85,12.85,0,0,1,1.86-.15c-.17,0-.29-.12.33-.14-.21.25,1.13,0,1.63.19-.45-.09,1.75-.12,1-.24.52,0,0,.09.43.12,1.22-.15,2.69-.07,4.2-.18,0,0-.24-.07.09-.13l.8.12c.3,0,.26-.2.81-.12-.07,0-.19-.05,0-.07,3,0,6-.43,9-.29-.41,0-.14-.06.17-.08-.2,0,0-.08,0-.12l1.26.06c.23-.13,1.35-.08,1.47-.2l-.39,0c.52-.15-.76-.25-.69-.42-.58.18-1.57-.14-2.53.12-.22,0,.18-.07.06-.1a3,3,0,0,1-1.09.11l.06,0c-1.55.09-2.21.06-4,.1l.14.07a4.67,4.67,0,0,1-.63,0c-.42.14,1.05-.05.66.11-.5-.14-2.63.13-3,0-.57,0,.18.09-.4.12l-.15,0-.15.07c-.37,0-.57-.05-.58-.09s.26,0,.35,0c-.49,0-1.37.09-1.54.08h.55c-1.56.12-3.48.15-5,.33l.11,0c-1.15.13-.93,0-2.2.1h0a7.34,7.34,0,0,1-.94.1c.32-.06-.61-.06-.19-.13a5.25,5.25,0,0,0-1.44.28l-.19-.06a3.32,3.32,0,0,1-.94.15c.52-.08-.41-.07.17-.18-1.34-.07-2,.25-2.59.07-.6.21,1.16,0,.82.1.29,0-.5.16-.92.16a11.48,11.48,0,0,0-1.81-.14l.22,0c-.4,0-.78.1-1.27,0-.27.12-.64,0-.72.17l1-.08a10,10,0,0,1-2,.22,4.72,4.72,0,0,1,.53-.11c-.27,0-.51,0-.79,0a15.38,15.38,0,0,1,1.78-.23c-.29,0-1.12.16-.83,0-.42.21-1.06,0-1.77.21l0-.09a4.37,4.37,0,0,1-.56.08l.38-.13a1.45,1.45,0,0,1-.29,0h-.19l-.16,0a.37.37,0,0,0,0-.18.51.51,0,0,1,0,.2s-.18.1,0,.14l-.11,0a1.52,1.52,0,0,1,0-.36,3,3,0,0,1,.06-.36,4.07,4.07,0,0,0,0-1.28c0,.22,0,.83-.07.49a5.9,5.9,0,0,1,.05-.91c-.07-1.08-.08.17-.15-.29.08-.37-.07-.53-.13-1H253c0-1.23-.2-.31-.19-1.42l.15.46-.06-.58.1.12a7,7,0,0,1-.1-1.44c-.05.15-.16-.07-.19-.62,0-.37.09.21,0-.56a1.19,1.19,0,0,1,.08.7s.06.17.09.2a10.14,10.14,0,0,0,0-1.29,2.82,2.82,0,0,1,0,.31l-.17-1.11c0-.18.06,0,.09.09,0-.22-.07-.38-.09-.71.07.08.14.06.2.53l-.13-1.06c0-.08.05-.55.08-.42,0-.59-.2-1.44-.19-2.23a2.16,2.16,0,0,1-.05-.28,15,15,0,0,0-.11-2.26l.1-.51c-.06-.68-.12.68-.22-.45-.08-.81.09-.31.11-.59a5.82,5.82,0,0,1-.18-1.84C252.29,149.2,252.28,149.39,252.3,149.62Zm-.47-8.42a2.14,2.14,0,0,1,0,.6C251.77,141.62,251.81,141.48,251.83,141.2Zm4.79-7.36a1.54,1.54,0,0,1-.53,0A3.12,3.12,0,0,1,256.62,133.84Zm29.24,5.57v0C285.87,139,285.87,139.15,285.86,139.41Zm0,.75c0,.08,0,.07,0,0a2.25,2.25,0,0,1,0-.41A.82.82,0,0,0,285.86,140.16ZM258,168h-.15C258.22,168,258.13,168,258,168Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "268.78px 149.978px",
                }}
                id="elj42xo458xm8"
                className="animable"
              />
              <path
                d="M255.17,134s-.06,0-.07,0S255,134,255.17,134Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "255.119px 134px",
                }}
                id="el0xhkgqcnwbyf"
                className="animable"
              />
              <path
                d="M252.61,154.86v0l0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.61px 154.86px",
                }}
                id="ellywq47r7gmp"
                className="animable"
              />
              <path
                d="M281.35,132.76a1,1,0,0,1-.3,0S281.11,132.77,281.35,132.76Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.2px 132.766px",
                }}
                id="el7uq075qerwh"
                className="animable"
              />
              <path
                d="M286.21,144.17l0-.49a3.1,3.1,0,0,1,0,.44S286.2,144,286.21,144.17Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.212px 143.925px",
                }}
                id="eliw3vro6o23a"
                className="animable"
              />
              <path
                d="M285.9,148.4c0,.07,0,.15,0,.22l0,0C285.94,148.29,285.9,148.08,285.9,148.4Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.909px 148.431px",
                }}
                id="elabmutnk6wk"
                className="animable"
              />
              <path
                d="M286,144.63c-.07,0,0,.34,0,.68v-.06C286.05,145.35,286,145,286,144.63Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.996px 144.97px",
                }}
                id="elw419n5utv6d"
                className="animable"
              />
              <path
                d="M286,145.39c0,.1,0,.21,0,.31S286,145.58,286,145.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286px 145.557px",
                }}
                id="ellctgg6k6vmi"
                className="animable"
              />
              <path
                d="M286,141.12c0,.16.1.1.07.19S285.87,140.24,286,141.12Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.014px 141.052px",
                }}
                id="eltu2hxfmnog"
                className="animable"
              />
              <polygon
                points="285.73 144.91 285.81 144.79 285.73 144.6 285.73 144.91"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.77px 144.755px",
                }}
                id="eljppek4svtu"
                className="animable"
              />
              <path
                d="M285.66,142.23v.55s0,.05,0,.14A3.49,3.49,0,0,0,285.66,142.23Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.669px 142.575px",
                }}
                id="el0lx9pw746zln"
                className="animable"
              />
              <path
                d="M285.7,142.92a3.52,3.52,0,0,0,0,.43C285.73,143.26,285.72,143.06,285.7,142.92Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.706px 143.135px",
                }}
                id="elktgbx461qah"
                className="animable"
              />
              <polygon
                points="285.67 144.18 285.68 144.01 285.67 143.63 285.67 144.18"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.675px 143.905px",
                }}
                id="elvo471203wgq"
                className="animable"
              />
              <path
                d="M285.39,136.4l-.06-.59c0-.06,0,.1,0,.32S285.36,136.49,285.39,136.4Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.36px 136.112px",
                }}
                id="elm0q1xgdoywb"
                className="animable"
              />
              <path
                d="M285.24,138.2a.69.69,0,0,1,.05-.26h-.11Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.235px 138.07px",
                }}
                id="elegw1md3n4q9"
                className="animable"
              />
              <path
                d="M285.12,136.48c0-.06-.13-.86-.08-.11C285.07,136.43,285.13,137,285.12,136.48Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.075px 136.374px",
                }}
                id="el8xjy6gmamun"
                className="animable"
              />
              <polygon
                points="280.42 132.51 280.57 132.5 280.54 132.32 280.42 132.51"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.495px 132.415px",
                }}
                id="elswrhmol893"
                className="animable"
              />
              <path
                d="M272.58,133.33c.71,0,.16-.12.8-.1.16,0-.52,0-.35-.06C272.61,133.24,272.38,133.22,272.58,133.33Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "272.956px 133.25px",
                }}
                id="el0izq5f8s0htg"
                className="animable"
              />
              <path
                d="M273,133.17l.21,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "273.105px 133.17px",
                }}
                id="elf836olclhfg"
                className="animable"
              />
              <polygon
                points="285.58 166.74 286.03 166.75 285.74 166.7 285.58 166.74"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.805px 166.725px",
                }}
                id="elwdpvo6x8t8j"
                className="animable"
              />
              <path
                d="M286.44,166.56l.11.11.26-.12A1.55,1.55,0,0,1,286.44,166.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.625px 166.61px",
                }}
                id="elkr6n2wpbdpb"
                className="animable"
              />
              <path
                d="M252,139.93l0,0a1.41,1.41,0,0,0,0,.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "251.993px 140.125px",
                }}
                id="elyhorfqqwxsr"
                className="animable"
              />
              <polygon
                points="252.22 146.05 252.21 145.95 252.12 146.15 252.22 146.05"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.17px 146.05px",
                }}
                id="elm83z4zlaagj"
                className="animable"
              />
              <path
                d="M252.6,151.18c0-.21,0-.46,0-.67C252.6,151,252.48,151.24,252.6,151.18Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.573px 150.85px",
                }}
                id="elakzqj6lza9s"
                className="animable"
              />
              <path
                d="M286.88,168.63c.05,0,0-.07,0-.09A.05.05,0,0,0,286.88,168.63Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.877px 168.585px",
                }}
                id="elfn942gprgy"
                className="animable"
              />
              <polygon
                points="286.06 168.83 286.07 168.8 285.95 168.88 286.06 168.83"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.01px 168.84px",
                }}
                id="el7hcp4tmgtze"
                className="animable"
              />
              <path
                d="M285.28,168.87c.08.12.28,0,.42,0l0,0,0-.1A1.93,1.93,0,0,1,285.28,168.87Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.49px 168.847px",
                }}
                id="elg1e3tkvv0c9"
                className="animable"
              />
              <path
                d="M284.86,168.62s-.09,0,.09,0h-.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.894px 168.62px",
                }}
                id="ello7k87fgm2q"
                className="animable"
              />
              <path
                d="M284.93,168.62c.09,0-.05,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.94px 168.62px",
                }}
                id="elvzcck5gk5bm"
                className="animable"
              />
              <path
                d="M284.56,168.67l.1,0s-.12,0-.08-.05S284.49,168.62,284.56,168.67Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.599px 168.635px",
                }}
                id="eln574a5ooo7"
                className="animable"
              />
              <path
                d="M284.46,168.76l.06,0S284.49,168.73,284.46,168.76Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.49px 168.753px",
                }}
                id="el4v62q7yy0a3"
                className="animable"
              />
              <path
                d="M284.63,168.7l-.11,0s0,.06.05,0S284.58,168.73,284.63,168.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.575px 168.703px",
                }}
                id="elyxtea9dpker"
                className="animable"
              />
              <path
                d="M284.7,168.71c.06,0,.14,0,0,.09C284.94,168.77,284.9,168.69,284.7,168.71Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.783px 168.753px",
                }}
                id="elzn4stn486ua"
                className="animable"
              />
              <path
                d="M285.25,169l.08,0,.07,0S285.24,169,285.25,169Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.325px 169px",
                }}
                id="eljhly9wjtmr"
                className="animable"
              />
              <path
                d="M283.89,168.64s0,.06,0,.1l.08,0S283.89,168.67,283.89,168.64Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.93px 168.69px",
                }}
                id="elq1e8kumgik7"
                className="animable"
              />
              <path
                d="M283.93,168.74l-.11,0C283.92,168.79,283.94,168.77,283.93,168.74Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.876px 168.755px",
                }}
                id="el5a7ig86nnm9"
                className="animable"
              />
              <polygon
                points="284.45 168.9 284.64 168.8 284.41 168.91 284.45 168.9"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.525px 168.855px",
                }}
                id="elltr9day1seo"
                className="animable"
              />
              <polygon
                points="283.43 168.66 283.5 168.59 283.38 168.69 283.43 168.66"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.44px 168.64px",
                }}
                id="el8nmj7qkxgmt"
                className="animable"
              />
              <path
                d="M283.13,168.68c.05-.05-.07-.08,0-.12a.53.53,0,0,0-.21.11A.27.27,0,0,1,283.13,168.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.031px 168.62px",
                }}
                id="el9hnmzlcsite"
                className="animable"
              />
              <path
                d="M282.8,168.73l.1-.06A.21.21,0,0,0,282.8,168.73Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.85px 168.7px",
                }}
                id="elh5q6yc66bxh"
                className="animable"
              />
              <path
                d="M283.62,168.79a.12.12,0,0,0-.1-.07l.05.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.57px 168.755px",
                }}
                id="elkvdkfzlwala"
                className="animable"
              />
              <path
                d="M283.57,168.79Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.57px 168.79px",
                }}
                id="elwe32wn8kt6o"
                className="animable"
              />
              <path
                d="M283.63,168.79h0S283.62,168.8,283.63,168.79Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.628px 168.792px",
                }}
                id="elut55zycxgxa"
                className="animable"
              />
              <path
                d="M284.13,168.85c-.06.05-.21.1-.06.15S284,168.92,284.13,168.85Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.068px 168.931px",
                }}
                id="el29w04uwj05x"
                className="animable"
              />
              <path
                d="M283.69,168.88h.13s0,0,0-.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.755px 168.845px",
                }}
                id="elptd9zcxaknq"
                className="animable"
              />
              <polygon
                points="283.63 168.9 283.69 168.88 283.64 168.87 283.63 168.9"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.66px 168.885px",
                }}
                id="el3b8j12ejd71"
                className="animable"
              />
              <path
                d="M283,168.79a.61.61,0,0,1-.18.05h.22S283.09,168.78,283,168.79Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.937px 168.814px",
                }}
                id="elpu6bhd99cdq"
                className="animable"
              />
              <path
                d="M283.29,168.8h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.29px 168.8px",
                }}
                id="elxbvg5dv1y9k"
                className="animable"
              />
              <path
                d="M282.17,168.87h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.17px 168.87px",
                }}
                id="eluq2fvd84xvj"
                className="animable"
              />
              <path
                d="M283.07,168.86a.09.09,0,0,0,.11,0l-.12,0S283.05,168.87,283.07,168.86Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.119px 168.869px",
                }}
                id="elxw6745i4hk8"
                className="animable"
              />
              <path
                d="M282.61,168.77l.1,0S282.62,168.75,282.61,168.77Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.66px 168.766px",
                }}
                id="elgjltvzzjth"
                className="animable"
              />
              <path
                d="M283.25,168.78h0S283.25,168.77,283.25,168.78Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.25px 168.778px",
                }}
                id="ellaaxccsrpe"
                className="animable"
              />
              <path
                d="M283.25,168.78l-.07.05.09,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.225px 168.805px",
                }}
                id="elsy2c93qbfwb"
                className="animable"
              />
              <path
                d="M282,169.36c0-.07.14-.14.27-.07s0-.28-.09-.41c0,0,0,.06-.12.11.05,0,.28.07.18.14s-.14,0-.22,0c.07.09-.2.14-.16.26l.14-.05S282,169.35,282,169.36Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.09px 169.135px",
                }}
                id="elu3l3f1xn05a"
                className="animable"
              />
              <path
                d="M282.42,168.88h.05a.5.5,0,0,1-.18-.09c-.08,0-.1.07-.1.09h.23Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.33px 168.835px",
                }}
                id="elmll6zpu7wu"
                className="animable"
              />
              <path
                d="M282.7,168.86c0,.07-.15.11-.07.19s.1-.07.19-.11c-.25,0,.06,0-.11-.08l.13,0-.13,0h0s0,0,0,0h0a0,0,0,0,1,0,0c-.07,0,0,0,0,0h0l-.25.06A1,1,0,0,0,282.7,168.86Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.65px 168.967px",
                }}
                id="elknzah48fuqi"
                className="animable"
              />
              <path
                d="M284,169.17h0S283.92,169.17,284,169.17Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.982px 169.17px",
                }}
                id="elhzt2dnenjil"
                className="animable"
              />
              <path
                d="M283.84,169.13s0,0,.09,0,0,0,0,.06.12-.06,0-.1v0a.44.44,0,0,1-.22-.12A.64.64,0,0,0,283.84,169.13Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.847px 169.088px",
                }}
                id="eldpgkm4q3k9"
                className="animable"
              />
              <path
                d="M283.27,169l-.2.05.1.06v0C283.28,169.09,283.25,169.05,283.27,169Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.17px 169.055px",
                }}
                id="el2pvg9x3s9mx"
                className="animable"
              />
              <polygon
                points="282.15 168.84 282.17 168.75 282.06 168.87 282.15 168.84"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.115px 168.81px",
                }}
                id="el0hljeuledxc"
                className="animable"
              />
              <polygon
                points="281.88 168.86 281.89 168.79 281.82 168.83 281.88 168.86"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.855px 168.825px",
                }}
                id="el08xs2uxqcfjd"
                className="animable"
              />
              <path
                d="M267.06,170.46l-.06,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "267.03px 170.46px",
                }}
                id="elovt6dco6xo9"
                className="animable"
              />
              <path
                d="M267.49,170.44v0S267.47,170.44,267.49,170.44Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "267.486px 170.44px",
                }}
                id="el574u98rflyd"
                className="animable"
              />
              <path
                d="M275.41,169.19c-.09-.06-.19-.16-.17-.08l0,.08A.22.22,0,0,1,275.41,169.19Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "275.324px 169.135px",
                }}
                id="elt8umnjbjcuj"
                className="animable"
              />
              <path
                d="M271,169.86l0,0,.06-.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "271.03px 169.82px",
                }}
                id="elqvhlo6l7z6r"
                className="animable"
              />
              <path
                d="M271,170.18l-.12,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "270.94px 170.18px",
                }}
                id="elhcms1vy15kt"
                className="animable"
              />
              <path
                d="M264.71,170.66l-.07.08C264.75,170.74,264.7,170.7,264.71,170.66Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "264.676px 170.7px",
                }}
                id="elmymzegbcysn"
                className="animable"
              />
              <path
                d="M279.19,169a.93.93,0,0,0-.19,0C279.09,169,279.17,169.09,279.19,169Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "279.095px 169.018px",
                }}
                id="elro7s0prijz"
                className="animable"
              />
              <path
                d="M267.19,169.65s-.05,0-.05,0S267.2,169.67,267.19,169.65Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "267.166px 169.654px",
                }}
                id="eljt40yjsraj"
                className="animable"
              />
              <path
                d="M278.78,169h0a1,1,0,0,1,.18,0A.22.22,0,0,0,278.78,169Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.87px 168.99px",
                }}
                id="elh6etl7lcgi"
                className="animable"
              />
              <path
                d="M256.16,171h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "256.16px 171px",
                }}
                id="elllmwkvohqzr"
                className="animable"
              />
              <path
                d="M253.63,170.38h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.63px 170.38px",
                }}
                id="el6b6cmrdl0l9"
                className="animable"
              />
              <path
                d="M280.82,168.88c.06,0,0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.833px 168.88px",
                }}
                id="elcled4q26d25"
                className="animable"
              />
              <path
                d="M281.11,169.16a.05.05,0,0,0,0,0A.05.05,0,0,1,281.11,169.16Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.11px 169.16px",
                }}
                id="elo95xwe579b"
                className="animable"
              />
              <path
                d="M278.25,169.5h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.25px 169.5px",
                }}
                id="eln8hekowbjw"
                className="animable"
              />
              <path
                d="M280.91,168.91l-.07,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.875px 168.91px",
                }}
                id="el9kfsxpvpa7o"
                className="animable"
              />
              <path
                d="M280.9,168.87H281S281,168.86,280.9,168.87Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.95px 168.868px",
                }}
                id="elp0qahi6q65k"
                className="animable"
              />
              <path
                d="M280.75,168.89h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.75px 168.89px",
                }}
                id="el2eiwusmv892"
                className="animable"
              />
              <path
                d="M260.73,170.59h0S260.68,170.61,260.73,170.59Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "260.719px 170.594px",
                }}
                id="elzdneegqloxl"
                className="animable"
              />
              <polygon
                points="274 170.05 274 170.05 274 170.04 274 170.05"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "274px 170.045px",
                }}
                id="elbkmgxoqmkf"
                className="animable"
              />
              <path
                d="M273.8,170.12a.17.17,0,0,1,.2-.08l-.08-.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "273.9px 169.975px",
                }}
                id="el7insr6ykpiq"
                className="animable"
              />
              <path
                d="M266,170.54l.14.06c-.16.12-.29.08-.15.17,0-.23.54-.05.55-.29l.1.05h-.06c.19.11.13-.18.36-.16.08,0,.14.05.09.08l.18-.07c.05,0,0,.05,0,.05l.27,0h0l.16-.13c.08,0,0,0,0,.06l.15-.07c.07.06-.06.13-.16.12.15.23.12-.06.43.06l-.14,0c.1.13.21.07.38.15,0,0-.34-.2-.18-.26a.26.26,0,0,0,.2,0c0,.08.09.05.2,0v.12c.12,0,.1-.1.17-.15s.16.09.2.16c.21,0-.15-.13.1-.17a.79.79,0,0,0,.5-.09c.12,0,0,.07,0,.1l.1-.1s0,0,0,.05,0-.12.14-.15.31.16.48,0c0,0,0,.07,0,.11a1.27,1.27,0,0,0,.4-.21l.17.07c-.13-.08,0-.19.16-.28a.59.59,0,0,0-.11-.08l.22-.07s0,0,0,.1l0,0c.08,0,0,.11,0,.17l-.09-.06c0,.07-.1.15-.08.21a.11.11,0,0,1,.07,0s0,0,0,0,0,0,.07,0l.06.12v-.05l.29.06a1.85,1.85,0,0,1,.71-.39c-.08.09,0,.15,0,.25-.09-.11-.25.07-.43.05.09,0,.08.06.05.08l.31-.12c0,.09.07.06.15.1-.07-.18.22-.13.35-.19s0,.12-.17.11c.17.13.2-.21.37-.11,0,0-.05,0-.07,0s.3,0,.26,0h0c.25,0,.75,0,.78-.19a.82.82,0,0,1,0,.21l.41-.52c0,.13.14.42-.07.51a.41.41,0,0,0,.26,0s-.11-.18-.05-.2c.15.14.11.07.3.19-.06,0,0-.23.09-.23s0,.2,0,.22l.23-.21c-.06,0,0,.17.05.22s.21,0,.27,0l-.1,0c.27,0,.25-.29.52-.29,0,0-.07.25.07.27s.25-.5.48-.59l0,.06.17-.08a3.35,3.35,0,0,1-.57.63c.11,0,0,.07.18.05s-.07.09-.12.09l.36,0c0-.12.26-.13.24-.23L276,170c0-.11.21-.34.46-.33.06,0,0,.21,0,.25s.23-.09.25-.06l-.1.06c.14,0,.22-.16.39-.06s.18.14.19.07a.93.93,0,0,1-.09-.64,1.66,1.66,0,0,1,.38.17c.1.1-.13.26-.05.35a.18.18,0,0,1,.08-.09c.06,0,0,.1.11.08s.26,0,.05-.11c.13,0,.14,0,.29,0,0-.08.12-.27.25-.31s0,.07,0,.09c.26,0,.41,0,.65,0,0,0,.14.06.1.11a.26.26,0,0,1,.14-.07c.15.09-.17,0-.05.14,0-.07.19-.1.29-.15s-.25,0-.35,0c0-.15.17-.31.42-.4s.09.1.13.07c.38,0,.31-.27.63-.18.13.09,0,.19.05.25-.17,0-.26,0-.37,0l.14,0a.25.25,0,0,1-.22,0c.11.08.64,0,.94.05.05,0,.14-.07.1-.11l-.1,0c-.09-.07.17-.11.05-.14a.71.71,0,0,1,.44-.12l0,.11s-.12-.06-.17,0,.28.13.35,0l-.13-.07c.13-.06.28-.18.43-.15-.07-.11,0,0-.15-.11.11.15-.4,0-.23.17-.13-.14-.2-.05-.36-.17,0,0,.06.07-.07.06l0,0h0s.06,0,.07.05-.27.1-.34.14,0,0,0-.08l-.1.07c0-.08-.22-.12,0-.17h-.16a.42.42,0,0,0-.31,0c-.08,0-.2.09-.13,0l-.26.06v0c-.26,0-.52.16-.82,0a.69.69,0,0,1-.31,0l0-.07c-.1,0-.33,0-.37,0-.13-.17-.54,0-.73,0v.09a1.75,1.75,0,0,0-.42.07v.08c-.14.07-.37,0-.59,0,0,0,.07,0,.11,0-.28-.16-.56.16-.81,0a.16.16,0,0,1-.15,0h0l-.09,0,.08,0a.81.81,0,0,0-.19,0l0-.08h0c-.07,0-.05,0,0,.1l-.09.06c-.07-.13-.18.05-.27-.09,0,.08-.35.07-.37.22,0,0,0,0,0,0a.36.36,0,0,0-.23,0l0-.1-.16.15c-.1-.06-.15-.1,0-.18-.24.14-.2,0-.4.19v-.07c-.07,0-.19.11-.24,0s-.83,0-1.27-.08c.1.2-.2-.08-.21.08,0,0-.09-.07,0-.12-.22.06-.34-.06-.48.09,0,0,0-.08,0-.11s-.06,0-.09,0,0,0,.07-.06c-.23,0-.24.17-.25.31-.12-.06-.18,0-.25,0s-.12-.08,0-.11c-.08,0-.48,0-.48.07s-.11,0-.13,0c-.18,0-.2,0-.37,0H270c-.06.14-.14,0-.28.06h0c-.33-.08-.14.05-.46,0l0,.08c0,.18-.28-.09-.46,0l.07,0c-.1.07-.3-.23-.4-.25s0,0,.07,0c-.27-.09,0,.11-.23.15,0-.06,0-.15-.09-.16s-.38.21-.56.11c0,0,.08.09,0,.12s-.39-.15-.5,0t0,0c-.11,0-.35.08-.41.16-.11-.29-.65.17-.67-.1l-.32,0v0a.38.38,0,0,0-.35.13c-.05,0,0-.05,0-.06a.65.65,0,0,0-.59.12l0-.09c-.07.06-.45-.11-.75,0a.05.05,0,0,1,0,0c-.33.11-.82-.2-.93.12l-.22.09c.29,0,0,.14.08.18-.11,0-.3-.08-.16-.1h0c0-.16-.37,0-.36-.15-.57,0-1.13,0-1.67,0l.07.13h-.19c-.05,0-.07-.1.07-.1-.08-.07-.22.07-.23.1-.26,0,.1-.15,0-.15h-.11l0,0c-.09,0-.09.11-.25.11s-.06-.07-.1-.08,0,0-.06,0h-.24l.1.1c-.1,0-.22,0-.1.11-.17-.16-.88,0-1-.07a.81.81,0,0,1-.47.09c.05,0,.08.13-.07.13,0-.24-.29-.09-.42-.25.12.1-.44.05-.25.2-.13,0,0-.09-.12-.14a7.12,7.12,0,0,1-1.05,0s.06.08,0,.13l-.21-.15c-.07,0-.06.18-.2.08,0,0,0,.07,0,.08-.75-.11-1.5.2-2.27,0,.11.06,0,.07,0,.07s0,.08,0,.12l-.32-.1c-.05.12-.34,0-.37.14l.1,0c-.12.14.2.28.19.45.14-.15.4.2.63,0,.06,0,0,0,0,.09s.13-.09.27-.07v0a5.75,5.75,0,0,1,1,.05l0-.08a.43.43,0,0,1,.16.05c.1-.13-.27,0-.18-.14.14.17.66,0,.76.16.14,0-.05-.09.09-.1l0,.05,0-.07a.17.17,0,0,1,.15.11l-.09,0c.12.05.34,0,.39,0l-.14,0a8.4,8.4,0,0,0,1.24-.15l0,0c.29-.08.23,0,.55,0h0a.37.37,0,0,1,.23-.07c-.08.05.16.08.05.14.31-.1.16-.07.36-.23l.05.06a.29.29,0,0,1,.23-.11c-.13.06.1.09,0,.17.34.13.49-.17.65,0,.15-.18-.29-.08-.21-.12-.07-.06.12-.15.23-.13s.19.22.46.2c0,0,0,0-.06,0s.2-.08.32,0c.07-.11.16,0,.18-.14l-.25.05c.14-.05.26-.22.5-.15a.43.43,0,0,1-.13.09c.07,0,.12,0,.2,0s-.3,0-.44.16c.07.05.27-.12.2,0,.1-.2.27,0,.44-.15v.09a.23.23,0,0,1,.13-.06l-.09.11c.15-.1.29.07.43,0-.3,0-.07-.1-.2-.15.36-.13.22.21.63.14-.06,0-.21,0-.13-.08a.69.69,0,0,1,.23.08c.27,0,0-.08.07-.14.1.1.13,0,.25-.08v.08c.31,0,.07-.18.35-.13l-.11.13.14,0,0,.1a.46.46,0,0,1,.36-.05c0-.06,0-.15.15-.16s-.05.08.14.06c0,.05-.08.12-.17.05a.56.56,0,0,1-.05.08c.1.06.25,0,.32,0l-.08,0,.28-.13s0,.06,0,.09a.54.54,0,0,1,.17-.07c0,.07,0,.14-.12.18l.26-.09s.14.08.11.1c.15,0,.35-.15.55-.11l.07,0c.19,0,.35.08.57,0l.13.12c.17,0-.18-.15.1-.21s.08.1.16.13.26-.2.45-.11C266.13,170.54,266.08,170.52,266,170.54Zm2.1-.16s-.12.07-.15,0S268.06,170.35,268.13,170.38Zm3.19-.37s-.07,0-.13,0A.2.2,0,0,1,271.32,170Zm8.89-1Zm.2,0a0,0,0,0,0,0,0s-.05,0-.1,0S280.35,169,280.41,169Zm-20.31,1.05,0,0C260.06,170.1,260.08,170.08,260.1,170.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "267.35px 169.74px",
                }}
                id="eldyhm3tu2lgs"
                className="animable"
              />
              <path
                d="M271,170.15a.06.06,0,0,1,0-.05S270.93,170.13,271,170.15Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "270.984px 170.125px",
                }}
                id="elpwkljwxfbh7"
                className="animable"
              />
              <path
                d="M264.72,170.65h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "264.72px 170.65px",
                }}
                id="elqsnojpg5on"
                className="animable"
              />
              <path
                d="M277.51,169.87l-.08,0S277.45,169.86,277.51,169.87Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "277.47px 169.868px",
                }}
                id="el4zyzbmnhhfe"
                className="animable"
              />
              <path
                d="M281.4,168.81l-.12,0a.17.17,0,0,1,.11,0S281.36,168.81,281.4,168.81Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.34px 168.805px",
                }}
                id="elsnky1ra5pkh"
                className="animable"
              />
              <path
                d="M282.48,169.28h0v0C282.45,169.23,282.4,169.27,282.48,169.28Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.458px 169.268px",
                }}
                id="el2lg6e5gxd3s"
                className="animable"
              />
              <path
                d="M281.53,169.09c0,.07.08,0,.17,0h0C281.7,169,281.61,169.05,281.53,169.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.615px 169.08px",
                }}
                id="elh35tbo6a40u"
                className="animable"
              />
              <path
                d="M281.72,169.11h.08S281.76,169.09,281.72,169.11Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.76px 169.106px",
                }}
                id="elpg9w4addmz"
                className="animable"
              />
              <path
                d="M280.64,169s0-.1.05-.07S280.42,169,280.64,169Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.629px 168.962px",
                }}
                id="elp7o2nw81jq"
                className="animable"
              />
              <polygon
                points="281.61 169.32 281.57 169.23 281.53 169.3 281.61 169.32"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.57px 169.275px",
                }}
                id="elldg7u93ulf"
                className="animable"
              />
              <path
                d="M280.93,169.29h.14s0,0,0,0S280.93,169.26,280.93,169.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281px 169.283px",
                }}
                id="elv7e1op7i1zi"
                className="animable"
              />
              <path
                d="M281.11,169.27a.15.15,0,0,0,.1,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.16px 169.274px",
                }}
                id="elwoux6atf5fs"
                className="animable"
              />
              <polygon
                points="281.42 169.35 281.38 169.34 281.29 169.33 281.42 169.35"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.355px 169.34px",
                }}
                id="elgaahuqrtyx9"
                className="animable"
              />
              <path
                d="M279.48,169.34l-.15,0s0,0,.08,0S279.5,169.37,279.48,169.34Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "279.409px 169.347px",
                }}
                id="el8vyqtwkr0hq"
                className="animable"
              />
              <path
                d="M279.93,169.56s0,0-.06-.06v.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "279.9px 169.55px",
                }}
                id="eloqysxfoqhfm"
                className="animable"
              />
              <path
                d="M279.5,169.61s-.21.09,0,.08C279.49,169.66,279.64,169.62,279.5,169.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "279.483px 169.65px",
                }}
                id="el7r7svbrra4u"
                className="animable"
              />
              <polygon
                points="277.27 169.58 277.31 169.58 277.29 169.4 277.27 169.58"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "277.29px 169.49px",
                }}
                id="elg68h95sww69"
                className="animable"
              />
              <path
                d="M275.32,170.1c.18,0,0-.11.2-.06s-.13,0-.09-.08S275.27,170,275.32,170.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "275.448px 170.017px",
                }}
                id="elay1v05m8ck4"
                className="animable"
              />
              <path
                d="M275.43,170l.05,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "275.455px 170px",
                }}
                id="elz7x0pcocfsg"
                className="animable"
              />
              <polygon
                points="253.21 170.31 253.1 170.28 253.17 170.33 253.21 170.31"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.155px 170.305px",
                }}
                id="el2y1zr29hfoo"
                className="animable"
              />
              <path
                d="M253,170.45l0-.11-.06.11S253,170.43,253,170.45Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.97px 170.395px",
                }}
                id="elkb71wq2awa9"
                className="animable"
              />
              <path
                d="M268.45,170.59v0s0,0-.1,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "268.4px 170.59px",
                }}
                id="elu48wc7z69h"
                className="animable"
              />
              <polygon
                points="266.92 170.58 266.95 170.58 266.89 170.48 266.92 170.58"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.92px 170.53px",
                }}
                id="elv9l07s60i18"
                className="animable"
              />
              <path
                d="M265.64,170.78c.06,0,.12,0,.17,0C265.7,170.78,265.62,170.65,265.64,170.78Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "265.723px 170.751px",
                }}
                id="elx9qmcju0vk"
                className="animable"
              />
              <path
                d="M253.4,171.8h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.4px 171.8px",
                }}
                id="el0ud1mhlh8t6"
                className="animable"
              />
              <polygon
                points="253.23 171.76 253.26 171.76 253.19 171.75 253.23 171.76"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.225px 171.755px",
                }}
                id="el6vh1pixnuf3"
                className="animable"
              />
              <path
                d="M253.23,171.71c-.12,0,0,0,0,0h.07S253.2,171.72,253.23,171.71Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.238px 171.712px",
                }}
                id="elnxo51w9rn5"
                className="animable"
              />
              <path
                d="M253.5,171.66c-.05,0,0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.489px 171.66px",
                }}
                id="elhhi2jh5gjzr"
                className="animable"
              />
              <path
                d="M253.5,171.67s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.5px 171.67px",
                }}
                id="el8svysasxiah"
                className="animable"
              />
              <path
                d="M253.47,171.65h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.47px 171.65px",
                }}
                id="el5lqjrimqqj6"
                className="animable"
              />
              <path
                d="M253.38,171.64h0S253.41,171.64,253.38,171.64Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.387px 171.64px",
                }}
                id="el9b251cdqthu"
                className="animable"
              />
              <path
                d="M253.44,171.65h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.44px 171.65px",
                }}
                id="ell9o7smjn4j"
                className="animable"
              />
              <path
                d="M253.42,171.66h-.09S253.44,171.67,253.42,171.66Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.376px 171.662px",
                }}
                id="eljhqsu1gxxos"
                className="animable"
              />
              <path
                d="M253.1,171.71h0S253.14,171.71,253.1,171.71Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.109px 171.71px",
                }}
                id="el0e2165xyyfxj"
                className="animable"
              />
              <path
                d="M253.53,171.6h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.53px 171.6px",
                }}
                id="elsdras8jis1m"
                className="animable"
              />
              <path
                d="M253.43,171.61h-.05S253.4,171.61,253.43,171.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.405px 171.61px",
                }}
                id="eli2d4bcc0g58"
                className="animable"
              />
              <polygon
                points="253.24 171.65 253.34 171.66 253.24 171.65 253.24 171.65"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.29px 171.655px",
                }}
                id="elfew14jtulzf"
                className="animable"
              />
              <polygon
                points="253.53 171.57 253.6 171.57 253.5 171.57 253.53 171.57"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.55px 171.57px",
                }}
                id="eltlxnk1jklfp"
                className="animable"
              />
              <path
                d="M253.52,171.55h0S253.56,171.54,253.52,171.55Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.529px 171.548px",
                }}
                id="elg9alp9192np"
                className="animable"
              />
              <path
                d="M253.49,171.53h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.49px 171.53px",
                }}
                id="elqhn5znxlmp"
                className="animable"
              />
              <path
                d="M253.39,171.59h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.39px 171.59px",
                }}
                id="elxhw0ibtot1a"
                className="animable"
              />
              <path
                d="M253.39,171.59h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.39px 171.59px",
                }}
                id="elf67y2keyav"
                className="animable"
              />
              <path
                d="M253.39,171.59Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.39px 171.59px",
                }}
                id="elkxkeq1mlvxn"
                className="animable"
              />
              <path
                d="M253.31,171.63h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.31px 171.63px",
                }}
                id="elaxsf5idbpsl"
                className="animable"
              />
              <path
                d="M253.3,171.6h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.3px 171.6px",
                }}
                id="el2t9d6awcxyu"
                className="animable"
              />
              <polygon
                points="253.28 171.59 253.3 171.6 253.31 171.59 253.28 171.59"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.295px 171.595px",
                }}
                id="elsehdpdemdrd"
                className="animable"
              />
              <path
                d="M253.42,171.55h0s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.42px 171.55px",
                }}
                id="el67bxfsn48jd"
                className="animable"
              />
              <path
                d="M253.4,171.57Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.4px 171.57px",
                }}
                id="elq7c3kqe286m"
                className="animable"
              />
              <path
                d="M253.38,171.49h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.38px 171.49px",
                }}
                id="elmv9nu3ja9tm"
                className="animable"
              />
              <path
                d="M253.34,171.55s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.34px 171.55px",
                }}
                id="el0lz4jm78g3d"
                className="animable"
              />
              <path
                d="M253.45,171.52h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.45px 171.52px",
                }}
                id="elmex7jod2mf"
                className="animable"
              />
              <path
                d="M253.42,171.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.42px 171.56px",
                }}
                id="el31sn03c8mak"
                className="animable"
              />
              <path
                d="M253.42,171.56h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.42px 171.56px",
                }}
                id="elez35z4gupv6"
                className="animable"
              />
              <path
                d="M252.9,171.5c.07,0,.13,0,.06,0l.4,0h-.1l-.15,0s0,0,.06,0h-.27Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.13px 171.5px",
                }}
                id="eluaipkpqnh7"
                className="animable"
              />
              <path
                d="M253.36,171.51h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.36px 171.51px",
                }}
                id="el5fukee5qtrp"
                className="animable"
              />
              <path
                d="M253.37,171.53h-.1s0,0,.09,0h0l0,0S253.36,171.52,253.37,171.53Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.32px 171.528px",
                }}
                id="elaltduvb6e8"
                className="animable"
              />
              <path
                d="M253,171.63h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253px 171.63px",
                }}
                id="elb1186mah42a"
                className="animable"
              />
              <path
                d="M253,171.62h.05l.13,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.09px 171.62px",
                }}
                id="elxbrlprujsxd"
                className="animable"
              />
              <path
                d="M253.18,171.57h-.06S253.15,171.58,253.18,171.57Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.15px 171.572px",
                }}
                id="elkbb20fppp8d"
                className="animable"
              />
              <polygon
                points="253.41 171.49 253.5 171.49 253.38 171.49 253.41 171.49"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.44px 171.49px",
                }}
                id="elmgwidgv731d"
                className="animable"
              />
              <polygon
                points="253.41 171.47 253.47 171.47 253.43 171.47 253.41 171.47"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.44px 171.47px",
                }}
                id="el9agpyg56xga"
                className="animable"
              />
              <path
                d="M252.5,170.54h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.5px 170.54px",
                }}
                id="elzeprin4kr0p"
                className="animable"
              />
              <path
                d="M252.49,170.57h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.49px 170.57px",
                }}
                id="elkwa3v5p9xvr"
                className="animable"
              />
              <path
                d="M253.38,171.05c.06,0,.16,0,.08,0h-.07S253.39,171,253.38,171.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.435px 171.039px",
                }}
                id="el0dwttkek6toq"
                className="animable"
              />
              <path
                d="M252.91,170.78h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.91px 170.78px",
                }}
                id="ele406ye3cf08"
                className="animable"
              />
              <path
                d="M252.59,170.79h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.59px 170.79px",
                }}
                id="elc9jh92occy"
                className="animable"
              />
              <path
                d="M252.41,170.39h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.41px 170.39px",
                }}
                id="el9nktrbmazq"
                className="animable"
              />
              <path
                d="M253.37,171.3l0,0S253.3,171.3,253.37,171.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.354px 171.3px",
                }}
                id="elf28jftgjvq4"
                className="animable"
              />
              <path
                d="M253.3,170.51h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.3px 170.51px",
                }}
                id="el8h24psoq47"
                className="animable"
              />
              <path
                d="M253.41,171.27h0S253.47,171.27,253.41,171.27Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.423px 171.27px",
                }}
                id="eluhq9w7zew0g"
                className="animable"
              />
              <path
                d="M252.44,169.82h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.44px 169.82px",
                }}
                id="el0p0gc4otg9pc"
                className="animable"
              />
              <path
                d="M253.2,169.62h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.2px 169.62px",
                }}
                id="elapxv9448a7m"
                className="animable"
              />
              <path
                d="M253.43,171.4Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.43px 171.4px",
                }}
                id="eleff4ac418gp"
                className="animable"
              />
              <path
                d="M253.14,171.43h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.14px 171.43px",
                }}
                id="elzi5hvoe76a"
                className="animable"
              />
              <path
                d="M252.94,171.26Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.94px 171.26px",
                }}
                id="elcv96axt76v6"
                className="animable"
              />
              <path
                d="M253.4,171.41h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.4px 171.41px",
                }}
                id="elkngfkmiuq8r"
                className="animable"
              />
              <path
                d="M253.44,171.41h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.44px 171.41px",
                }}
                id="ellugrgoc5fae"
                className="animable"
              />
              <path
                d="M253.43,171.4h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.43px 171.4px",
                }}
                id="elqu0lzwz0lpn"
                className="animable"
              />
              <path
                d="M252.66,170.11h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.66px 170.11px",
                }}
                id="elwqrq02qx80i"
                className="animable"
              />
              <polygon
                points="252.58 170.99 252.59 170.99 252.59 170.99 252.58 170.99"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.585px 170.99px",
                }}
                id="el83ptp5k6jtv"
                className="animable"
              />
              <path
                d="M252.52,171c.1,0,.09,0,.07,0h-.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.562px 171px",
                }}
                id="elib09crpj997"
                className="animable"
              />
              <path
                d="M252.47,170.47h-.24c.24,0,0,0,.27,0h-.06c-.12,0,.17,0,.14,0h-.07l0,0h.12s0,0-.06,0h.06c-.06,0-.12,0-.11,0-.24,0,.06,0-.08,0h0a.89.89,0,0,0-.17,0,2.13,2.13,0,0,1,.27,0s0,0,0,0-.06,0-.06,0h-.11a.65.65,0,0,0,.14,0l-.17,0s.14,0,.16,0,0,0,.07,0h0s0,0,0,0a.65.65,0,0,1,.14,0c.13,0-.18,0,0,0h-.11l.19,0h-.08l-.08,0a2.55,2.55,0,0,1,.36,0l-.25,0c.11,0,0,0,0,0h0c-.09,0-.06,0-.1,0,.18,0,.12,0,.17,0s-.12,0-.1,0c-.13,0,.2,0,.09,0h0c.05,0,0,0,0,0h0s0,.05.15,0h.29a4.71,4.71,0,0,1-.5,0s0,0,0,0a.69.69,0,0,1,.2,0l-.2,0a1.59,1.59,0,0,1,.22,0h0a.86.86,0,0,0-.22,0s0,0,0,0h0s.28,0,.27,0l-.27,0h0s-.07,0-.06,0h-.09l0,0h.22l-.13,0a1.26,1.26,0,0,1,.32,0h-.25s.08,0,0,0h-.06s.15,0,0,0h-.08l.65,0-.19,0-.34,0h.08s-.1,0-.08,0,0,0,.1,0,0,0,0,0h.21s-.07,0-.07,0h-.05c-.1,0,0,0-.14,0l.14,0c.11,0,0,0,0,0h.37s-.09,0-.07,0,.26,0,.15,0l-.25,0s.06,0,.05,0H253c-.09,0,0,0-.09.06a.33.33,0,0,0,.1,0h0c.07,0,.1,0,.14,0s.13,0,.09,0h-.1s.06,0,0,0-.15,0,0,0h.08s.17,0,.13,0,0,0,.11,0c-.15,0,0,0-.15,0a.84.84,0,0,0,.18,0h-.14c-.08,0-.08,0-.12,0s0,0,.08,0h-.07a1.28,1.28,0,0,1,.18,0h0c.05,0,.06,0,0,0s-.09,0,0,0h0s-.14,0,0-.06c0,0,0,0,0,0h.07s.07,0,0,0c.18,0,0,0,.07-.05h-.09l-.06,0h-.08c-.06,0,.06,0,.05,0s0,0,0,0c.18,0-.13,0,0-.05,0,0,0,0,0,0h-.1c.14,0,0,0,.1,0a.78.78,0,0,0-.21,0h0s0,0,0,0h.14l-.17,0h.07s-.1,0,0,0,0-.06.14-.09c-.21,0,.09,0-.07,0h.12c-.05,0,.07,0-.06,0h.1s0,0,0,0h-.23c.06,0,.05,0,0,0h.11s.07,0,0,0,0,0,0,0,0,0,0,0h0c-.14,0,0,0,0,0h0c.1,0,0,0,.07,0h-.09c-.18,0,.11,0,0,0h-.05l.27,0h0c.11,0-.11,0-.13,0l.16,0s-.19,0-.08,0h-.13s.18,0,0,0h0l-.14,0c.28,0-.15,0,.12-.05l0,0h0s-.05,0-.11,0h.06c.05,0,0,0-.1,0h.09c-.05,0,.14,0,0,0h0c-.1,0,.24-.06-.08-.06h-.07a1,1,0,0,1-.19,0s.09,0,.11,0h0c.17,0,.06,0,.17,0s.08-.07.08-.11h0c.07,0-.06,0-.09,0s.15,0,.15,0h0c-.06,0-.11,0-.11,0h.09v0l-.1,0s0,0-.11,0c.18,0,.06-.06.12-.07s-.08,0-.07,0H253c.24,0,.1,0,.27,0-.11,0,0,0-.19,0l.15,0c-.1,0,.05,0,0-.07h-.12l.15,0s-.18,0-.07,0h-.08c.15-.05-.12-.09.15-.16h-.18l.11,0c-.11,0,0,0-.12,0h0c-.13,0-.29,0-.45,0,.15,0-.22,0,0,0h-.09c.07,0,.09,0,0,0h0s-.06,0-.1.07h0c.13,0,0,0,.15,0-.17,0,0,0-.19.05h.11l-.12,0s0,0,0,0,0,0,0,0h0c.05,0,0,0,.09.07h0c.07,0-.05,0,0,0h0s.06,0,0,0h-.14a.62.62,0,0,0,.21,0h0a1.16,1.16,0,0,0-.17,0c-.14,0,.15,0-.06,0,.18,0,.1,0,.14,0s.13,0,.11,0-.23,0-.22,0h0s.06,0,0,0a.54.54,0,0,0,.13,0h0s.19,0,.12,0h-.09s0,0,0,0,0,0-.14,0,.1,0,0,0c.19,0,0,0,.12,0h-.14c.1,0-.08,0,0,0s.1,0,.16,0-.21,0-.17.05a.15.15,0,0,1,.09,0l-.09,0h.13c-.1,0,0,0,.08,0h-.09c-.05,0,.18,0,.11,0h-.18c.07,0,.05,0,0,0h.15s-.08,0-.06,0-.12,0,0,0h-.08c-.05,0,0,0,0,0h0l.12,0h-.08s0,0,0,0h-.2s.14,0,.08,0h0s-.1,0,0,0h-.13s.16,0,.2,0-.1,0-.13,0,.19,0,.09,0S252.48,170.47,252.47,170.47Zm.06.14s-.07,0,0,0S252.56,170.6,252.53,170.61Zm.22.2s0,0,0,0S252.77,170.8,252.75,170.81Zm.61.56h0S253.38,171.36,253.36,171.37Zm0,0h0s0,0,0,0S253.31,171.38,253.33,171.38Zm-.1-1.34h0S253.2,170,253.23,170Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.876px 170.57px",
                }}
                id="elix9oh79uef"
                className="animable"
              />
              <path
                d="M252.63,170.79h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.63px 170.79px",
                }}
                id="el4v8ddecaebn"
                className="animable"
              />
              <path
                d="M252.42,170.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.42px 170.39px",
                }}
                id="elbag4pgyp9us"
                className="animable"
              />
              <path
                d="M252.6,171.22h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.6px 171.22px",
                }}
                id="ela2guo6cvizw"
                className="animable"
              />
              <path
                d="M253.47,171.44h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.47px 171.44px",
                }}
                id="elw922514wlar"
                className="animable"
              />
              <path
                d="M253,171.53h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253px 171.53px",
                }}
                id="el1egf34k6zx7"
                className="animable"
              />
              <path
                d="M253.19,171.46c-.07,0,0,0,0,0h0C253.26,171.47,253.23,171.46,253.19,171.46Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.195px 171.462px",
                }}
                id="el35gf1d1m7sc"
                className="animable"
              />
              <path
                d="M253.16,171.47h0S253.17,171.48,253.16,171.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.162px 171.472px",
                }}
                id="elgp1tusbxtn5"
                className="animable"
              />
              <path
                d="M253.38,171.39s.09,0,.06,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.413px 171.39px",
                }}
                id="eltssd9j2kyt"
                className="animable"
              />
              <polygon
                points="252.96 171.48 253.05 171.47 252.98 171.47 252.96 171.48"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.005px 171.475px",
                }}
                id="els9n0zwnas"
                className="animable"
              />
              <path
                d="M253,171.43h0S253.05,171.43,253,171.43Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.011px 171.43px",
                }}
                id="elt6ltv8tla9"
                className="animable"
              />
              <path
                d="M253,171.44h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253px 171.44px",
                }}
                id="elfkt6mu3q0d7"
                className="animable"
              />
              <polygon
                points="252.93 171.47 252.95 171.46 252.96 171.46 252.93 171.47"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.945px 171.465px",
                }}
                id="elx2emibca2n"
                className="animable"
              />
              <path
                d="M253,171.33h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253px 171.33px",
                }}
                id="elesdozar4ox7"
                className="animable"
              />
              <path
                d="M252.8,171.37h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.8px 171.37px",
                }}
                id="el4kf7o1u9hws"
                className="animable"
              />
              <path
                d="M252.77,171.35h-.08A.22.22,0,0,0,252.77,171.35Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.73px 171.352px",
                }}
                id="ely0lkdio7cds"
                className="animable"
              />
              <polygon
                points="252.9 171.19 252.9 171.19 253.08 171.19 252.9 171.19"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.99px 171.19px",
                }}
                id="el6ih0j9kf9pn"
                className="animable"
              />
              <path
                d="M252.47,171.08c-.05,0,.11,0,.05,0h-.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.497px 171.08px",
                }}
                id="el58lr315cqdp"
                className="animable"
              />
              <path
                d="M252.6,171.09h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.6px 171.09px",
                }}
                id="ele9qaf4t7ang"
                className="animable"
              />
              <polygon
                points="253.29 169.59 253.33 169.58 253.27 169.59 253.29 169.59"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.3px 169.585px",
                }}
                id="elq8iftdpwds9"
                className="animable"
              />
              <path
                d="M253.16,169.58h0S253.19,169.58,253.16,169.58Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "253.167px 169.58px",
                }}
                id="els49n2uyl3j"
                className="animable"
              />
              <path
                d="M252.3,170.64h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.3px 170.64px",
                }}
                id="el88tujfd0sak"
                className="animable"
              />
              <polygon
                points="252.38 170.53 252.38 170.54 252.49 170.53 252.38 170.53"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.435px 170.535px",
                }}
                id="eld5l7l1eztr"
                className="animable"
              />
              <path
                d="M252.25,170.46s0,0,0,0S252.38,170.45,252.25,170.46Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.279px 170.458px",
                }}
                id="elbss5cuoozl6"
                className="animable"
              />
              <path
                d="M287.42,170.1h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.42px 170.1px",
                }}
                id="el5xxry4dxlet"
                className="animable"
              />
              <polygon
                points="287.25 170.05 287.28 170.05 287.21 170.05 287.25 170.05"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.245px 170.05px",
                }}
                id="elgz53q2ft0zl"
                className="animable"
              />
              <path
                d="M287.25,170c-.13,0,0,0,0,0h.07S287.21,170,287.25,170Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.256px 170px",
                }}
                id="el7i7huo6yfc9"
                className="animable"
              />
              <path
                d="M287.52,170s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.52px 170px",
                }}
                id="elfqipq38911r"
                className="animable"
              />
              <path
                d="M287.51,170Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.51px 170px",
                }}
                id="eldv0b9bq00cl"
                className="animable"
              />
              <path
                d="M287.49,169.94h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.49px 169.94px",
                }}
                id="elhgfe2u9arp7"
                className="animable"
              />
              <path
                d="M287.39,169.94h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.39px 169.94px",
                }}
                id="elq4qj169mskq"
                className="animable"
              />
              <path
                d="M287.45,170h0s-.06,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.437px 170px",
                }}
                id="el63bsdt5mzk4"
                className="animable"
              />
              <path
                d="M287.43,170a.1.1,0,0,1-.08,0S287.45,170,287.43,170Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.391px 170.004px",
                }}
                id="eljqyrgntvf8i"
                className="animable"
              />
              <path
                d="M287.12,170h0S287.16,170,287.12,170Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.129px 170px",
                }}
                id="elt93e13tcr8"
                className="animable"
              />
              <path
                d="M287.55,169.9h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.55px 169.9px",
                }}
                id="el1myl94c5rcs"
                className="animable"
              />
              <path
                d="M287.44,169.9h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.44px 169.9px",
                }}
                id="elfek5cvxa1l"
                className="animable"
              />
              <polygon
                points="287.25 169.95 287.35 169.96 287.25 169.94 287.25 169.95"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.3px 169.95px",
                }}
                id="el3712twcxcdn"
                className="animable"
              />
              <polygon
                points="287.55 169.87 287.61 169.87 287.52 169.86 287.55 169.87"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.565px 169.865px",
                }}
                id="el27k9l98niup"
                className="animable"
              />
              <path
                d="M287.54,169.85h0S287.58,169.84,287.54,169.85Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.549px 169.848px",
                }}
                id="elzcfhivbj43c"
                className="animable"
              />
              <path
                d="M287.5,169.83h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.5px 169.83px",
                }}
                id="elzxnrl6zcala"
                className="animable"
              />
              <path
                d="M287.41,169.88h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.41px 169.88px",
                }}
                id="elkg1keuu9qgp"
                className="animable"
              />
              <path
                d="M287.41,169.88Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.41px 169.88px",
                }}
                id="eli71ka6egt5"
                className="animable"
              />
              <path
                d="M287.41,169.89h0S287.4,169.89,287.41,169.89Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.408px 169.89px",
                }}
                id="elspmi1u76hm"
                className="animable"
              />
              <path
                d="M287.32,169.92a.4.4,0,0,0-.15,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.245px 169.916px",
                }}
                id="elo64i9lxiht"
                className="animable"
              />
              <path
                d="M287.32,169.89h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.32px 169.89px",
                }}
                id="elucwev00jjw"
                className="animable"
              />
              <polygon
                points="287.29 169.89 287.32 169.89 287.32 169.89 287.29 169.89"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.305px 169.89px",
                }}
                id="el30fqzl47jo2"
                className="animable"
              />
              <path
                d="M287.44,169.84h-.05l0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.415px 169.84px",
                }}
                id="elxiaiwn7x1uj"
                className="animable"
              />
              <path
                d="M287.42,169.86h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.42px 169.86px",
                }}
                id="el1rvirfwp7zy"
                className="animable"
              />
              <path
                d="M287.39,169.79h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.39px 169.79px",
                }}
                id="ela3pye1ox81p"
                className="animable"
              />
              <path
                d="M287.36,169.85s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.36px 169.85px",
                }}
                id="el4jxyq2z3dht"
                className="animable"
              />
              <path
                d="M287.47,169.82h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.47px 169.82px",
                }}
                id="el39tv8xu2kw1"
                className="animable"
              />
              <path
                d="M287.43,169.86h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.43px 169.86px",
                }}
                id="el9iu13bpomyp"
                className="animable"
              />
              <path
                d="M287.43,169.86h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.43px 169.86px",
                }}
                id="elxj8slx9lpb"
                className="animable"
              />
              <path
                d="M286.91,169.8c.07,0,.14,0,.06,0l.41,0h-.1l-.15,0s0,0,.06,0-.13,0-.25,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.145px 169.8px",
                }}
                id="elal5h4cqpors"
                className="animable"
              />
              <path
                d="M287.37,169.81h0l.1,0h-.09v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.42px 169.81px",
                }}
                id="elfft9lrkaze7"
                className="animable"
              />
              <path
                d="M287.38,169.83a1.2,1.2,0,0,0-.19,0h.1s0,0,.08,0h.05s0,0,0,0h0S287.37,169.82,287.38,169.83Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.305px 169.828px",
                }}
                id="elrm67l5c9b7"
                className="animable"
              />
              <path
                d="M287,169.92h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287px 169.92px",
                }}
                id="el5xxx10runop"
                className="animable"
              />
              <path
                d="M287.05,169.92h0a.26.26,0,0,0,.09,0h0l.13,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.16px 169.922px",
                }}
                id="el5c4wlyx0r77"
                className="animable"
              />
              <path
                d="M287.2,169.87h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.2px 169.87px",
                }}
                id="elqghrmkscqkk"
                className="animable"
              />
              <polygon
                points="287.43 169.79 287.51 169.78 287.4 169.78 287.43 169.79"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.455px 169.785px",
                }}
                id="elp2xvw01m7d"
                className="animable"
              />
              <polygon
                points="287.42 169.77 287.49 169.77 287.45 169.76 287.42 169.77"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.455px 169.765px",
                }}
                id="els89fnk49y1"
                className="animable"
              />
              <path
                d="M286.51,168.83h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.51px 168.83px",
                }}
                id="el9e7ymq8wd0r"
                className="animable"
              />
              <path
                d="M286.51,168.86h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.51px 168.86px",
                }}
                id="elv2715yicvwl"
                className="animable"
              />
              <path
                d="M287.39,169.34h0S287.41,169.34,287.39,169.34Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.394px 169.34px",
                }}
                id="el6vhjp1ydj62"
                className="animable"
              />
              <path
                d="M286.92,169.08h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.92px 169.08px",
                }}
                id="elhqn0txwc0wn"
                className="animable"
              />
              <path
                d="M286.61,169.09h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.61px 169.09px",
                }}
                id="elxni00d6flel"
                className="animable"
              />
              <path
                d="M286.42,168.68h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.42px 168.68px",
                }}
                id="elkh2jsqc8qn"
                className="animable"
              />
              <path
                d="M287.38,169.59h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.38px 169.59px",
                }}
                id="eljrwde4spwdg"
                className="animable"
              />
              <path
                d="M287.32,168.8h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.32px 168.8px",
                }}
                id="elh6t8eyv7ywt"
                className="animable"
              />
              <path
                d="M287.43,169.56h0s0,0,0,0S287.48,169.57,287.43,169.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.441px 169.562px",
                }}
                id="el4lnhb0e5djd"
                className="animable"
              />
              <path
                d="M286.46,168.12h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.46px 168.12px",
                }}
                id="elkcuqlac7tkl"
                className="animable"
              />
              <path
                d="M287.22,167.92Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.22px 167.92px",
                }}
                id="elp7nzrimb28"
                className="animable"
              />
              <path
                d="M287.45,169.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.45px 169.7px",
                }}
                id="el7qgamz3ie1o"
                className="animable"
              />
              <path
                d="M287.15,169.73h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.15px 169.73px",
                }}
                id="el1xg1f258x3m"
                className="animable"
              />
              <path
                d="M287,169.55Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287px 169.55px",
                }}
                id="elmdo3ss4mujg"
                className="animable"
              />
              <path
                d="M287.42,169.71h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.42px 169.71px",
                }}
                id="elk34efhsvj3"
                className="animable"
              />
              <path
                d="M287.45,169.7h0S287.46,169.71,287.45,169.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.452px 169.702px",
                }}
                id="eljj7m849dgc"
                className="animable"
              />
              <path
                d="M287.45,169.69h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.45px 169.69px",
                }}
                id="el7bc15sdhk8m"
                className="animable"
              />
              <path
                d="M286.68,168.41Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.68px 168.41px",
                }}
                id="el8x7m0wjbaud"
                className="animable"
              />
              <polygon
                points="286.6 169.29 286.6 169.29 286.6 169.29 286.6 169.29"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.6px 169.29px",
                }}
                id="elgrmm8k1es7m"
                className="animable"
              />
              <path
                d="M286.54,169.28c.1,0,.08,0,.06,0l.22,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.68px 169.28px",
                }}
                id="elebq9aiar8ss"
                className="animable"
              />
              <path
                d="M286.49,168.77h-.07c-.11,0-.08,0-.17,0,.24,0,0,0,.26,0h-.06c-.11,0,.18,0,.14,0a.11.11,0,0,1-.08,0h0l0,0h.12c-.07,0-.13,0-.12,0-.23,0,.06,0-.07,0h0a.71.71,0,0,0-.16,0l.27,0s0,0,0,0-.07,0-.06,0h0l-.17,0s.14,0,.16,0,0,0,.07,0h.09c.12,0-.18,0,0,0h-.11a.92.92,0,0,0,.19,0l-.07,0a.76.76,0,0,1,.26,0l.1,0h-.38l-.08,0a2.73,2.73,0,0,1,.37,0h-.26c.12,0-.05,0,0,0h-.08l.11,0h-.1c.17,0,.11,0,.17,0s-.12,0-.11,0,.21,0,.1,0h0c.06,0,0,0,0,0h0s0,0,.15,0h.3c-.13,0-.44,0-.51,0s0,0,0,0h.19l-.19,0h.2l-.22,0s0,0,0,0h0s.28,0,.27,0h-.3c-.05,0-.07,0-.06,0h-.09l0,0h.09c.12,0,.33,0,.31,0h-.24s.07,0,0,0h-.06s.16,0,.05,0-.15,0-.08,0l.65,0-.2,0h-.34c.12,0,0,0,.1,0s0,0,0,0H287s-.06,0-.06,0h-.05c.11,0,0,0,0,0a2.13,2.13,0,0,1,.38,0s-.1,0-.08,0,.26,0,.16,0H287s.06,0,.06,0l-.05,0s0,0,0,0,0,0-.09.07h.23c.06,0,.14,0,.1,0h-.11s.06,0,0,0-.14,0,0,0h.07c.06,0,.17,0,.14,0h.11c-.16,0,0,0-.16,0a.74.74,0,0,0,.18,0h-.24a.59.59,0,0,1,.18,0h0s.06,0,0,0-.09,0,0,0l-.05,0h0s-.13,0,0-.05c0,0-.05,0,0,0h.06s.07,0,0,0c.18,0,0,0,.07-.05h-.09s0,0-.06,0h-.07c-.07,0,.05,0,0,0s0,0,0,0c.17,0-.13,0,0-.06s0,0,0,0h-.1c.14,0,0,0,.1,0a.87.87,0,0,0-.2,0s0,0,0,0,0,0,0,0h0a.59.59,0,0,1,.19,0l-.18,0h.07s-.09,0,0,0,0-.05.14-.09c-.2,0,.09,0-.07,0h.12c-.05,0,.08,0-.06,0h.09s0,0,.06,0a1.11,1.11,0,0,0-.3,0c.07,0,.06,0,0,0h.11s.06,0-.05,0,0,0,0,0,0,0,0,0h0c-.13,0,0,0,0,0h0c.1,0-.05,0,.06,0h-.08c-.18,0,.1,0,0,0h0c-.07,0,.24,0,.26,0h0c.11,0-.11,0-.14,0h.17s-.2,0-.09,0h-.12s.17,0,0,0h0a1.13,1.13,0,0,0-.14,0c.29,0-.14,0,.12-.05v0h0s0,0-.12,0h.06c.05,0,0,0-.09,0h.09s.13,0,0-.05h0c-.11,0,.24-.07-.08-.06h-.08s-.14,0-.19,0a.31.31,0,0,1,.11,0h0c.16,0,.05,0,.17,0s.07-.08.08-.11h-.13v0h0a.41.41,0,0,1,.16,0h0c-.05,0-.11,0-.1,0h.09v0h-.1a.34.34,0,0,0-.11,0c.17,0,.06-.06.11-.07s-.07,0-.06,0-.14,0-.12,0a1.3,1.3,0,0,0,.27,0c-.12,0,0,0-.19,0h.14c-.09,0,.06,0,0-.07a.56.56,0,0,1-.13,0l.15,0s-.17,0-.07,0h-.07c.15-.06-.13-.1.15-.16-.06,0-.07,0-.07,0h-.12l.12,0c-.12,0,0,0-.12,0h0c-.13,0-.29,0-.46,0,.15,0-.22,0,0,0h-.1c.08,0,.09,0,.06,0h0s-.05,0-.1.07h0c.13,0,0,0,.15,0-.17,0,0,.05-.2.06,0,0,.1,0,.1,0h-.12c-.06,0,0,0,0,0h0c.05,0,0,.06.09.08h0c.07,0,0,0,0,0h0s.06,0,.06,0a.66.66,0,0,0-.14,0h.25c-.06,0-.1,0-.17,0s.14,0-.07,0c.18,0,.1,0,.14,0s.14,0,.12,0-.23,0-.23,0h0s.06,0,0,0c.11,0,0,0,.13,0h0s.2,0,.12,0h-.08s0,0,0,0,0,0-.14,0,.11,0,0,0c.19,0,0,0,.12,0h-.14c.09,0-.08,0,0,0s.1,0,.16,0-.22,0-.18,0,0,0,.09,0l-.09,0s.09,0,.14,0c-.1,0,0,0,.07,0h-.08c-.06,0,.17,0,.11,0h-.18c.07,0,0,0,0,0a.4.4,0,0,1,.16,0s-.08,0-.07,0-.12,0,0,0h-.08c-.06,0,0,0,0,0s0,0,0,0h-.08l.07,0h-.1s.13,0,.08,0h0s-.09,0,0,0l-.12,0s.15,0,.2,0-.1,0-.14,0,.19,0,.09,0Zm.06.13s-.07,0,0,0S286.58,168.9,286.55,168.9Zm.21.2s0,0,0,0S286.78,169.1,286.76,169.1Zm.62.56h0Zm0,0h0S287.32,169.67,287.34,169.68Zm-.1-1.34h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.881px 168.85px",
                }}
                id="elwdhqmi7s5dk"
                className="animable"
              />
              <path
                d="M286.64,169.09h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.64px 169.09px",
                }}
                id="el5ln82tz13ih"
                className="animable"
              />
              <path
                d="M286.44,168.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.44px 168.68px",
                }}
                id="elyk8mdppm5v"
                className="animable"
              />
              <path
                d="M286.62,169.52h0S286.62,169.51,286.62,169.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.62px 169.518px",
                }}
                id="ell3zqyfomqc"
                className="animable"
              />
              <path
                d="M287.49,169.73h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.49px 169.73px",
                }}
                id="eljwl4n3kcpt"
                className="animable"
              />
              <path
                d="M287,169.83h0A.08.08,0,0,0,287,169.83Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287px 169.83px",
                }}
                id="el12rbodmm7kei"
                className="animable"
              />
              <path
                d="M287.21,169.76c-.07,0,0,0,0,0h0C287.28,169.76,287.24,169.76,287.21,169.76Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.214px 169.76px",
                }}
                id="elp5z0qatrxxh"
                className="animable"
              />
              <path
                d="M287.18,169.77Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.18px 169.77px",
                }}
                id="el2kvgvxwk53b"
                className="animable"
              />
              <path
                d="M287.39,169.69h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.39px 169.69px",
                }}
                id="el2vx98yvbfk"
                className="animable"
              />
              <polygon
                points="286.98 169.77 287.06 169.77 286.99 169.77 286.98 169.77"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.02px 169.77px",
                }}
                id="eloelm0bi884"
                className="animable"
              />
              <path
                d="M287,169.72h0s0,0,0,0S287.06,169.72,287,169.72Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.013px 169.72px",
                }}
                id="eldknuv1al6gi"
                className="animable"
              />
              <path
                d="M287,169.74h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287px 169.74px",
                }}
                id="el5gnm1i3knap"
                className="animable"
              />
              <polygon
                points="286.95 169.76 286.97 169.76 286.97 169.75 286.95 169.76"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.96px 169.755px",
                }}
                id="elg521zleiltp"
                className="animable"
              />
              <path
                d="M287.05,169.63h0s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.05px 169.63px",
                }}
                id="el1jk7hmoj4ng"
                className="animable"
              />
              <path
                d="M286.81,169.67h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.81px 169.67px",
                }}
                id="elu1kqi7rg13j"
                className="animable"
              />
              <path
                d="M286.78,169.64h-.08S286.76,169.65,286.78,169.64Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.74px 169.642px",
                }}
                id="elenpviw3wvm5"
                className="animable"
              />
              <polygon
                points="286.92 169.49 286.92 169.49 287.09 169.48 286.92 169.49"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.005px 169.485px",
                }}
                id="elx1bzclh2jrm"
                className="animable"
              />
              <path
                d="M286.48,169.38s.11,0,.06,0h-.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.517px 169.38px",
                }}
                id="el0gdr8x2sv525"
                className="animable"
              />
              <path
                d="M286.62,169.38h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.62px 169.38px",
                }}
                id="elnx16ndd4twb"
                className="animable"
              />
              <polygon
                points="287.31 167.88 287.35 167.87 287.29 167.88 287.31 167.88"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.32px 167.875px",
                }}
                id="el9yafb54kroq"
                className="animable"
              />
              <path
                d="M287.18,167.88h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.18px 167.88px",
                }}
                id="elzfrxnft9xif"
                className="animable"
              />
              <path
                d="M286.32,168.94h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.32px 168.94px",
                }}
                id="elhbo9zsbnp6"
                className="animable"
              />
              <polygon
                points="286.4 168.83 286.4 168.83 286.5 168.82 286.4 168.83"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.45px 168.825px",
                }}
                id="eldx3o95zja9m"
                className="animable"
              />
              <path
                d="M286.26,168.75h0S286.39,168.74,286.26,168.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.289px 168.748px",
                }}
                id="el9maeuqs6je"
                className="animable"
              />
              <path
                d="M287.81,132.05c0-.05-.07,0-.09,0A.05.05,0,0,0,287.81,132.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.765px 132.053px",
                }}
                id="eluraer3vxm7s"
                className="animable"
              />
              <polygon
                points="288.01 132.88 287.98 132.86 288.06 132.98 288.01 132.88"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.02px 132.92px",
                }}
                id="elpprqnmnyxhf"
                className="animable"
              />
              <path
                d="M288.05,133.65c.12-.08,0-.28,0-.42l0,0-.1-.06A2,2,0,0,1,288.05,133.65Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.027px 133.41px",
                }}
                id="eltdp0t61bvtr"
                className="animable"
              />
              <path
                d="M287.8,134.08c.05,0,.05.08,0-.1,0,0,0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.819px 134.039px",
                }}
                id="el0ptr88ni0b9a"
                className="animable"
              />
              <path
                d="M287.8,134c0-.08,0,.05,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.8px 133.993px",
                }}
                id="el2ljmoz1nczp"
                className="animable"
              />
              <path
                d="M287.85,134.37l-.05-.1s0,.12-.05.08C287.78,134.46,287.8,134.44,287.85,134.37Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.8px 134.349px",
                }}
                id="elu89ew6kk05q"
                className="animable"
              />
              <path
                d="M287.94,134.47l0-.05S287.91,134.44,287.94,134.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.933px 134.445px",
                }}
                id="elcm0gd683tgg"
                className="animable"
              />
              <path
                d="M287.88,134.3l0,.12s.06,0,0-.06S287.91,134.35,287.88,134.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.883px 134.36px",
                }}
                id="el2jjgxi8fgz3"
                className="animable"
              />
              <path
                d="M287.89,134.23c0-.06,0-.14.09,0C288,134,287.86,134,287.89,134.23Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.934px 134.144px",
                }}
                id="elbxvrsnml0ws"
                className="animable"
              />
              <path
                d="M288.18,133.68l0-.08,0-.07S288.14,133.69,288.18,133.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.171px 133.605px",
                }}
                id="elq05f4rhyg7e"
                className="animable"
              />
              <path
                d="M287.82,135.05s.06,0,.1-.05l0-.08S287.85,135,287.82,135.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.87px 134.985px",
                }}
                id="el2uwjp827uxk"
                className="animable"
              />
              <path
                d="M287.92,135l.05.11C288,135,288,135,287.92,135Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.954px 135.055px",
                }}
                id="elilfprktkzs"
                className="animable"
              />
              <polygon
                points="288.08 134.48 287.98 134.29 288.09 134.52 288.08 134.48"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.035px 134.405px",
                }}
                id="elkz8mk4vfmw"
                className="animable"
              />
              <polygon
                points="287.83 135.5 287.77 135.44 287.87 135.55 287.83 135.5"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.82px 135.495px",
                }}
                id="el72qll48hnu3"
                className="animable"
              />
              <path
                d="M287.86,135.8c-.05-.05-.08.07-.12,0a.53.53,0,0,0,.11.21A.27.27,0,0,1,287.86,135.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.8px 135.899px",
                }}
                id="elep0b10wyz9t"
                className="animable"
              />
              <path
                d="M287.91,136.14a.6.6,0,0,0-.06-.11A.3.3,0,0,0,287.91,136.14Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.88px 136.085px",
                }}
                id="ele5981k22j8i"
                className="animable"
              />
              <path
                d="M288,135.32a.11.11,0,0,0-.07.09l.07,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.965px 135.365px",
                }}
                id="elt8cllopkk8k"
                className="animable"
              />
              <path
                d="M288,135.36h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 135.36px",
                }}
                id="elqwpj7eeftvc"
                className="animable"
              />
              <path
                d="M288,135.31h0S288,135.31,288,135.31Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 135.31px",
                }}
                id="elfis2ep138d9"
                className="animable"
              />
              <path
                d="M288,134.8c.05.07.1.21.15.07S288.1,134.9,288,134.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.082px 134.865px",
                }}
                id="el2loui3a9t2x"
                className="animable"
              />
              <path
                d="M288.06,135.24v-.12s-.05,0-.07,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.025px 135.18px",
                }}
                id="ela7nz9odhis9"
                className="animable"
              />
              <polygon
                points="288.08 135.3 288.06 135.24 288.05 135.29 288.08 135.3"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.065px 135.27px",
                }}
                id="elftko726npk9"
                className="animable"
              />
              <path
                d="M288,135.92a.48.48,0,0,1,0,.18,1.7,1.7,0,0,0,0-.22S288,135.84,288,135.92Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.004px 135.985px",
                }}
                id="elgc7dustxj2n"
                className="animable"
              />
              <path
                d="M288,135.65h0S288,135.64,288,135.65Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 135.648px",
                }}
                id="elnwp01daffkp"
                className="animable"
              />
              <path
                d="M288.05,136.76h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.05px 136.76px",
                }}
                id="elx8tigqf4fk"
                className="animable"
              />
              <path
                d="M288,135.86a.09.09,0,0,0,0-.11.51.51,0,0,1,0,.13S288.05,135.88,288,135.86Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.011px 135.815px",
                }}
                id="eloz29kv4tuo"
                className="animable"
              />
              <path
                d="M288,136.32a.3.3,0,0,0,0-.1S287.93,136.31,288,136.32Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.987px 136.27px",
                }}
                id="elv1z3h5n25m"
                className="animable"
              />
              <path
                d="M288,135.68h0S288,135.69,288,135.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 135.682px",
                }}
                id="elor5ztdx5m88"
                className="animable"
              />
              <path
                d="M288,135.68a.18.18,0,0,0,.05.07l0-.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.025px 135.705px",
                }}
                id="el4k5neuzf8c4"
                className="animable"
              />
              <path
                d="M288.54,136.94c-.07,0-.14-.15-.07-.28s-.28,0-.41.09c0,0,.06,0,.11.12,0,0,.07-.28.14-.18s0,.14-.05.22.14.2.26.16l0-.14S288.53,137,288.54,136.94Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.3px 136.842px",
                }}
                id="elsia77v6c2y"
                className="animable"
              />
              <path
                d="M288.06,136.51v0a.5.5,0,0,1-.09.18c0,.08.07.1.09.11v-.24Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.015px 136.655px",
                }}
                id="elsjqchop9vjg"
                className="animable"
              />
              <path
                d="M288,136.23c.07,0,.11.16.19.08s-.07-.11-.11-.19c0,.24,0-.07-.08.11a.69.69,0,0,1,0-.13l0,.12h0s0,0,0,0h0c0,.07,0,0,0,0h0l.06.24A1,1,0,0,0,288,136.23Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.105px 136.28px",
                }}
                id="elkkc1he7jsr"
                className="animable"
              />
              <path
                d="M288.35,135h0S288.35,135,288.35,135Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.35px 135px",
                }}
                id="elu3j4jg9ro4n"
                className="animable"
              />
              <path
                d="M288.31,135.09s0,0,0-.09,0,0,.06,0-.06-.12-.1,0h0a.44.44,0,0,1-.12.22A.48.48,0,0,0,288.31,135.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.268px 135.083px",
                }}
                id="ely95t397bl4"
                className="animable"
              />
              <path
                d="M288.19,135.66l.05.21.06-.11h0C288.27,135.65,288.23,135.68,288.19,135.66Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.245px 135.765px",
                }}
                id="el21fg60khcvz"
                className="animable"
              />
              <polygon
                points="288.01 136.78 287.93 136.76 288.05 136.87 288.01 136.78"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.99px 136.815px",
                }}
                id="eld9hka9jo30w"
                className="animable"
              />
              <polygon
                points="288.04 137.05 287.97 137.04 288.01 137.11 288.04 137.05"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.005px 137.075px",
                }}
                id="elxjklah3c8q8"
                className="animable"
              />
              <path
                d="M289.64,151.87l0,.06A.14.14,0,0,0,289.64,151.87Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.642px 151.9px",
                }}
                id="elajp30dcrhp"
                className="animable"
              />
              <path
                d="M289.62,151.44h0S289.62,151.46,289.62,151.44Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.62px 151.444px",
                }}
                id="eljrnm2vejgol"
                className="animable"
              />
              <path
                d="M288.37,143.52c-.06.09-.16.19-.08.18l.08,0A.25.25,0,0,1,288.37,143.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.315px 143.61px",
                }}
                id="elykv2hof1gwl"
                className="animable"
              />
              <path
                d="M289,147.93l0,0-.08-.05A.22.22,0,0,1,289,147.93Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.96px 147.905px",
                }}
                id="elspfiqjizf3d"
                className="animable"
              />
              <path
                d="M289.36,148l0,.12A.55.55,0,0,0,289.36,148Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.362px 148.06px",
                }}
                id="elu59w5wuphep"
                className="animable"
              />
              <path
                d="M289.84,154.23l.08.06C289.92,154.18,289.88,154.23,289.84,154.23Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.88px 154.254px",
                }}
                id="el2sg6fqo4xsn"
                className="animable"
              />
              <path
                d="M288.2,139.74a.93.93,0,0,0,0,.19C288.2,139.84,288.26,139.77,288.2,139.74Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.211px 139.835px",
                }}
                id="elq43j9jlipqm"
                className="animable"
              />
              <path
                d="M288.83,151.75s0,.05.05,0S288.85,151.74,288.83,151.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.863px 151.749px",
                }}
                id="el5zo95nq7g5y"
                className="animable"
              />
              <path
                d="M288.18,140.15s0,0,0,0a.84.84,0,0,1,0-.18A.22.22,0,0,0,288.18,140.15Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.17px 140.06px",
                }}
                id="elhtx8wj7nmlm"
                className="animable"
              />
              <path
                d="M290.21,162.77v0S290.21,162.79,290.21,162.77Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.21px 162.774px",
                }}
                id="elr4e0j5hmoqg"
                className="animable"
              />
              <path
                d="M289.56,165.3a.41.41,0,0,0,0-.1A.46.46,0,0,0,289.56,165.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.56px 165.25px",
                }}
                id="elgwjka2w29rf"
                className="animable"
              />
              <path
                d="M288.06,138.11c0-.06,0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.06px 138.097px",
                }}
                id="eluv8hloyot3m"
                className="animable"
              />
              <path
                d="M288.34,137.82l0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.34px 137.82px",
                }}
                id="eltbsmrxs05d8"
                className="animable"
              />
              <path
                d="M288.68,140.68h0S288.69,140.67,288.68,140.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.682px 140.678px",
                }}
                id="el03r7p6afa1su"
                className="animable"
              />
              <path
                d="M288.09,138a.19.19,0,0,0,0,.08A.45.45,0,0,1,288.09,138Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.088px 138.04px",
                }}
                id="eljua95vj85p8"
                className="animable"
              />
              <path
                d="M288.05,138a.43.43,0,0,1,0,0S288,138,288.05,138Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.039px 138px",
                }}
                id="el1by6e4zfswl"
                className="animable"
              />
              <path
                d="M288.07,138.19a.14.14,0,0,1,0-.09h0A.28.28,0,0,0,288.07,138.19Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.066px 138.145px",
                }}
                id="el6u3c28b6tga"
                className="animable"
              />
              <path
                d="M289.77,158.21h0C289.8,158.27,289.79,158.25,289.77,158.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.779px 158.229px",
                }}
                id="eldb9q1k9pr8d"
                className="animable"
              />
              <polygon
                points="289.23 144.93 289.23 144.93 289.23 144.94 289.23 144.93"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.23px 144.935px",
                }}
                id="elxcbk1nd74a"
                className="animable"
              />
              <path
                d="M289.3,145.13c-.1-.08-.09-.13-.08-.19L289,145Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.15px 145.035px",
                }}
                id="elhkmjhmfqd6u"
                className="animable"
              />
              <path
                d="M289.72,152.91l.06-.15c.12.17.08.29.17.15-.23,0-.05-.54-.29-.55l0-.1s0,0,0,.06c.11-.19-.18-.12-.16-.36,0-.08.05-.14.08-.09l-.07-.17c0-.06.05,0,.05,0l0-.28h0a1,1,0,0,1-.13-.16c0-.07,0,0,.06,0l-.07-.15c.06-.06.13.06.12.17.23-.16-.06-.13.06-.44l0,.14c.13-.1.07-.21.15-.38,0,0-.2.34-.26.18a.26.26,0,0,0,0-.2c.08,0,.05-.09,0-.2h.12c0-.11-.1-.1-.15-.17s.09-.16.16-.2c0-.21-.13.16-.17-.09a.81.81,0,0,0-.09-.51c0-.12.07,0,.1,0l-.1-.11s0,0,.05,0-.12,0-.15-.14.16-.3,0-.48c0,0,.07,0,.12,0a1.39,1.39,0,0,0-.22-.4l.07-.18c-.08.13-.19,0-.28-.16a.59.59,0,0,0-.08.11l-.07-.22s.05,0,.1,0l0,0c0-.09.11-.05.17,0l-.06.08a.35.35,0,0,0,.21.09.15.15,0,0,1,0-.08s0,0,0,0,0,0,0-.07l.12-.05h-.05l.06-.3a1.78,1.78,0,0,1-.39-.71c.09.08.15,0,.25,0-.11.1.07.26.05.43,0-.09.06-.07.08,0l-.12-.3c.09,0,.06-.07.1-.16-.18.07-.13-.22-.19-.34s.12,0,.11.17c.13-.18-.21-.21-.11-.38,0,0,0,.06,0,.07s0-.3,0-.25v0c0-.25,0-.75-.19-.78a.56.56,0,0,1,.21,0l-.52-.42c.13,0,.42-.13.51.07a.41.41,0,0,0,0-.26s-.18.11-.2.06c.14-.16.07-.11.19-.31,0,.07-.23,0-.23-.09s.2,0,.22,0l-.21-.23c0,.06.17,0,.22-.05s0-.21,0-.27l0,.1c0-.27-.29-.25-.29-.51,0,0,.25.06.27-.07s-.5-.25-.59-.49l.06,0-.08-.18a3.35,3.35,0,0,1,.63.57c0-.1.07,0,.05-.18s.09.08.09.12l0-.35c-.12,0-.13-.26-.23-.25l.14.38c-.11,0-.34-.21-.33-.45,0-.07.21,0,.25,0s-.09-.24-.06-.26l.06.1c0-.14-.16-.22-.06-.39s.14-.18.07-.18a.92.92,0,0,1-.64.08,1.66,1.66,0,0,1,.17-.38c.1-.1.26.13.35.05s-.08-.05-.09-.08.1,0,.08-.11,0-.26-.11-.05c0-.13,0-.14,0-.29-.08.05-.27-.12-.31-.25s.07,0,.09,0c0-.26,0-.4,0-.65,0,0,.06-.14.11-.09a.37.37,0,0,1-.07-.14c.09-.15,0,.16.14,0-.07,0-.1-.19-.15-.3s0,.25,0,.36c-.15,0-.31-.18-.4-.43s.1-.09.07-.12c0-.39-.27-.32-.18-.64.09-.13.19,0,.25-.05.05.17,0,.26,0,.37l0-.14a.26.26,0,0,1,0,.23c.08-.11,0-.65.05-.95,0-.05-.07-.14-.11-.09l0,.09c-.07.09-.11-.17-.14-.05a.69.69,0,0,1-.12-.44l.11,0s-.06.12,0,.17.13-.28,0-.35l-.07.13c-.06-.13-.18-.27-.15-.43-.11.07,0,0-.11.15.15-.1,0,.4.17.24-.14.12-.05.2-.17.35,0,0,.07,0,.06.08l0,0h0s0,0,0,0,0-.06.05-.07.1.27.14.34,0,0-.08,0l.07.11c-.08,0-.12.21-.17,0v.16a.44.44,0,0,0,0,.32c0,.08.09.19,0,.12l.06.26h0c0,.26.16.52,0,.82a.71.71,0,0,1,0,.32l-.07,0c0,.11-.05.33,0,.38-.17.12,0,.53,0,.73h.09a1.75,1.75,0,0,0,.07.42h.08c.07.14,0,.37,0,.59,0,0,0-.07,0-.11-.16.28.16.56.05.81a.16.16,0,0,1,0,.15h0v0a.36.36,0,0,0,0,.09l0-.07a.86.86,0,0,0,0,.18l-.08,0h0c0,.07,0,0,.1,0l.06.08c-.13.07.05.19-.09.27.08,0,.07.35.22.38,0,0,0,0,0,0a.36.36,0,0,0,0,.23l-.1,0,.15.16c-.06.11-.1.15-.18,0,.14.24.05.2.19.41h-.07c0,.07.11.19.05.24s0,.83-.08,1.27c.2-.09-.08.21.08.22-.05,0-.07.08-.12,0,.06.21-.06.33.09.48-.05,0-.08-.05-.11,0s.05.07,0,.09,0,0-.06-.06c0,.22.17.24.31.25a.17.17,0,0,0,0,.25s-.08.11-.11,0c0,.08,0,.48.07.49s0,.11,0,.13,0,.19,0,.36v0c.14.07,0,.15.06.29h0c-.08.33,0,.14,0,.46l.08,0c.18,0-.09.28,0,.46l0-.06c.07.09-.23.29-.25.39s0,0,0-.06c-.09.27.11,0,.15.22-.06,0-.15,0-.16.09s.21.38.11.56c0,0,.09-.08.12,0s-.15.39,0,.51c0,0,0,0,0,0s.08.35.16.41c-.29.11.17.66-.1.67l0,.33h0a.38.38,0,0,0,.13.35c0,.05-.05,0-.06,0a.65.65,0,0,0,.12.59l-.09,0c.06.06-.11.45,0,.74a0,0,0,0,1,0,0c.11.34-.2.83.12.93l.09.22c0-.29.14,0,.18-.08,0,.11-.08.3-.1.16v0c-.16,0,0,.36-.15.36,0,.57,0,1.13,0,1.66l.13-.07v.19s-.1.07-.1-.06c-.07.07.07.22.1.22,0,.26-.15-.1-.15,0v.11l0,0c.05.08.11.09.11.25s-.07.05-.08.09,0,0,0,.06v.24l.1-.1c.05.1,0,.22.11.11-.16.16,0,.87-.07,1a.83.83,0,0,1,.09.47c0-.05.13-.08.13.08-.24,0-.09.28-.25.41.1-.11.05.44.2.26,0,.13-.09,0-.14.11a7.12,7.12,0,0,1,0,1.05s.08-.06.13,0l-.15.2c0,.08.18.06.08.2,0,0,.07,0,.08,0-.11.75.2,1.51,0,2.27.06-.11.07,0,.07,0s.08,0,.12,0l-.1.31c.12.06,0,.34.14.37l0-.1c.14.13.28-.2.45-.19-.15-.14.2-.4,0-.63,0-.06.05,0,.09,0s-.09-.13-.07-.27h0a5.75,5.75,0,0,1,.05-1l-.08,0a.33.33,0,0,1,.05-.16c-.13-.1,0,.26-.14.17.17-.13,0-.66.16-.75,0-.15-.09,0-.1-.1l.05,0-.07,0c0-.1.07-.15.11-.15a.26.26,0,0,0,0,.08c.05-.12,0-.34,0-.38l0,.14a8.54,8.54,0,0,0-.15-1.25l0,0c-.08-.28,0-.23,0-.55h0a.35.35,0,0,1-.07-.23c.05.08.08-.16.14,0-.1-.31-.07-.15-.23-.35l.07-.06c-.07-.06-.1-.07-.12-.23.06.13.09-.1.17,0,.13-.34-.17-.49,0-.65-.18-.14-.08.29-.12.21-.06.07-.15-.12-.13-.23s.22-.19.2-.46c0,0,0,0,0,.06s-.08-.19,0-.32c-.11-.06,0-.16-.14-.17l.05.24c-.05-.14-.22-.26-.15-.49,0,0,.07.08.09.13s0-.13,0-.2,0,.29.16.44c.05-.08-.12-.28,0-.21-.2-.1,0-.27-.15-.44h.09a.3.3,0,0,1-.06-.14l.11.09c-.1-.14.07-.29,0-.43,0,.3-.1.07-.15.2-.13-.36.21-.22.14-.62,0,.05,0,.21-.08.12a.69.69,0,0,1,.08-.23c0-.27-.08,0-.14-.07.1-.09,0-.13-.08-.24h.08c0-.31-.18-.07-.13-.35l.13.11,0-.14.1,0a.46.46,0,0,1-.05-.36c-.06,0-.15,0-.16-.14s.08,0,.06-.15c.05,0,.12.09.05.18s.06,0,.08,0,0-.25,0-.32a.19.19,0,0,1,0,.08l-.13-.28s.06,0,.09,0a.75.75,0,0,1-.07-.18c.07,0,.14,0,.18.12l-.09-.26s.08-.14.1-.11c0-.14-.15-.35-.11-.55a.15.15,0,0,1,0-.07c0-.18.08-.35,0-.57l.12-.13c0-.16-.15.18-.21-.1s.1-.08.13-.15-.2-.27-.11-.46C289.72,152.8,289.7,152.85,289.72,152.91Zm-.16-2.11s.07.12,0,.15S289.53,150.87,289.56,150.8Zm-.37-3.19s0,.08,0,.13A.2.2,0,0,1,289.19,147.61Zm-1-8.89Zm0-.19s0,0,0,0,0,0,0,.1S288.21,138.58,288.18,138.53Zm1.05,20.3,0,0C289.28,158.88,289.26,158.85,289.23,158.83Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.961px 151.455px",
                }}
                id="ella3jfl3cpe"
                className="animable"
              />
              <path
                d="M289.33,148l-.05,0S289.31,148,289.33,148Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.305px 148px",
                }}
                id="elep8li7mzi9"
                className="animable"
              />
              <path
                d="M289.83,154.21h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.83px 154.21px",
                }}
                id="el991bra91tzt"
                className="animable"
              />
              <path
                d="M289.05,141.42a.29.29,0,0,1,0,.08S289,141.48,289.05,141.42Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.04px 141.46px",
                }}
                id="elrbjb8vtltuc"
                className="animable"
              />
              <path
                d="M288,137.53l0,.12a.17.17,0,0,1,0-.11S288,137.57,288,137.53Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.995px 137.59px",
                }}
                id="el7swsk1nx07a"
                className="animable"
              />
              <path
                d="M288.46,136.45s0,0,0,0h0C288.41,136.48,288.45,136.53,288.46,136.45Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.448px 136.472px",
                }}
                id="eln9xiag7z1ll"
                className="animable"
              />
              <path
                d="M288.27,137.41c.07,0,0-.09,0-.18v0C288.19,137.23,288.23,137.32,288.27,137.41Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.262px 137.32px",
                }}
                id="ela3wypn7za44"
                className="animable"
              />
              <path
                d="M288.29,137.21a.17.17,0,0,1,0-.07S288.27,137.17,288.29,137.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.286px 137.175px",
                }}
                id="el4kv1qiff97"
                className="animable"
              />
              <path
                d="M288.12,138.29s-.09,0-.06,0S288.18,138.51,288.12,138.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.097px 138.339px",
                }}
                id="elsygzcdjt4w"
                className="animable"
              />
              <polygon
                points="288.5 137.33 288.41 137.36 288.48 137.41 288.5 137.33"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.455px 137.37px",
                }}
                id="el0r1e3rihrpqo"
                className="animable"
              />
              <path
                d="M288.47,138v-.14l0,0C288.45,137.91,288.44,138,288.47,138Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.461px 137.93px",
                }}
                id="elu84lkjwcsjp"
                className="animable"
              />
              <path
                d="M288.45,137.83c0-.05,0-.09,0-.11A.65.65,0,0,0,288.45,137.83Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.449px 137.775px",
                }}
                id="elcl7hdhm4we"
                className="animable"
              />
              <polygon
                points="288.53 137.51 288.51 137.55 288.51 137.65 288.53 137.51"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.52px 137.58px",
                }}
                id="elx0w6ip56qi"
                className="animable"
              />
              <path
                d="M288.52,139.46l0,.14s.05,0,0-.08S288.55,139.43,288.52,139.46Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.523px 139.522px",
                }}
                id="el9jb8m7r9z7c"
                className="animable"
              />
              <path
                d="M288.74,139s0,0-.06.07h.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.73px 139.035px",
                }}
                id="elm7bk8cdfl"
                className="animable"
              />
              <path
                d="M288.79,139.43s.09.21.08,0C288.84,139.44,288.8,139.29,288.79,139.43Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.83px 139.447px",
                }}
                id="ellwep3cma82k"
                className="animable"
              />
              <polygon
                points="288.76 141.66 288.76 141.63 288.58 141.64 288.76 141.66"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.67px 141.645px",
                }}
                id="elz5330zapwp"
                className="animable"
              />
              <path
                d="M289.28,143.61c0-.18-.11,0-.06-.2s0,.13-.08.09C289.19,143.61,289.17,143.66,289.28,143.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.21px 143.487px",
                }}
                id="elpl6mh26rga"
                className="animable"
              />
              <path
                d="M289.14,143.5s0,0,0-.05Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.14px 143.475px",
                }}
                id="elihcjx5vlpcq"
                className="animable"
              />
              <polygon
                points="289.49 165.72 289.46 165.83 289.51 165.76 289.49 165.72"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.485px 165.775px",
                }}
                id="el16k7gn37m1r"
                className="animable"
              />
              <path
                d="M289.63,165.93l-.11,0,.11.06S289.61,166,289.63,165.93Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.575px 165.96px",
                }}
                id="elj3q42srknxg"
                className="animable"
              />
              <path
                d="M289.77,150.48h0s0,.05,0,.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.77px 150.53px",
                }}
                id="el0xen4zt836zq"
                className="animable"
              />
              <polygon
                points="289.76 152.01 289.76 151.98 289.66 152.04 289.76 152.01"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.71px 152.01px",
                }}
                id="eljma9lsr9f5p"
                className="animable"
              />
              <path
                d="M290,153.29s0-.12,0-.17C290,153.24,289.83,153.31,290,153.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.962px 153.207px",
                }}
                id="el0i2aprmst0ug"
                className="animable"
              />
              <path
                d="M291,165.53s0-.07,0-.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "291px 165.49px",
                }}
                id="elku17yufjura"
                className="animable"
              />
              <polygon
                points="290.94 165.7 290.94 165.67 290.93 165.74 290.94 165.7"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.935px 165.705px",
                }}
                id="el3ida88vu1wt"
                className="animable"
              />
              <path
                d="M290.89,165.7c0,.12,0,0,0,0v-.07S290.89,165.73,290.89,165.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.89px 165.692px",
                }}
                id="el4m5lt4csley"
                className="animable"
              />
              <path
                d="M290.84,165.43s0,0,0,0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.84px 165.43px",
                }}
                id="el8y104fth7bk"
                className="animable"
              />
              <path
                d="M290.85,165.43s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.85px 165.43px",
                }}
                id="elu00r1zd81on"
                className="animable"
              />
              <path
                d="M290.83,165.46v0s0,0,0-.06A.38.38,0,0,0,290.83,165.46Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.829px 165.43px",
                }}
                id="elawo23s968m"
                className="animable"
              />
              <path
                d="M290.82,165.55h0S290.82,165.52,290.82,165.55Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.82px 165.543px",
                }}
                id="elgm5ibnre0y7"
                className="animable"
              />
              <path
                d="M290.83,165.5v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.83px 165.5px",
                }}
                id="el7xcvx2w13n9"
                className="animable"
              />
              <path
                d="M290.84,165.51a.27.27,0,0,1,0,.09S290.85,165.5,290.84,165.51Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.842px 165.555px",
                }}
                id="el90ut28g1vqf"
                className="animable"
              />
              <path
                d="M290.89,165.83v0S290.89,165.79,290.89,165.83Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.89px 165.821px",
                }}
                id="elu0jc6qfcjse"
                className="animable"
              />
              <path
                d="M290.78,165.4s0,.07,0,.1v0A.35.35,0,0,1,290.78,165.4Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.778px 165.45px",
                }}
                id="eljayya3q5idh"
                className="animable"
              />
              <path
                d="M290.79,165.5v.05S290.79,165.53,290.79,165.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.79px 165.525px",
                }}
                id="elb1c87t7ackl"
                className="animable"
              />
              <polygon
                points="290.83 165.69 290.84 165.6 290.83 165.69 290.83 165.69"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.835px 165.645px",
                }}
                id="el80xg4u98bmv"
                className="animable"
              />
              <polygon
                points="290.75 165.4 290.75 165.34 290.75 165.43 290.75 165.4"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.75px 165.385px",
                }}
                id="eldquocuz378n"
                className="animable"
              />
              <path
                d="M290.73,165.41s0-.09,0-.12a.46.46,0,0,1,0,.1S290.72,165.37,290.73,165.41Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.729px 165.35px",
                }}
                id="el9zwpwwbe53g"
                className="animable"
              />
              <path
                d="M290.71,165.44v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.71px 165.44px",
                }}
                id="elzkr8hlgxzr"
                className="animable"
              />
              <path
                d="M290.77,165.54s0-.06,0-.08v.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.77px 165.5px",
                }}
                id="elpt1173qnef"
                className="animable"
              />
              <path
                d="M290.76,165.54Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.76px 165.54px",
                }}
                id="elcvdlquncisi"
                className="animable"
              />
              <path
                d="M290.77,165.54Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.77px 165.54px",
                }}
                id="elbwk56jssqk4"
                className="animable"
              />
              <path
                d="M290.81,165.63a.37.37,0,0,0,0,.15A.75.75,0,0,1,290.81,165.63Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.806px 165.705px",
                }}
                id="el7jzsfduht6p"
                className="animable"
              />
              <path
                d="M290.78,165.63v0s0-.05,0-.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.78px 165.595px",
                }}
                id="elf98o91yzih7"
                className="animable"
              />
              <polygon
                points="290.77 165.66 290.78 165.63 290.77 165.63 290.77 165.66"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.775px 165.645px",
                }}
                id="el24r7gpbubn"
                className="animable"
              />
              <path
                d="M290.73,165.51a.06.06,0,0,1,0,0s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.73px 165.51px",
                }}
                id="elwarljykjn4"
                className="animable"
              />
              <path
                d="M290.75,165.53h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.75px 165.53px",
                }}
                id="elftep1hg242b"
                className="animable"
              />
              <path
                d="M290.67,165.55v0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.67px 165.55px",
                }}
                id="elk8ygrajo7ch"
                className="animable"
              />
              <path
                d="M290.73,165.59s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.73px 165.59px",
                }}
                id="eljgr4sxmrrqn"
                className="animable"
              />
              <path
                d="M290.7,165.48v0S290.7,165.46,290.7,165.48Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.7px 165.476px",
                }}
                id="elxgq5kbna6kp"
                className="animable"
              />
              <path
                d="M290.74,165.52h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.74px 165.52px",
                }}
                id="el3n10fcgzv9c"
                className="animable"
              />
              <path
                d="M290.74,165.52v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.74px 165.52px",
                }}
                id="elbui3yfxp77d"
                className="animable"
              />
              <path
                d="M290.68,166c0-.07,0-.14,0-.06a3.74,3.74,0,0,0,0-.41.17.17,0,0,1,0,.1.61.61,0,0,1,0,.15s0,0,0-.06a2,2,0,0,1,0,.25v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.684px 165.765px",
                }}
                id="elstwd86ied5"
                className="animable"
              />
              <path
                d="M290.69,165.57h0a.51.51,0,0,0,0-.1.31.31,0,0,0,0,.09h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.69px 165.52px",
                }}
                id="el20ec57tidq8"
                className="animable"
              />
              <path
                d="M290.71,165.56a1.21,1.21,0,0,0,0,.19s0-.06,0-.09,0,0,0-.09v0a0,0,0,0,1,0,0h0v0a0,0,0,0,1,0,0v0h0l0,0S290.7,165.57,290.71,165.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.708px 165.655px",
                }}
                id="elqzjs8lom2h"
                className="animable"
              />
              <path
                d="M290.81,165.94v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.81px 165.94px",
                }}
                id="elqz8rwm1gk4"
                className="animable"
              />
              <path
                d="M290.8,165.89v0a.13.13,0,0,1,0,.06.34.34,0,0,0,0-.09h0s0-.12,0-.13S290.79,165.83,290.8,165.89Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.8px 165.84px",
                }}
                id="el6cmk8448fpv"
                className="animable"
              />
              <path
                d="M290.75,165.75v.06S290.76,165.78,290.75,165.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.752px 165.78px",
                }}
                id="elnqodr1wehkr"
                className="animable"
              />
              <polygon
                points="290.67 165.52 290.67 165.44 290.67 165.55 290.67 165.52"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.67px 165.495px",
                }}
                id="elyuyz478fyr"
                className="animable"
              />
              <polygon
                points="290.65 165.53 290.65 165.46 290.65 165.5 290.65 165.53"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.65px 165.495px",
                }}
                id="eltelgw3b4hm"
                className="animable"
              />
              <path
                d="M289.72,166.44v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.72px 166.44px",
                }}
                id="elx48vi1hkbxe"
                className="animable"
              />
              <path
                d="M289.75,166.44v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.75px 166.44px",
                }}
                id="el1ikuf12lnzu"
                className="animable"
              />
              <path
                d="M290.23,165.56c0-.07,0-.17,0-.09v.08S290.22,165.54,290.23,165.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.228px 165.5px",
                }}
                id="el9vzbh7qjsbr"
                className="animable"
              />
              <path
                d="M290,166v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290px 166px",
                }}
                id="elt0high67gk"
                className="animable"
              />
              <path
                d="M290,166.34v-.05S290,166.32,290,166.34Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290px 166.315px",
                }}
                id="elb9ek5rczc78"
                className="animable"
              />
              <path
                d="M289.57,166.52v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.57px 166.52px",
                }}
                id="elufukossc1u"
                className="animable"
              />
              <path
                d="M290.48,165.56s0,0,0,0S290.48,165.63,290.48,165.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.48px 165.576px",
                }}
                id="eluv0h9iysah"
                className="animable"
              />
              <path
                d="M289.69,165.63v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.69px 165.63px",
                }}
                id="elwsvdanwp9v"
                className="animable"
              />
              <path
                d="M290.45,165.52v0s0,0,0,0S290.45,165.47,290.45,165.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.45px 165.509px",
                }}
                id="eleu83ka82mp5"
                className="animable"
              />
              <path
                d="M289,166.49h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 166.49px",
                }}
                id="el1q2gfpy9q3l"
                className="animable"
              />
              <path
                d="M288.8,165.73h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.8px 165.73px",
                }}
                id="elsthdnem4t2"
                className="animable"
              />
              <path
                d="M290.58,165.5s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.58px 165.5px",
                }}
                id="el7yvpja5765"
                className="animable"
              />
              <path
                d="M290.61,165.8a0,0,0,0,0,0,0A0,0,0,0,1,290.61,165.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.61px 165.8px",
                }}
                id="elv9qp5t6wa1"
                className="animable"
              />
              <path
                d="M290.44,166h0S290.44,166,290.44,166Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.44px 166px",
                }}
                id="elfkg0bodu3kt"
                className="animable"
              />
              <path
                d="M290.59,165.53v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.59px 165.53px",
                }}
                id="elswvl6euoadh"
                className="animable"
              />
              <path
                d="M290.59,165.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.59px 165.5px",
                }}
                id="elm4cyidv9cpn"
                className="animable"
              />
              <path
                d="M290.58,165.5h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.58px 165.5px",
                }}
                id="eln50i146ltx"
                className="animable"
              />
              <path
                d="M289.29,166.27h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.29px 166.27px",
                }}
                id="eltkkoaw296za"
                className="animable"
              />
              <polygon
                points="290.17 166.35 290.17 166.35 290.17 166.34 290.17 166.35"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.17px 166.345px",
                }}
                id="elry5u9uuwfif"
                className="animable"
              />
              <path
                d="M290.16,166.41c0-.1,0-.09,0-.07v.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.16px 166.368px",
                }}
                id="eltjt3ckax9e"
                className="animable"
              />
              <path
                d="M289.65,166.46v.07a1,1,0,0,0,0,.17c0-.24,0,0,0-.26v.05h0c0,.12,0-.17,0-.13s0,0,0,.08v0l0,0h0v-.12s0,0,0,.05v-.06c0,.06,0,.12,0,.12,0,.23,0-.07,0,.07v0a.89.89,0,0,0,0,.17,2.13,2.13,0,0,1,0-.27s0,0,0,0,0,.06,0,.06v.11a.65.65,0,0,0,0-.14.89.89,0,0,1,0,.17s0-.14,0-.16,0,0,0-.07a.38.38,0,0,1,0,.1v-.09s0,0,0,0a.66.66,0,0,1,0-.14c0-.12,0,.18,0,0,0,0,0,.07,0,.11s0-.07,0-.19v.07a1.22,1.22,0,0,1,0-.27.34.34,0,0,1,0-.09v0a.81.81,0,0,1,0,.17v-.07a1.48,1.48,0,0,0,0,.21v.05l0,.07a2.7,2.7,0,0,1,0-.36c0,.09,0,.15,0,.26,0-.12,0,.05,0,0a.27.27,0,0,1,0,.09l0-.11c0,.09,0,.07,0,.1,0-.17,0-.12,0-.17s0,.12,0,.11,0-.21,0-.1v0c0-.05,0,0,0,0h0s0,0,0-.15c0,0,0,.18,0,.21v-.5a4.79,4.79,0,0,1,0,.51s0,0,0,0a.67.67,0,0,1,0-.2c0,.15,0,.08,0,.2a1.59,1.59,0,0,1,0-.22s0,.2,0,.22v-.2a.83.83,0,0,0,0,.22s0,0,0,0v0s0-.28,0-.27a2.46,2.46,0,0,0,0,.28c0-.1,0-.49,0-.57v0c0,.16,0,.48,0,.6s0,.07,0,.06a.23.23,0,0,1,0,.09l0,0c0-.12,0-.12,0-.22l0,.13a1.26,1.26,0,0,1,0-.32,2.18,2.18,0,0,1,0,.25s0-.08,0,0v.07s0-.16,0-.05,0,.15,0,.08,0-.55,0-.65l0,.2c0,.1,0,.25,0,.34a.27.27,0,0,1,0-.09s0,.1,0,.09,0,0,0-.1,0,0,0,0a1.39,1.39,0,0,1,0-.29.17.17,0,0,0,0,.08s0,.07,0,.07,0,.06,0,.11,0-.06,0-.06,0,0,0,.14a.46.46,0,0,1,0-.14c0-.11,0,0,0,0a3.66,3.66,0,0,1,0-.37s0,.1,0,.07,0-.25,0-.15,0,.19,0,.26,0-.06,0-.06v.05s0,0,0,0,0,0,.06.1a.41.41,0,0,0,0-.11v0a.61.61,0,0,1,0-.14c0-.06,0-.14,0-.1v.11s0-.07,0,0,0,.14,0,0v-.07c0-.06,0-.17,0-.14s0,0,0-.11c0,.15,0,0,0,.16a1,1,0,0,0,0-.19s0,.07,0,.05v0h0a.43.43,0,0,1,0,.05c0,.08,0,.08,0,.12s0,0,0-.07v.06a1.28,1.28,0,0,1,0-.18h0s0-.06,0,0,0,.09,0,0v0s0,.13-.06,0c0,0,0,0,0,0v-.07s0-.06,0,0c0-.19,0,0,0-.07v.09s0,0,0,0v.08c0,.07,0-.05,0,0s0,0,0,0c0-.17,0,.13-.05,0,0,0,0,0,0,0h0v0a.08.08,0,0,1,0,0v-.08h0s0,0,0,.1v0c0-.13,0,.05,0-.1,0,.08,0,.06,0,.21v0s0,0,0,0v0a1,1,0,0,1,0-.18c0,.13,0,.05,0,.17v-.07s0,.1,0,0-.06,0-.09-.13c0,.2,0-.09,0,.06v-.11c0,.05,0-.08,0,.06a.32.32,0,0,1,0-.1s0,0,0,0v-.06a1.35,1.35,0,0,0,0,.3c0-.07,0-.06,0,0a.4.4,0,0,1,0-.11s0-.06,0,.05,0,0,0,0h0v0c0,.13,0,0,0,0h0c0-.1,0,0,0-.06v.08c0,.18,0-.11,0,0v.05c0,.06,0-.24,0-.27a0,0,0,0,1,0,0c0-.11,0,.11,0,.13a1.32,1.32,0,0,0,0-.16c0-.05,0,.2,0,.08,0,0,0,.09,0,.13s0-.17,0,0v0a.37.37,0,0,0,0,.14c0-.29,0,.14-.05-.13l0,0v0s0,0,0,.11a.11.11,0,0,1,0,0c0-.06,0-.05,0,.09v-.09s0-.13,0,0v0c0,.1-.06-.24-.06.08v.08s0,.13,0,.19,0-.1,0-.11h0c0-.16,0-.06,0-.17s-.07-.08-.11-.08v.13h0s0-.1,0-.1,0,.07,0,.09,0-.15,0-.15h0v0c0,.05,0,.1,0,.1a.22.22,0,0,0,0-.09h0l0,.1c0,.05,0,0,0,.11,0-.18-.06-.06-.07-.11s0,.07,0,.06,0,.14,0,.12c0-.23,0-.1,0-.27,0,.11,0,0,0,.19,0,0,0-.09,0-.14s0-.06-.07,0a.49.49,0,0,1,0,.13l0-.16s0,.18,0,.07v.08c0-.15-.09.13-.16-.15v.18l0-.11c0,.11,0,0,0,.12v0c0,.13,0,.29,0,.46,0-.15,0,.21,0,0v.09c0-.07,0-.08,0,0v0s0,.05.07.09v-.08s0,0,0,.06,0,0,0-.15c0,.17,0,0,.05.19,0,0,0-.09,0-.09v0a1,1,0,0,1,0,.12s0,0,0,0,0,0,0,0v0c0-.05.05,0,.07-.09v0c0-.08,0,0,0,0h0s0-.06,0-.05,0,.09,0,.14a.6.6,0,0,0,0-.21v.07a.28.28,0,0,1,0-.11s0,.1,0,.17,0-.14.05.06c0-.18,0-.1,0-.13s0-.14,0-.12,0,.23,0,.22v0s0-.07,0,0a.64.64,0,0,0,0-.14v0s0-.2,0-.12,0,.06,0,.08,0,0,0,0,0,0,0,.13,0-.1,0,.05c0-.19,0,0,0-.12v.14c0-.09,0,.08,0,0s0-.1,0-.16,0,.21.05.17,0-.05,0-.08,0,0,0,.09,0-.09,0-.14,0,0,0-.07v.08c0,.05,0-.18,0-.11v.18c0-.07,0,0,0,0v-.15s0,.08,0,.07,0,.12,0,0v.08c0,.06,0,0,0,0v0l0-.12v.08s0,0,0-.05v.09s0,.09,0,.11,0-.13,0-.08v0s0,.1,0,0v.12s0-.16,0-.2,0,.1,0,.14,0-.19,0-.09S289.65,166.45,289.65,166.46Zm.14-.06s0,.07,0,0S289.78,166.37,289.79,166.4Zm.2-.21s0,0,0,0Zm.56-.62h0S290.54,165.55,290.55,165.57Zm0,0v0s0,0,0,0S290.56,165.62,290.56,165.6Zm-1.34.1v0S289.22,165.73,289.22,165.7Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.775px 165.94px",
                }}
                id="el1c8ibilegrc"
                className="animable"
              />
              <path
                d="M290,166.3v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290px 166.3px",
                }}
                id="eliyt5csb5mnc"
                className="animable"
              />
              <path
                d="M289.57,166.51Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.57px 166.51px",
                }}
                id="el3ak47wk7s7p"
                className="animable"
              />
              <path
                d="M290.4,166.33v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.4px 166.33px",
                }}
                id="el9jjyjpoy3ph"
                className="animable"
              />
              <path
                d="M290.62,165.46v0a0,0,0,0,1,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.62px 165.46px",
                }}
                id="elj0np5e21d5o"
                className="animable"
              />
              <path
                d="M290.71,166h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.71px 166px",
                }}
                id="elrdcuq94enw"
                className="animable"
              />
              <path
                d="M290.64,165.74c0,.07,0,0,0,0h0C290.65,165.67,290.64,165.71,290.64,165.74Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.642px 165.736px",
                }}
                id="elyjgebxwkj09"
                className="animable"
              />
              <path
                d="M290.65,165.77h0S290.66,165.76,290.65,165.77Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.652px 165.768px",
                }}
                id="elgtdvyk9t8y9"
                className="animable"
              />
              <path
                d="M290.57,165.56s0-.09,0-.07S290.56,165.61,290.57,165.56Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.568px 165.53px",
                }}
                id="elixzqhpaszr"
                className="animable"
              />
              <polygon
                points="290.66 165.97 290.65 165.88 290.65 165.96 290.66 165.97"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.655px 165.925px",
                }}
                id="elqda8urixvv"
                className="animable"
              />
              <path
                d="M290.61,165.91v0S290.61,165.88,290.61,165.91Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.61px 165.903px",
                }}
                id="elazywhmp1jm6"
                className="animable"
              />
              <path
                d="M290.62,165.9h0S290.62,165.89,290.62,165.9Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.62px 165.898px",
                }}
                id="elabuqao8t65e"
                className="animable"
              />
              <polygon
                points="290.64 166 290.64 165.98 290.63 165.97 290.64 166"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.635px 165.985px",
                }}
                id="eltq8sde1g60m"
                className="animable"
              />
              <path
                d="M290.51,165.9v0s0,.05,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.51px 165.911px",
                }}
                id="elbhw05hppugj"
                className="animable"
              />
              <path
                d="M290.55,166.14v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.55px 166.14px",
                }}
                id="el5k6ycusosgs"
                className="animable"
              />
              <path
                d="M290.53,166.16s0,.09,0,.08A.22.22,0,0,0,290.53,166.16Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.532px 166.2px",
                }}
                id="el41xkzwraz01"
                className="animable"
              />
              <polygon
                points="290.37 166.03 290.38 166.03 290.37 165.85 290.37 166.03"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.375px 165.94px",
                }}
                id="eldm2tmsht2a"
                className="animable"
              />
              <path
                d="M290.26,166.47s0-.11,0-.06,0,0,0-.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.26px 166.4px",
                }}
                id="elrgm61c8nfi"
                className="animable"
              />
              <path
                d="M290.26,166.33v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "290.26px 166.33px",
                }}
                id="elmsz71przjqc"
                className="animable"
              />
              <polygon
                points="288.77 165.64 288.76 165.6 288.76 165.66 288.77 165.64"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.765px 165.63px",
                }}
                id="elgdw72yu6tgf"
                className="animable"
              />
              <path
                d="M288.76,165.77v0S288.76,165.74,288.76,165.77Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.76px 165.763px",
                }}
                id="el9mjs2qcmr1"
                className="animable"
              />
              <path
                d="M289.82,166.63v0s0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.82px 166.63px",
                }}
                id="els2aodepyjha"
                className="animable"
              />
              <polygon
                points="289.71 166.55 289.72 166.55 289.71 166.45 289.71 166.55"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.715px 166.5px",
                }}
                id="el9r1cod4yqv"
                className="animable"
              />
              <path
                d="M289.64,166.68s0,0,0,0S289.63,166.56,289.64,166.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.638px 166.653px",
                }}
                id="elkvmtwi144a"
                className="animable"
              />
              <path
                d="M289.28,131.51v-.08A.19.19,0,0,0,289.28,131.51Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.278px 131.47px",
                }}
                id="eluqyzwgi4h"
                className="animable"
              />
              <polygon
                points="289.23 131.68 289.23 131.65 289.23 131.73 289.23 131.68"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.23px 131.69px",
                }}
                id="el9pu3rw4kg2b"
                className="animable"
              />
              <path
                d="M289.18,131.68c0,.13,0,0,0,0v-.08S289.19,131.72,289.18,131.68Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.182px 131.669px",
                }}
                id="eljvlv8l2tc5h"
                className="animable"
              />
              <path
                d="M289.14,131.42s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.14px 131.42px",
                }}
                id="ely28igxkv6m9"
                className="animable"
              />
              <path
                d="M289.15,131.42Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.15px 131.42px",
                }}
                id="elltx5zchruur"
                className="animable"
              />
              <path
                d="M289.12,131.45v0a.11.11,0,0,1,0-.05S289.12,131.4,289.12,131.45Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.119px 131.425px",
                }}
                id="els1v32yv2qc"
                className="animable"
              />
              <path
                d="M289.12,131.54v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.12px 131.54px",
                }}
                id="el4dpue5tanxv"
                className="animable"
              />
              <path
                d="M289.13,131.48v0s0,.06,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.13px 131.493px",
                }}
                id="el1yzxy4fquvv"
                className="animable"
              />
              <path
                d="M289.13,131.5a.13.13,0,0,1,0,.09S289.15,131.48,289.13,131.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.134px 131.544px",
                }}
                id="el2jkb8sxrco4"
                className="animable"
              />
              <path
                d="M289.19,131.81v0S289.18,131.77,289.19,131.81Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.188px 131.801px",
                }}
                id="elkxntcsjfcs"
                className="animable"
              />
              <path
                d="M289.08,131.38v.08A.17.17,0,0,1,289.08,131.38Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.078px 131.42px",
                }}
                id="elpnlqan8lgw"
                className="animable"
              />
              <path
                d="M289.08,131.49v0S289.09,131.52,289.08,131.49Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.082px 131.497px",
                }}
                id="el2xkgr6h5vjb"
                className="animable"
              />
              <polygon
                points="289.13 131.68 289.13 131.58 289.12 131.68 289.13 131.68"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.125px 131.63px",
                }}
                id="elmh6j924l4o"
                className="animable"
              />
              <polygon
                points="289.05 131.38 289.05 131.32 289.04 131.41 289.05 131.38"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.045px 131.365px",
                }}
                id="elr2p7t10ynh"
                className="animable"
              />
              <path
                d="M289,131.39s0-.08,0-.11a.2.2,0,0,0,0,.09S289,131.35,289,131.39Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.997px 131.335px",
                }}
                id="elbydmpdddffa"
                className="animable"
              />
              <path
                d="M289,131.43v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.43px",
                }}
                id="elh46o2vpzfn"
                className="animable"
              />
              <path
                d="M289.06,131.52s0-.06,0-.07v.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.06px 131.485px",
                }}
                id="elki7jeqw4fef"
                className="animable"
              />
              <path
                d="M289.06,131.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.06px 131.52px",
                }}
                id="eljzm8d5gicia"
                className="animable"
              />
              <path
                d="M289.07,131.52h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.07px 131.52px",
                }}
                id="elcs66p7gkmd9"
                className="animable"
              />
              <path
                d="M289.1,131.61a.4.4,0,0,0,0,.15S289.1,131.67,289.1,131.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.096px 131.685px",
                }}
                id="elaadipmiw8ym"
                className="animable"
              />
              <path
                d="M289.07,131.62h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.07px 131.62px",
                }}
                id="eloe59cypgzgn"
                className="animable"
              />
              <polygon
                points="289.07 131.64 289.07 131.62 289.07 131.61 289.07 131.64"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.07px 131.625px",
                }}
                id="elidvgcykpxgs"
                className="animable"
              />
              <path
                d="M289,131.5s0,0,0,0l0,0S289,131.49,289,131.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.498px",
                }}
                id="elh5a66023hnd"
                className="animable"
              />
              <path
                d="M289,131.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.52px",
                }}
                id="el3t041bgteg3"
                className="animable"
              />
              <path
                d="M289,131.54h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.54px",
                }}
                id="el0no0oap9svb"
                className="animable"
              />
              <path
                d="M289,131.57s0,0,0,0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.57px",
                }}
                id="elb4cq2k3grgk"
                className="animable"
              />
              <path
                d="M289,131.46v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.46px",
                }}
                id="el44x0y084q9e"
                className="animable"
              />
              <path
                d="M289,131.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.5px",
                }}
                id="elzk63sohp1bd"
                className="animable"
              />
              <path
                d="M289,131.5v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.5px",
                }}
                id="elmilaps0cv4"
                className="animable"
              />
              <path
                d="M289,132c0-.07,0-.13,0-.06s0-.28,0-.41v.1s0,.09,0,.15,0,0,0-.06,0,.13,0,.26v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.765px",
                }}
                id="el5uwmz9zmpmf"
                className="animable"
              />
              <path
                d="M289,131.56h0s0,0,0-.09v.08h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.515px",
                }}
                id="el7zvwjswbpjx"
                className="animable"
              />
              <path
                d="M289,131.55a1.21,1.21,0,0,0,0,.19s0-.06,0-.1,0,0,0-.08v0a.08.08,0,0,0,0,0h0s0,0,0,0h0v0h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.998px 131.645px",
                }}
                id="eli88tyrf2drr"
                className="animable"
              />
              <path
                d="M289.1,131.92h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.1px 131.92px",
                }}
                id="elp3oyd2weszc"
                className="animable"
              />
              <path
                d="M289.1,131.88v0a.27.27,0,0,0,0-.09h0a.83.83,0,0,1,0-.13A1.24,1.24,0,0,0,289.1,131.88Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.099px 131.77px",
                }}
                id="elp0gtjgyznpf"
                className="animable"
              />
              <path
                d="M289.05,131.73v.07S289.05,131.76,289.05,131.73Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289.05px 131.765px",
                }}
                id="el05i0fwk6itqj"
                className="animable"
              />
              <polygon
                points="288.97 131.5 288.96 131.42 288.96 131.53 288.97 131.5"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.965px 131.475px",
                }}
                id="eledzxjg3axnb"
                className="animable"
              />
              <polygon
                points="288.95 131.51 288.95 131.44 288.94 131.49 288.95 131.51"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.945px 131.475px",
                }}
                id="el8gmqrdctqng"
                className="animable"
              />
              <path
                d="M288,132.42v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 132.42px",
                }}
                id="elec6vdp4n0hj"
                className="animable"
              />
              <path
                d="M288,132.42v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 132.42px",
                }}
                id="elk8hx16z7bn"
                className="animable"
              />
              <path
                d="M288.52,131.54c0-.06,0-.16,0-.08v.07S288.52,131.53,288.52,131.54Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.52px 131.485px",
                }}
                id="el3ya6djxlfso"
                className="animable"
              />
              <path
                d="M288.25,132v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.25px 132px",
                }}
                id="elhkeyzufi4rn"
                className="animable"
              />
              <path
                d="M288.27,132.32v0A.06.06,0,0,0,288.27,132.32Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.27px 132.32px",
                }}
                id="elwhtxvrk02c"
                className="animable"
              />
              <path
                d="M287.86,132.51v.08S287.86,132.55,287.86,132.51Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.86px 132.55px",
                }}
                id="el26dkojvzrjy"
                className="animable"
              />
              <path
                d="M288.77,131.55a.2.2,0,0,0,0-.05C288.77,131.55,288.77,131.61,288.77,131.55Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.771px 131.538px",
                }}
                id="eln7qhh8zj1u"
                className="animable"
              />
              <path
                d="M288,131.61v0A0,0,0,0,0,288,131.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288px 131.61px",
                }}
                id="elfs36d08rxzj"
                className="animable"
              />
              <path
                d="M288.74,131.51h0s0,0,0,0S288.75,131.45,288.74,131.51Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.742px 131.497px",
                }}
                id="elvb5j01rdfjr"
                className="animable"
              />
              <path
                d="M287.3,132.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.3px 132.47px",
                }}
                id="elm25kh764ld"
                className="animable"
              />
              <path
                d="M287.1,131.72h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.1px 131.72px",
                }}
                id="ell8n1gnrdfr"
                className="animable"
              />
              <path
                d="M288.88,131.48Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.88px 131.48px",
                }}
                id="eltsbrf38vsbf"
                className="animable"
              />
              <path
                d="M288.91,131.78v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.91px 131.78px",
                }}
                id="elbhg0cze0ur"
                className="animable"
              />
              <path
                d="M288.73,132Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.73px 132px",
                }}
                id="elrpn8qqlmn5l"
                className="animable"
              />
              <path
                d="M288.89,131.52v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.89px 131.52px",
                }}
                id="el9rw4j8s47y"
                className="animable"
              />
              <path
                d="M288.88,131.48h0S288.89,131.47,288.88,131.48Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.882px 131.478px",
                }}
                id="eld3mhcyrqcps"
                className="animable"
              />
              <path
                d="M288.87,131.49h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.87px 131.49px",
                }}
                id="eldw5rlds37yd"
                className="animable"
              />
              <path
                d="M287.59,132.26h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.59px 132.26px",
                }}
                id="el4v62vel6tsn"
                className="animable"
              />
              <polygon
                points="288.47 132.34 288.47 132.33 288.47 132.33 288.47 132.34"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.47px 132.335px",
                }}
                id="ele11fgigbrxl"
                className="animable"
              />
              <path
                d="M288.46,132.39c0-.09,0-.08,0-.06l0-.22Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.46px 132.25px",
                }}
                id="elxorfv5t8ai"
                className="animable"
              />
              <path
                d="M288,132.45v.06c0,.12,0,.08,0,.17,0-.23,0,0,0-.26v.05h0c0,.11,0-.17,0-.14a.11.11,0,0,1,0,.08v-.06s0,0,0,0l0,0h0s0-.1,0-.12v0c0,.06,0,.12,0,.11,0,.24,0-.06,0,.08v0a1.08,1.08,0,0,0,0,.17,2.23,2.23,0,0,1,0-.27s0,0,0,0,0,.07,0,.06v0a.54.54,0,0,1,0,.17s0-.13,0-.16,0,0,0-.07,0,.07,0,.1v0s0-.12,0-.15,0,.18,0,0v.11a.84.84,0,0,0,0-.19l0,.08a1.24,1.24,0,0,1,0-.27.2.2,0,0,0,0-.09v-.06a.22.22,0,0,1,0,.1v0a.86.86,0,0,1,0,.17v.14a0,0,0,0,1,0,0v.08l0,.08a2.89,2.89,0,0,1,0-.37c0,.1,0,.15,0,.26,0-.11,0,.05,0,0a.19.19,0,0,1,0,.08l0-.11a.25.25,0,0,0,0,.11c0-.18,0-.12,0-.17s0,.11,0,.1,0-.21,0-.09v0s0,0,0,0h0s.05,0,.05-.15v-.29c0,.12,0,.43,0,.5s0,0,0,0v-.19c0,.14,0,.07,0,.19a1.61,1.61,0,0,1,0-.22s0,.2,0,.22v-.2a2.15,2.15,0,0,0,0,.23s0,0,0,0v-.05s0-.27,0-.26a1.17,1.17,0,0,0,0,.27c0-.1,0-.48,0-.56v0c0,.15,0,.47,0,.6s0,.06,0,.06,0,.08,0,.08l0,0c0-.12,0-.12,0-.22v.12c0-.11,0-.33,0-.31a2.09,2.09,0,0,1,0,.25s0-.08,0-.05v.06s0-.16,0,0,0,.15,0,.08,0-.55,0-.65a1,1,0,0,1,0,.19c0,.11,0,.25,0,.34v-.08a.37.37,0,0,1,0,.08c0-.12,0,0,0-.1s0,0,0,0v-.3a.31.31,0,0,1,0,.09s0,.06,0,.06,0,.07,0,.12,0-.06,0-.07,0,0,0,.14c0-.07,0-.09,0-.14s0,0,0,0a2,2,0,0,1,0-.37s0,.09,0,.07,0-.26,0-.15a2.26,2.26,0,0,1,0,.25s0-.06,0,0l0,0s0,.06,0,0,.05,0,.07.09a.33.33,0,0,0,0-.1h0a.64.64,0,0,0,0-.14c0-.05,0-.14,0-.1v.11s0-.06,0,0,0,.15,0,0v-.07c0-.05,0-.17,0-.13v-.12c0,.16,0,0,0,.16a.84.84,0,0,0,0-.18s0,.07,0,.05v0h0v.06c0,.08,0,.08,0,.12s0,0,0-.08v.06a.53.53,0,0,1,0-.17h0s0-.06,0,0,0,.08,0,0l0,.05v0s0,.13-.05,0c0,0,0,0,0,0v-.07s0-.07,0,0c0-.18,0,0-.05-.07v.09s0,0,0,.06v.08c0,.06,0-.06,0,0s0,0,0,0c0-.18,0,.13-.06,0,0,0,0,0,0,0h0v0a.1.1,0,0,1,0,0s0,0,0,0v-.09h0s0,0,0,.11a.43.43,0,0,0,0,.05c0-.14,0,0,0-.1a.87.87,0,0,0,0,.2s0,0,0,0,0,0,0,0v0a.57.57,0,0,1,0-.19c0,.13,0,0,0,.18v-.07s0,.1,0,0,0,0-.09-.14c0,.21,0-.09,0,.07,0,0,0-.08,0-.12s0-.07,0,.06v-.1h0s0,0,0-.06a1.11,1.11,0,0,0,0,.3c0-.07,0-.06,0,0,0-.05,0-.1,0-.11s0-.07,0,0,0,0,0,0,0,0,0,0v0c0,.14,0,0,0,.05v0c0-.09,0,.05,0-.06v.08c0,.19,0-.1,0,0v0c0,.06,0-.25,0-.27v0c0-.1,0,.11,0,.14a.62.62,0,0,0,0-.16c0-.05,0,.19,0,.08a.48.48,0,0,1,0,.12s0-.17,0,0v0a1.13,1.13,0,0,0,0,.14c0-.29,0,.15,0-.12h0v0s0,0,0,.11v-.06c0-.05,0,0,0,.09v-.08c0,.05,0-.14-.05,0v0c0,.1-.07-.24-.06.07v.08s0,.14,0,.19,0-.09,0-.11v0c0-.17,0-.06,0-.17s-.08-.08-.11-.09v.13h0s0-.1,0-.09,0,.06,0,.09a.36.36,0,0,1,0-.15h0c0,.05,0,.11,0,.1a.28.28,0,0,1,0-.08h0v.1a.31.31,0,0,0,0,.11c0-.18-.06-.07-.07-.12s0,.07,0,.07,0,.13,0,.12c0-.24,0-.1,0-.27,0,.11,0,0,0,.18a1,1,0,0,0,0-.14c0,.09-.05,0-.07,0a.47.47,0,0,1,0,.12l0-.15s0,.18,0,.07a.2.2,0,0,1,0,.07c-.06-.14-.1.13-.16-.15,0,.06,0,.07,0,.07a.38.38,0,0,1,0,.12l0-.12c0,.12,0,0,0,.12v0c0,.13,0,.29,0,.46,0-.15,0,.22,0,0,0,0,0,.05,0,.09s0-.09,0-.06v0s0,0,.07.1v-.08a.43.43,0,0,1,0,.05c0-.12,0,0,0-.14,0,.17,0,0,.06.19,0,0,0-.1,0-.1v0s0,.07,0,.12h0c0,.07,0,0,0,0v0c0-.05.06,0,.08-.09v0c0-.07,0,0,0,0h0s0-.06,0-.06a.66.66,0,0,0,0,.14c0-.08,0-.06,0-.2v.06a.28.28,0,0,1,0-.11c0,.06,0,.1,0,.18s0-.15,0,.06c0-.18,0-.1,0-.14s0-.13,0-.12,0,.23,0,.23h0c0,.05,0-.06,0,0,0-.11,0,0,0-.13v0s0-.2,0-.12v.09s0,0,0,0,0,0,0,.14,0-.1,0,.05c0-.19,0,0,0-.13v.09s0-.06,0,0v.1c0-.09,0,.08,0,0s0-.1,0-.16,0,.22.05.18,0-.05,0-.09a.2.2,0,0,1,0,.09s0-.08,0-.13c0,.1,0,0,0-.08v.09s0-.18,0-.12v.19c0-.08,0-.06,0,0a.41.41,0,0,1,0-.16c0,.05,0,.08,0,.07s0,.12,0,0v.08c0,.06,0,0,0,0h0v-.12a.22.22,0,0,1,0,.08v-.06a.54.54,0,0,1,0,.18l0-.08s0,.08,0,.11,0-.14,0-.09a0,0,0,0,1,0,0s0,.1,0,0l0,.12s0-.15,0-.2,0,.11,0,.14,0-.19,0-.09Zm.13-.07s0,.08,0,0S288.08,132.36,288.08,132.38Zm.2-.21s0,0,0,0S288.28,132.15,288.28,132.17Zm.56-.62h0S288.84,131.54,288.84,131.55Zm0,0a.08.08,0,0,0,0,0v0S288.85,131.61,288.86,131.59Zm-1.34.1v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.12px 132.004px",
                }}
                id="el1oz388dlyda"
                className="animable"
              />
              <path
                d="M288.27,132.29v0A.09.09,0,0,0,288.27,132.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.27px 132.29px",
                }}
                id="eloq4eryzakk"
                className="animable"
              />
              <path
                d="M287.86,132.49h0v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.86px 132.49px",
                }}
                id="elyclgu3zfm5q"
                className="animable"
              />
              <path
                d="M288.7,132.32s0,0,0,0A.07.07,0,0,0,288.7,132.32Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.7px 132.32px",
                }}
                id="el4vd3mnb9qda"
                className="animable"
              />
              <path
                d="M288.91,131.44v.05A.13.13,0,0,1,288.91,131.44Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.909px 131.465px",
                }}
                id="elxnf3esptu4"
                className="animable"
              />
              <path
                d="M289,132h0v0S289,132,289,132Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 132px",
                }}
                id="elyp73fl9pdo"
                className="animable"
              />
              <path
                d="M288.94,131.72c0,.07,0,.05,0,0h0C288.94,131.66,288.94,131.69,288.94,131.72Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.94px 131.725px",
                }}
                id="el3xompem9vdr"
                className="animable"
              />
              <path
                d="M289,131.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "289px 131.75px",
                }}
                id="eles15e0psx47"
                className="animable"
              />
              <path
                d="M288.87,131.54v-.06C288.87,131.39,288.86,131.59,288.87,131.54Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.868px 131.502px",
                }}
                id="elg6a9cduaj6j"
                className="animable"
              />
              <polygon
                points="288.95 131.96 288.95 131.87 288.95 131.94 288.95 131.96"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.95px 131.915px",
                }}
                id="elwqpdqi90dc"
                className="animable"
              />
              <path
                d="M288.9,131.89v0s0,0,0,0S288.9,131.87,288.9,131.89Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.9px 131.886px",
                }}
                id="elzuw8tg1lp9o"
                className="animable"
              />
              <path
                d="M288.92,131.89v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.92px 131.89px",
                }}
                id="el4zwe7frvb3q"
                className="animable"
              />
              <polygon
                points="288.94 131.98 288.94 131.97 288.93 131.96 288.94 131.98"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.935px 131.97px",
                }}
                id="elfp7eptbmmbh"
                className="animable"
              />
              <path
                d="M288.81,131.88v0s0,.05,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.81px 131.891px",
                }}
                id="elg5eji4ylm2a"
                className="animable"
              />
              <path
                d="M288.85,132.12s0,0,0-.07v.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.85px 132.085px",
                }}
                id="elk3thjinpaen"
                className="animable"
              />
              <path
                d="M288.82,132.15s0,.09,0,.08S288.83,132.17,288.82,132.15Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.822px 132.19px",
                }}
                id="eli79gppp7ef"
                className="animable"
              />
              <polygon
                points="288.67 132.02 288.67 132.02 288.66 131.84 288.67 132.02"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.665px 131.93px",
                }}
                id="el6zrfnmn63"
                className="animable"
              />
              <path
                d="M288.56,132.45s0-.11,0-.06,0,0,0-.08A.56.56,0,0,0,288.56,132.45Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.558px 132.38px",
                }}
                id="elhqs5orarvd8"
                className="animable"
              />
              <path
                d="M288.56,132.31v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.56px 132.31px",
                }}
                id="elm5w79otas1"
                className="animable"
              />
              <polygon
                points="287.06 131.62 287.05 131.59 287.06 131.65 287.06 131.62"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.055px 131.62px",
                }}
                id="elcweodqowgio"
                className="animable"
              />
              <path
                d="M287.06,131.75v0S287.05,131.73,287.06,131.75Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.058px 131.746px",
                }}
                id="elff0g0eo8mvn"
                className="animable"
              />
              <path
                d="M288.12,132.61v0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.12px 132.61px",
                }}
                id="el2frgps7h5k4"
                className="animable"
              />
              <polygon
                points="288.01 132.53 288.01 132.53 288 132.43 288.01 132.53"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "288.005px 132.48px",
                }}
                id="el244hkb7b45w"
                className="animable"
              />
              <path
                d="M287.93,132.67s0,0,0,0S287.92,132.54,287.93,132.67Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.928px 132.641px",
                }}
                id="elbecrot1x9bs"
                className="animable"
              />
              <path
                d="M273.34,179.65l-.44.2a2.41,2.41,0,0,0-.36.23v.13l-.1-.09s-.06,0-.11,0a.17.17,0,0,1-.14.14l-.13,0c-.09,0-.16,0-.2-.14s-.09-.18-.16-.17h-.08l0-.09c0-.1,0-.16-.07-.18h-.1a.22.22,0,0,1-.11,0,.31.31,0,0,1-.12-.15v-.16a.62.62,0,0,0-.34-.56.06.06,0,0,0-.05.06l0,.07,0,.09a.1.1,0,0,1,0,.08.67.67,0,0,1-.1-.31c0-.17-.08-.25-.22-.25t-.06-.15a.31.31,0,0,1-.09-.15.3.3,0,0,1,.07-.08l0,.08v.1s0,.06.11.06a.3.3,0,0,1,.07-.16.59.59,0,0,0,.09-.18.17.17,0,0,0-.08-.13.24.24,0,0,0-.2-.11.47.47,0,0,0-.22.09v-.06l-.26,0,0,0v0c0-.06-.05-.11-.15-.18a.6.6,0,0,1-.16-.14.24.24,0,0,0-.18.1l-.2.21a.15.15,0,0,1,0,.1.14.14,0,0,0,0,.06.89.89,0,0,1-.09.16.54.54,0,0,0-.09.25.53.53,0,0,0-.19.31.5.5,0,0,1-.23.33c.06.05.09.09.09.14a.72.72,0,0,1-.1.26l-.11.21h-.1c-.06,0-.13.06-.18.17a.36.36,0,0,1-.19.22l-.05-.29c-.19,0-.28.05-.27.22l0,.09a.14.14,0,0,1,0,.1.12.12,0,0,1-.11,0c.06-.06.08-.1.05-.14a.18.18,0,0,0-.17-.07l-.08.09a.18.18,0,0,1-.11,0l0-.26a.06.06,0,0,0,.06,0,.15.15,0,0,1,.11-.15c.06,0,.11-.11.12-.26l-.07,0-.09.06-.09,0a.1.1,0,0,1,.07-.1l.1-.08.08.08c.1,0,.14-.11.13-.25v-.29a.71.71,0,0,0,.08-.26c0-.14.07-.21.14-.22l.08,0c.07,0,.1-.05.1-.1l-.06-.16a.16.16,0,0,1,.05-.1s.06,0,.08-.07l0-.14.1,0,0-.27s.07,0,.09,0a.18.18,0,0,0,.13-.11l0-.14,0-.17a.25.25,0,0,1,0-.15h.11a.07.07,0,0,1,0,0v0a.2.2,0,0,1,0-.13.21.21,0,0,1,.18,0v-.05c-.06,0-.09,0-.1-.1a.2.2,0,0,1,0-.09l0,0,.23.2a.56.56,0,0,0,.05-.18.35.35,0,0,1,0-.18s-.06-.06-.14-.06l-.07,0a2.87,2.87,0,0,0-.31-.6,4.94,4.94,0,0,1-.33-.63,3,3,0,0,1-.2-.29l-.33-.79a.78.78,0,0,1-.16-.26.35.35,0,0,0-.19-.25.13.13,0,0,0,0-.07v-.07l-.15-.15.25,0,.15,0,.18,0a.35.35,0,0,1,.15.13l.12,0a.22.22,0,0,0,.13-.05l.13-.15v0h.08a.41.41,0,0,0,0,.1s0,.05,0,.1a.25.25,0,0,0,.13.11.07.07,0,0,1,.06,0h0a.1.1,0,0,1,.09,0l.1.19v.06l-.06-.09a.13.13,0,0,0-.09-.09.3.3,0,0,0,.16.31l.36.2s-.08,0-.13,0a.19.19,0,0,0-.08.15.52.52,0,0,0,.16.19l.33.27a1.22,1.22,0,0,0,.32.19l.13-.26a.92.92,0,0,0,.1-.39c0-.07.08-.14.21-.2s.19-.11.2-.15a.14.14,0,0,1,0-.06l-.06-.09,0-.14a.58.58,0,0,1,0-.21.39.39,0,0,1,.14-.15.92.92,0,0,1,.25-.44v-.1l0,0,.06.09c0,.05.06.06.12,0l.08-.39a.16.16,0,0,0,.09,0l.12-.09c.06-.07.1-.11.13-.11l0,.06a.43.43,0,0,1,0,0,.44.44,0,0,1-.05.09l-.05.14s.07.07.21.06l.08-.12a.28.28,0,0,0,0-.15.13.13,0,0,0,0-.1l.18-.18a.5.5,0,0,1,.23-.19v.09s-.07,0-.11.05l-.07.06a.11.11,0,0,1,.11.06l0,.12v.16l-.05.06,0,0s0,0-.05-.12a.09.09,0,0,0-.08-.09l0,.09a.2.2,0,0,0,0,.09l.1.11-.28.63h-.13a.1.1,0,0,0-.08.05.35.35,0,0,1,.07.15c0,.06,0,.14-.13.24a.54.54,0,0,0-.13.28,1.56,1.56,0,0,0-.29.49,3.39,3.39,0,0,1-.23.49c-.05-.07-.1-.09-.14-.07a1.62,1.62,0,0,1-.15.64,2.87,2.87,0,0,0-.18.52l.21.14a.46.46,0,0,1,.19.17l.08.29a.35.35,0,0,0,.13.19l.08,0a.18.18,0,0,0-.12.19.22.22,0,0,0,.05.11l.1.08c.05,0,.08-.09.07-.22l.09.19a.21.21,0,0,0,.2.15,1,1,0,0,0,.2.35l.3.33v.1c0,.07.09.17.26.3a.49.49,0,0,1,.24.38.44.44,0,0,1,.22.16l.15.2,0,.13q0,.12.27.21t.27.27ZM268,180H268l0,0Zm.15-1.42h-.12v.06A.13.13,0,0,0,268.18,178.6Zm.24.85v-.06h-.06v.06Zm0-1.21-.05-.06v.1Zm.19-4.76-.06.08a.06.06,0,0,0,0,.09l0-.12s0,0,0,0A0,0,0,0,1,268.63,173.48Zm.57,4.41v0h-.08v0Zm-.18-4-.06-.06.05.11Zm.38,1a.18.18,0,0,0-.16-.06.13.13,0,0,0-.13.07s.06.06.12.06a.2.2,0,0,1,.14,0Zm-.06.39v0h-.08v0Zm.37-.55,0-.09c0-.05,0-.07-.08-.07a.19.19,0,0,0,0,.09l0,.11a.12.12,0,0,1,0,.11s.07,0,.09,0A.39.39,0,0,0,269.71,174.7Zm.4.17v-.06H270a.12.12,0,0,0-.11.08Zm.09,3.44a.21.21,0,0,1-.15-.17s0-.07,0-.08Zm.05-.92-.08,0a.14.14,0,0,0-.1.06.18.18,0,0,0-.05.11h.14l.07-.07A.19.19,0,0,0,270.25,177.39Zm.54.18v0h-.14v0Zm0-4.42v0h-.06v0Zm.41,5.48,0-.14c0-.07,0-.1-.08-.11l-.09,0,.11.16A.17.17,0,0,0,271.19,178.63Zm.09-1.3v0s0,0-.08,0v.08Zm.15.76h-.05v.12S271.42,178.16,271.43,178.09Zm.23.32h-.09v.1h0S271.66,178.47,271.66,178.41Zm.34,1.52s0-.05,0-.08l-.1,0-.08,0a.14.14,0,0,0,.15.1Zm.1-.53a.11.11,0,0,0-.11-.07l-.08,0-.1.1s0,.06,0,.07a.1.1,0,0,1,.08,0h0a.34.34,0,0,1,.11,0S272.1,179.46,272.1,179.4Zm.07-.47v-.07a.07.07,0,0,0,0-.06l-.06.14Zm.09.34v-.05l-.08,0v.1Zm.67.83v.09a.11.11,0,0,1,0,.05l0,0-.05-.05,0-.07S272.9,180.07,272.93,180.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "270.23px 176.01px",
                }}
                id="el3dc1tds86ix"
                className="animable"
              />
              <path
                d="M297.38,152.18l-.45.21a1.93,1.93,0,0,0-.36.23v.13l-.1-.09a.11.11,0,0,0-.11,0,.21.21,0,0,1-.14.15l-.13,0c-.09,0-.16,0-.21-.14s-.08-.18-.15-.18l-.09,0,0-.09c0-.1,0-.16-.07-.18h-.09l-.11,0a.31.31,0,0,1-.12-.15V152a.63.63,0,0,0-.34-.56s-.05,0-.05.06l0,.07,0,.08a.22.22,0,0,1,0,.08.49.49,0,0,1-.1-.3c0-.17-.08-.26-.22-.25,0,0,0-.09-.07-.16a.51.51,0,0,1-.09-.14.29.29,0,0,1,.08-.08l0,.08v.1s0,.06.12.06a.32.32,0,0,1,.07-.17.49.49,0,0,0,.08-.17.14.14,0,0,0-.08-.13.21.21,0,0,0-.2-.11.33.33,0,0,0-.22.09v-.06l-.25,0v-.1c0-.05-.05-.11-.14-.17a.68.68,0,0,1-.17-.15.26.26,0,0,0-.17.11l-.2.2a.3.3,0,0,1,.05.1l0,.07a.74.74,0,0,1-.08.15.42.42,0,0,0-.09.26.46.46,0,0,0-.19.31.56.56,0,0,1-.23.33.19.19,0,0,1,.08.14.51.51,0,0,1-.1.25l-.11.22-.06,0h0q-.1,0-.18.18a.33.33,0,0,1-.19.21l-.06-.29c-.18,0-.27.05-.26.23l0,.09a.14.14,0,0,1-.06.09.09.09,0,0,1-.1,0c.06-.06.07-.11,0-.15s-.09-.07-.18-.06l-.08.09a.23.23,0,0,1-.11,0v-.26s.05,0,.06-.06,0-.11.1-.15.11-.11.13-.25l-.07,0-.1.05-.08,0a.13.13,0,0,1,.06-.11l.1-.07.08.07c.1,0,.15-.1.14-.24l0-.16v-.14a.69.69,0,0,0,.07-.25c0-.14.07-.22.14-.22h.08c.07,0,.1-.05.1-.1l-.05-.16a.11.11,0,0,1,.05-.09.1.1,0,0,0,.07-.08l.05-.14.1.05,0-.27.1,0a.15.15,0,0,0,.12-.12l0-.14,0-.17a.32.32,0,0,1,0-.14h.11s0,0,0-.06v0a.2.2,0,0,1,0-.13c0-.05.09-.06.17-.06l0,0c-.06,0-.1,0-.1-.11s0-.07,0-.08l0-.06.22.21a.43.43,0,0,0,.05-.19.47.47,0,0,1,0-.17s-.07-.06-.14-.06l-.08,0a3.31,3.31,0,0,0-.3-.61,5.72,5.72,0,0,1-.33-.62,1.94,1.94,0,0,1-.21-.29l-.33-.8a.62.62,0,0,1-.16-.25.34.34,0,0,0-.18-.25.17.17,0,0,0,0-.07v-.07l-.14-.15.25,0,.14,0a.32.32,0,0,1,.18,0,.51.51,0,0,1,.16.12l.12,0a.17.17,0,0,0,.13-.05l.13-.14v0h.07v.09a.33.33,0,0,0,0,.1.17.17,0,0,0,.13.11l.05,0h0a.11.11,0,0,1,.1,0l.09.18V146l-.06-.08a.13.13,0,0,0-.09-.09.31.31,0,0,0,.15.31l.37.2s-.08,0-.14,0a.18.18,0,0,0-.07.15s.05.1.16.19l.32.27a2,2,0,0,0,.33.19l.12-.27a.89.89,0,0,0,.11-.38c0-.08.08-.14.21-.2s.19-.11.19-.16,0,0,0,0a.41.41,0,0,0-.06-.1v-.13a.44.44,0,0,1,0-.21.39.39,0,0,1,.14-.15.93.93,0,0,1,.26-.45V145a.05.05,0,0,1,0,0,.17.17,0,0,1,.06.09.1.1,0,0,0,.13,0l.07-.39a.19.19,0,0,0,.1.05l.12-.1a.37.37,0,0,1,.13-.11l0,.07,0,.05-.05.09-.05.14q0,.08.21.06l.08-.12a.24.24,0,0,0,0-.15.15.15,0,0,0,0-.1.88.88,0,0,0,.17-.19.66.66,0,0,1,.24-.19v.1s-.07,0-.11.05l-.06.06c.05,0,.08,0,.1,0l0,.12v.17l-.05.06,0,0s0,0-.05-.12a.09.09,0,0,0-.08-.09l0,.08a.22.22,0,0,0,0,.1l.1.11-.28.63h-.13a.14.14,0,0,0-.08.05.48.48,0,0,1,.08.16.38.38,0,0,1-.14.24.62.62,0,0,0-.12.27,1.49,1.49,0,0,0-.29.49,4.08,4.08,0,0,1-.23.49c-.06-.06-.11-.09-.14-.07a2,2,0,0,1-.15.64c-.11.28-.17.45-.19.52l.21.15a.5.5,0,0,1,.2.17l.07.29a.46.46,0,0,0,.13.19l.08-.05a.17.17,0,0,0-.11.2.18.18,0,0,0,.05.11l.1.08c.05,0,.07-.1.06-.22l.1.18a.21.21,0,0,0,.2.16.9.9,0,0,0,.2.35l.3.33,0,.09c0,.08.09.18.26.31a.46.46,0,0,1,.25.37.47.47,0,0,1,.21.16l.16.21,0,.13c0,.07.09.14.26.21s.27.15.27.27Zm-5.31.37H292l0,0Zm.14-1.41-.11,0v.06A.12.12,0,0,0,292.21,151.14Zm.24.84v-.06h-.06V152Zm0-1.2a.1.1,0,0,0-.05-.06v.1Zm.19-4.77-.05.08s0,.08,0,.1l.05-.13a.06.06,0,0,0,0,0A0,0,0,0,1,292.66,146Zm.58,4.41v0h-.09v0Zm-.19-4-.06-.06.05.1Zm.38,1s-.07,0-.16-.06a.15.15,0,0,0-.13.08.12.12,0,0,0,.12.06c.07,0,.11,0,.14.05Zm-.05.39v0h-.07v0Zm.36-.54a.31.31,0,0,1,0-.09c0-.05,0-.08-.08-.07s0,0,0,.08l0,.11s0,.08,0,.11a.06.06,0,0,0,.09,0A.16.16,0,0,0,293.74,147.24Zm.4.16v-.06H294a.14.14,0,0,0-.11.08Zm.1,3.45c-.1,0-.15-.09-.16-.17l0-.09Zm.05-.93-.08,0a.12.12,0,0,0-.1.06.16.16,0,0,0-.05.12h.14l.06-.07A.09.09,0,0,0,294.29,149.92Zm.53.19v0h-.14v0Zm0-4.42v0h-.06v0Zm.41,5.48,0-.14q0-.1-.09-.12l-.08,0,.1.17A.2.2,0,0,0,295.23,151.17Zm.09-1.3v0s0,0-.08,0v.07Zm.14.76h0v.11S295.46,150.7,295.46,150.63Zm.23.31h-.08V151h0S295.69,151,295.69,150.94Zm.35,1.53a.07.07,0,0,0,0-.08l-.11,0-.07,0a.14.14,0,0,0,.15.1Zm.09-.53a.11.11,0,0,0-.11-.08l-.07,0-.11.1s0,.06,0,.07l.08,0h0a.41.41,0,0,1,.12,0S296.14,152,296.13,151.94Zm.07-.47v-.07a.1.1,0,0,0,0-.07l-.05.14Zm.1.34v-.05l-.08,0v.09Zm.67.82v.1l0,0-.05,0-.05-.05,0-.08A.08.08,0,0,1,297,152.63Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "294.28px 148.505px",
                }}
                id="el9qlxaqb30ip"
                className="animable"
              />
              <path
                d="M287,165.89c0,.05.08,0,.1,0S287,165.91,287,165.89Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.051px 165.901px",
                }}
                id="el4z3evkh1cxp"
                className="animable"
              />
              <polygon
                points="286.06 165.23 286.1 165.22 285.92 165.16 286.06 165.23"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "286.01px 165.195px",
                }}
                id="el1t3j1et6tj8"
                className="animable"
              />
              <path
                d="M285.25,164.49c0,.16.31.25.42.41v0l.13,0A3.53,3.53,0,0,1,285.25,164.49Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.525px 164.695px",
                }}
                id="el61jaze09lp6"
                className="animable"
              />
              <path
                d="M285,163.9c0,.06-.13-.06.09.1a0,0,0,0,1,0,0A.23.23,0,0,1,285,163.9Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.026px 163.95px",
                }}
                id="eli9e628fxybg"
                className="animable"
              />
              <path
                d="M285.08,164c.08.08,0-.08,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.098px 164px",
                }}
                id="elr8mupdp24up"
                className="animable"
              />
              <path
                d="M284.66,163.64l.14.07c0-.05-.1-.14-.05-.12C284.62,163.5,284.63,163.54,284.66,163.64Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.721px 163.625px",
                }}
                id="el7z69gqbdbuq"
                className="animable"
              />
              <path
                d="M284.49,163.61l.07,0S284.55,163.61,284.49,163.61Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.525px 163.61px",
                }}
                id="el0a6ka2rzvqj"
                className="animable"
              />
              <path
                d="M284.71,163.73l-.15-.08s0,.06.07,0S284.64,163.7,284.71,163.73Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.635px 163.681px",
                }}
                id="el0wwmjp1wml9e"
                className="animable"
              />
              <path
                d="M284.77,163.81c.05.07.12.16,0,.09C285,164.08,285,164,284.77,163.81Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.856px 163.905px",
                }}
                id="elk5yy2xevnu"
                className="animable"
              />
              <path
                d="M285.12,164.55l.06.1.1,0S285.14,164.51,285.12,164.55Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "285.2px 164.596px",
                }}
                id="elvfaxiy2svfj"
                className="animable"
              />
              <path
                d="M284,163s0,.09,0,.12l.1.05C284.05,163.07,284,163,284,163Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.05px 163.085px",
                }}
                id="elokgxnk6ax7i"
                className="animable"
              />
              <path
                d="M284,163.07l-.16-.08C283.93,163.1,284,163.1,284,163.07Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.92px 163.039px",
                }}
                id="elo5u0txhhtw9"
                className="animable"
              />
              <polygon
                points="284.38 163.69 284.65 163.81 284.34 163.66 284.38 163.69"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "284.495px 163.735px",
                }}
                id="elnpqsh7btfl"
                className="animable"
              />
              <polygon
                points="283.53 162.52 283.64 162.54 283.45 162.49 283.53 162.52"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.545px 162.515px",
                }}
                id="elbct68gpmw1i"
                className="animable"
              />
              <path
                d="M283.2,162.24c.09,0,0-.13.07-.1A1.09,1.09,0,0,0,283,162,.65.65,0,0,1,283.2,162.24Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.135px 162.12px",
                }}
                id="eli4l2lha3fcl"
                className="animable"
              />
              <path
                d="M282.83,161.94a.52.52,0,0,0,.15.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.905px 161.97px",
                }}
                id="elt4mznd7ba7"
                className="animable"
              />
              <path
                d="M283.62,162.79a.2.2,0,0,0-.05-.15v.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.595px 162.715px",
                }}
                id="elx31d3oqeun"
                className="animable"
              />
              <path
                d="M283.57,162.75h0l-.1-.09Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.52px 162.705px",
                }}
                id="elr057l8mxwja"
                className="animable"
              />
              <path
                d="M283.63,162.8h0S283.62,162.8,283.63,162.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.628px 162.8px",
                }}
                id="els2vp0my1ke"
                className="animable"
              />
              <path
                d="M284.1,163.34c-.1,0-.29-.14-.18,0C284,163.4,284,163.29,284.1,163.34Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.993px 163.318px",
                }}
                id="eltycrfc23yfg"
                className="animable"
              />
              <path
                d="M283.63,162.93l.12.13s0-.06.06,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.72px 162.995px",
                }}
                id="eli9r3rrf77zn"
                className="animable"
              />
              <polygon
                points="283.55 162.88 283.63 162.93 283.58 162.88 283.55 162.88"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.59px 162.905px",
                }}
                id="eln41furwagyr"
                className="animable"
              />
              <path
                d="M283,162.2a.9.9,0,0,1-.22-.14,1.52,1.52,0,0,0,.22.22S283.09,162.27,283,162.2Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.91px 162.17px",
                }}
                id="elgolhz3gnqro"
                className="animable"
              />
              <path
                d="M283.28,162.47h0S283.27,162.48,283.28,162.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.278px 162.472px",
                }}
                id="elphutc4ygjx8"
                className="animable"
              />
              <path
                d="M282.1,161.43v0h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.1px 161.43px",
                }}
                id="elmia2vxz60hc"
                className="animable"
              />
              <path
                d="M283,162.3a.25.25,0,0,0,.14.1.81.81,0,0,1-.14-.12S283,162.29,283,162.3Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.07px 162.34px",
                }}
                id="elr7ft8my1l2"
                className="animable"
              />
              <path
                d="M282.61,161.79l.07.12S282.64,161.79,282.61,161.79Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.645px 161.85px",
                }}
                id="elujkk0nh6vx"
                className="animable"
              />
              <path
                d="M283.26,162.43h0S283.26,162.41,283.26,162.43Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.26px 162.426px",
                }}
                id="el6p1bknobdt"
                className="animable"
              />
              <path
                d="M283.26,162.43l-.11,0,.11.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.205px 162.46px",
                }}
                id="el727wcpwjqfb"
                className="animable"
              />
              <path
                d="M281.56,161.59c.06,0,.25,0,.33.22s.23-.17.21-.36c0,0-.05,0-.2,0,0,.07.23.33.07.28s-.12-.15-.18-.25c0,.12-.31-.11-.35,0l.18.1S281.53,161.54,281.56,161.59Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.771px 161.662px",
                }}
                id="elp0sluns9ecb"
                className="animable"
              />
              <path
                d="M282.34,161.68l.06,0a.67.67,0,0,1-.12-.24c-.11,0-.15,0-.17,0l.26.25Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.255px 161.565px",
                }}
                id="elc236oqblp3v"
                className="animable"
              />
              <path
                d="M282.64,161.94c-.09,0-.24-.07-.22.06s.16.05.27.11c-.26-.21.11,0-.05-.16a.58.58,0,0,1,.15.11l-.11-.15h0s0,0,0,0h0c-.06-.08,0,0,0,0h0l-.29-.2A1.92,1.92,0,0,0,282.64,161.94Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.59px 161.91px",
                }}
                id="elt24pe4p94u"
                className="animable"
              />
              <path
                d="M283.67,163.38v0S283.64,163.35,283.67,163.38Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.663px 163.373px",
                }}
                id="elhlx0sly8hf"
                className="animable"
              />
              <path
                d="M283.6,163.25a.21.21,0,0,1,.12.06c0,.07,0,0,0,.05s.16.08.07-.05h0c-.09-.11-.16-.3-.13-.3A.52.52,0,0,0,283.6,163.25Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "283.709px 163.206px",
                }}
                id="el6llef6em7m5"
                className="animable"
              />
              <path
                d="M283.1,162.6l-.24-.16.07.15,0,0C283.06,162.67,283.07,162.61,283.1,162.6Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.98px 162.535px",
                }}
                id="elvrf1wlypm5"
                className="animable"
              />
              <polygon
                points="282.1 161.38 282.18 161.34 281.98 161.32 282.1 161.38"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.08px 161.35px",
                }}
                id="eld9qp8px212h"
                className="animable"
              />
              <polygon
                points="281.81 161.13 281.87 161.09 281.77 161.06 281.81 161.13"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.82px 161.095px",
                }}
                id="elhurmi361y66"
                className="animable"
              />
              <path
                d="M265.62,147.65l-.08,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "265.58px 147.65px",
                }}
                id="el1etlx6ules9"
                className="animable"
              />
              <path
                d="M266.07,148.06h0S266.05,148,266.07,148.06Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.066px 148.047px",
                }}
                id="elhpnuwa8ehqq"
                className="animable"
              />
              <path
                d="M275,155c-.05-.13-.08-.29-.12-.23l0,.09A.37.37,0,0,1,275,155Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "274.94px 154.879px",
                }}
                id="elo54nei69k09"
                className="animable"
              />
              <path
                d="M270.05,151.12v0h.11Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "270.105px 151.12px",
                }}
                id="elfwt1f2i7rk"
                className="animable"
              />
              <path
                d="M269.78,151.29l-.09-.14A.63.63,0,0,0,269.78,151.29Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "269.735px 151.22px",
                }}
                id="el3iz9k90rig"
                className="animable"
              />
              <path
                d="M263.09,145.47H263C263.08,145.56,263.06,145.49,263.09,145.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "263.045px 145.492px",
                }}
                id="elegffxr09g9k"
                className="animable"
              />
              <path
                d="M279,158.59a1,1,0,0,0-.17-.21C278.86,158.5,278.89,158.62,279,158.59Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.915px 158.487px",
                }}
                id="elty3ltn30e6"
                className="animable"
              />
              <path
                d="M266.34,147.21s-.07,0-.08,0S266.34,147.23,266.34,147.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.3px 147.214px",
                }}
                id="el4601zybcyvh"
                className="animable"
              />
              <path
                d="M278.57,158.18l0,0a.83.83,0,0,1,.19.15A.34.34,0,0,0,278.57,158.18Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.665px 158.255px",
                }}
                id="el60e6oqupho9"
                className="animable"
              />
              <path
                d="M254.16,137.31v0S254.15,137.3,254.16,137.31Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "254.158px 137.308px",
                }}
                id="eldfbo64omozq"
                className="animable"
              />
              <path
                d="M252.06,134.37l.11.1S252.13,134.42,252.06,134.37Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "252.115px 134.42px",
                }}
                id="elnhuirir49wc"
                className="animable"
              />
              <path
                d="M280.72,160.1c.06.05,0,0,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.733px 160.111px",
                }}
                id="el4ilgfunehr6"
                className="animable"
              />
              <path
                d="M280.81,160.58h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.81px 160.58px",
                }}
                id="el8bj86n2909i"
                className="animable"
              />
              <path
                d="M277.67,158h0S277.67,158,277.67,158Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "277.67px 158px",
                }}
                id="eluzo6rjg5nsr"
                className="animable"
              />
              <path
                d="M280.79,160.21l-.06-.09A.27.27,0,0,0,280.79,160.21Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.76px 160.165px",
                }}
                id="el79921mgtxh"
                className="animable"
              />
              <path
                d="M280.8,160.17s0,0,.05.05S280.86,160.21,280.8,160.17Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.836px 160.204px",
                }}
                id="elu64l2g8pzm"
                className="animable"
              />
              <path
                d="M280.64,160l.09.09a0,0,0,0,1,0,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.685px 160.045px",
                }}
                id="elrhgxyto7lq"
                className="animable"
              />
              <path
                d="M259.11,141.5h0C259,141.46,259.05,141.48,259.11,141.5Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "259.077px 141.488px",
                }}
                id="el7hspzvqc0e6"
                className="animable"
              />
              <polygon
                points="272.95 154.2 272.96 154.2 272.96 154.19 272.95 154.2"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "272.955px 154.195px",
                }}
                id="elxqxfbd0czb"
                className="animable"
              />
              <path
                d="M272.71,154.05a.28.28,0,0,1,.25.14L273,154Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "272.855px 154.095px",
                }}
                id="elxfk9iwhwldf"
                className="animable"
              />
              <path
                d="M264.52,146.68l.1.19c-.25-.08-.35-.23-.28,0,.18-.16.59.5.77.35l.07.12-.07-.05c.11.26.26,0,.48.25.07.09.11.17,0,.14l.23.13c0,.05,0,.07,0,0l.3.25h0l.27.07c.05.09-.06,0-.06,0l.2.1c0,.1-.15,0-.25-.08,0,.31.17.08.4.47l-.17-.11c0,.18.16.26.27.47,0-.07-.2-.46,0-.35a.44.44,0,0,0,.23.17c-.06.06.05.13.16.23l-.08.08c.15.09.17,0,.28.06a.3.3,0,0,1,.09.31c.21.21-.06-.24.21,0a1.76,1.76,0,0,0,.58.43c.11.15-.07,0,0,.1l.17,0s0,0,0,.05.08-.08.25,0c.13-.06.19.41.47.48,0,.06-.06,0-.08.07a2.42,2.42,0,0,0,.56.25l.13.22c-.08-.18.16-.1.36,0A.91.91,0,0,0,270,151l.28.17s0,0-.11,0l.07,0c.05.11,0,.12-.14.11a.65.65,0,0,0,0-.13c-.1,0-.2,0-.24.07a.26.26,0,0,1,.1,0s-.05,0-.05,0,0,0,.1,0l0,.15.05,0,.25.34a2.94,2.94,0,0,1,1,.42c-.15,0-.12.09-.2.17,0-.17-.3-.21-.47-.39.09.09,0,.11,0,.1l.39.22c-.07.06,0,.11.09.21.05-.18.32.14.48.22,0,.05-.1.06-.25-.09.09.26.37.05.47.29,0,0-.06-.06-.1-.05.13,0,.33.28.24.27l0,0c.25.25.79.72.93.65a1.06,1.06,0,0,1-.19.11l.8,0c-.12.05-.17.42-.44.28a.7.7,0,0,0,.28.24c0-.05,0-.23.08-.19.06.25.06.16.18.42-.05-.07.16-.16.25-.06s-.12.15-.17.13l.38.08c-.09,0-.13.12-.11.21s.22.2.3.24l-.08-.13c.26.28.47.05.74.31-.06,0-.25.11-.13.26a1.68,1.68,0,0,1,.92.06v.09l.24.12a4.27,4.27,0,0,1-1-.13c.07.13,0,.08.14.22s-.14,0-.19-.06l.35.36c.06-.1.36.17.41.08l-.48-.27c.06-.1.45,0,.7.21,0,.1-.19.12-.21.15a.68.68,0,0,1,.3.21l-.15-.05c.12.16.34.09.44.33.06.06.08.28.14.24a.69.69,0,0,1,.38-.53,2.44,2.44,0,0,1,.25.49c0,.17-.31.05-.29.2,0,0,.11,0,.14,0s-.09.05.05.16c.11-.07.27.26.13,0,.16.1.13.15.29.29,0-.1.32-.07.48,0,0,0,0,.07,0,.1.26.25.37.43.63.66,0,0,.11.18,0,.17a.45.45,0,0,1,.19.09c.09.22-.19-.15-.15.05.05,0,.27.11.41.18,0-.11-.25-.25-.39-.32.1-.11.4,0,.71.15s0,.15.08.17c.39.38.52.12.78.5.07.19-.12.14-.14.22-.2-.13-.22-.29-.34-.39l.11.16a.47.47,0,0,1-.25-.19c.05.17.66.63.92,1,.08,0,.19.09.17,0l-.1-.08c0-.13.25.09.15,0a1.17,1.17,0,0,1,.53.34l0,.11s-.08-.15-.15-.17c.11,0,.18.36.36.33l-.08-.17a1.44,1.44,0,0,1,.55.31c0-.14-.05,0-.08-.22,0,.21-.41-.38-.36-.12,0-.21-.16-.23-.24-.46,0,0,0,.1-.11,0h-.06a.19.19,0,0,1,0,.11c-.13,0-.34-.2-.44-.24s.06,0,0-.07l-.15-.05c0-.07-.13-.29.09-.15l-.17-.16a.8.8,0,0,0-.31-.32c-.07-.09-.26-.12-.15-.1l-.31-.21,0,0c-.27-.25-.64-.4-.85-.78a1.4,1.4,0,0,1-.35-.29l0-.08c-.14-.08-.31-.36-.4-.35,0-.25-.54-.54-.72-.75l-.07.06a2.85,2.85,0,0,0-.48-.36l0,.06c-.2-.08-.35-.38-.59-.59,0,0,.09,0,.12.1-.17-.39-.69-.44-.86-.77a.34.34,0,0,1-.15-.14h0l0,0L275,155a.46.46,0,0,0,.05.1,1.64,1.64,0,0,0-.22-.15l0-.09,0,0c-.05-.09-.09,0-.09.06l-.13,0c0-.15-.23-.14-.21-.32-.1,0-.4-.29-.54-.22s0,0,0,0a.58.58,0,0,0-.24-.2l0-.11-.28-.06c-.06-.15-.08-.21.08-.18-.34-.14-.24-.15-.55-.26l.06,0c-.08-.07-.27-.12-.28-.2-.09-.26-.86-.81-1.23-1.31-.05.23-.15-.26-.28-.16,0-.06,0-.13.07-.1-.27-.17-.3-.37-.55-.42,0-.06.1,0,.09-.05s-.1,0-.1-.07.06,0,.11,0c-.24-.2-.37-.12-.48,0a.28.28,0,0,0-.29-.21c0-.07-.05-.17.11-.05-.08-.08-.46-.51-.54-.43s-.11-.11-.14-.12a4.27,4.27,0,0,0-.38-.36l0,.06c-.17,0-.18-.11-.33-.23h0c-.27-.38-.18-.1-.43-.48l0,.08c-.16.11-.23-.35-.47-.47l0,.1c-.14-.05-.13-.45-.22-.56s.06,0,.1,0c-.21-.33-.11,0-.34-.12,0-.07.15-.06,0-.2s-.53-.22-.64-.47c0,.07,0,.14-.1.08s-.28-.49-.51-.5c0,0,0,0,0-.05s-.41-.29-.53-.29c.09-.31-.79-.53-.61-.72l-.36-.31h0a.79.79,0,0,0-.45-.26c0-.06,0,0,0-.05a1.33,1.33,0,0,0-.69-.5l0-.09c-.1,0-.37-.52-.76-.73a.11.11,0,0,1,.06,0c-.42-.24-.69-1-1-.83l-.29-.16c.29.3-.13.07,0,.22-.14-.1-.25-.35-.09-.23v0c.17-.07-.34-.38-.25-.46-.61-.54-1.13-1.13-1.69-1.64l0,.15-.19-.18c0-.07,0-.14.14,0,0-.12-.28-.16-.3-.15-.24-.28.21,0,.12-.09l-.12-.1,0,0c-.13-.05-.17,0-.34-.17s0-.11,0-.15,0,0-.06-.06l-.24-.23,0,.16c-.14-.06-.22-.21-.19,0-.05-.28-.87-.88-.94-1a1.76,1.76,0,0,1-.53-.4c0,.06,0,.17-.17,0,.22-.12-.23-.34-.24-.58,0,.18-.48-.4-.4-.11-.13-.14,0-.08,0-.21a12.28,12.28,0,0,1-1.08-1s0,.11-.11.07l-.1-.3c-.09-.06-.2.07-.27-.14,0,0,0,.09-.05.05-.67-.81-1.67-1.34-2.26-2.26.06.14,0,.08-.1,0s0,.05-.07.09l-.25-.38c-.14,0-.36-.32-.46-.27l.11.08c-.22,0,0,.4-.13.5.25,0,.26.54.66.61,0,.08-.09,0-.08.05a.47.47,0,0,1,.32.22h0a9.44,9.44,0,0,1,1,1l0-.09a.66.66,0,0,1,.12.19c.2,0-.27-.25-.07-.26,0,.24.69.63.65.85.15.13,0-.11.17,0v.08l.09,0a.22.22,0,0,1,.07.23s-.08,0-.11-.07c.09.16.38.31.41.36l-.12-.15c.44.33.9.84,1.37,1.12v0c.35.22.22.25.57.52h0a.82.82,0,0,1,.29.18c-.12,0,.1.21,0,.15.38.24.21.1.52.19v.1a.48.48,0,0,1,.32.14c-.18-.08,0,.17-.17.09.26.42.62.36.64.66.28,0-.24-.35-.12-.3,0-.11.22,0,.32.14s0,.34.32.59c0,0,0,0-.07,0,.08.13.25.14.33.31.15,0,.16.16.28.07l-.28-.21c.17.11.42.11.6.39a.76.76,0,0,1-.19-.07c0,.1.15.11.2.2-.12.09-.34-.26-.57-.32,0,.11.37.19.19.23.24,0,.26.27.55.34l-.07.05a.43.43,0,0,1,.18.09h-.17c.21.07.24.33.42.43-.31-.28,0-.14-.09-.3.46.26.07.36.53.71-.07,0-.19-.23-.07-.18a1,1,0,0,1,.18.29c.29.24,0-.11.17,0s.16.09.31.18l-.06.06c.29.33.21-.05.45.26l-.2,0,.17.11-.09,0a.9.9,0,0,1,.39.33c0-.08.13-.1.27,0s-.11,0,.1.18c-.08,0-.17,0-.21-.13h-.11a1.17,1.17,0,0,0,.33.31l-.07-.08.38.17c0,.07,0,.06-.08,0s.13.06.22.13a.2.2,0,0,1-.26,0l.33.2s.09.19,0,.17c.13.17.47.25.64.47a.17.17,0,0,1,.1,0,2.07,2.07,0,0,0,.59.54l.05.21c.19.14-.08-.28.25,0s0,.15.06.24a.85.85,0,0,1,.55.37A1.39,1.39,0,0,1,264.52,146.68Zm2.25,2s-.18-.06-.17-.12S266.72,148.56,266.77,148.64Zm3.5,2.89s-.09-.06-.13-.13A.4.4,0,0,1,270.27,151.53Zm9.76,8Zm.18.21s0,0,0,0-.07,0-.12-.08S280.13,159.74,280.21,159.78Zm-21.35-19.26,0,0C258.78,140.51,258.82,140.51,258.86,140.52Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.51px 147.517px",
                }}
                id="el68m75x9f1zo"
                className="animable"
              />
              <path
                d="M269.81,151.28a.2.2,0,0,1,0-.05S269.79,151.23,269.81,151.28Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "269.806px 151.255px",
                }}
                id="elt1ztrts9q3j"
                className="animable"
              />
              <path
                d="M263.12,145.47h0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "263.12px 145.47px",
                }}
                id="el3yx4ichgdxg"
                className="animable"
              />
              <path
                d="M276.65,157.53a.29.29,0,0,1,0-.1S276.59,157.47,276.65,157.53Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "276.637px 157.48px",
                }}
                id="elriv8zrcyv8"
                className="animable"
              />
              <path
                d="M281.36,160.62l-.15-.09a.25.25,0,0,1,.1.12S281.31,160.59,281.36,160.62Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.285px 160.59px",
                }}
                id="elwhr3i6dy7i"
                className="animable"
              />
              <path
                d="M282.11,162l.06.05,0,0C282.11,162,282,161.93,282.11,162Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "282.117px 162.01px",
                }}
                id="eld50epja1gf"
                className="animable"
              />
              <path
                d="M281.28,160.94c0,.06.06.12.16.19l0,0C281.52,161.06,281.4,161,281.28,160.94Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.373px 161.035px",
                }}
                id="elmb35ivyo7fd"
                className="animable"
              />
              <path
                d="M281.46,161.14l.09.08S281.52,161.18,281.46,161.14Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.505px 161.18px",
                }}
                id="elsqgzbu6zmad"
                className="animable"
              />
              <path
                d="M280.49,160c0,.05.09,0,.09,0S280.23,159.8,280.49,160Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.487px 159.967px",
                }}
                id="elgztcc971g4q"
                className="animable"
              />
              <polygon
                points="281.2 161.18 281.23 161.09 281.13 161.09 281.2 161.18"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "281.18px 161.135px",
                }}
                id="elksujfyzctud"
                className="animable"
              />
              <path
                d="M280.54,160.49l.13.15h.05C280.64,160.57,280.55,160.47,280.54,160.49Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.63px 160.564px",
                }}
                id="ele9z90c8vj8o"
                className="animable"
              />
              <path
                d="M280.72,160.65a.5.5,0,0,0,.13.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.785px 160.7px",
                }}
                id="eln0bd8mtksr"
                className="animable"
              />
              <polygon
                points="280.99 161.02 280.96 160.97 280.86 160.88 280.99 161.02"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "280.925px 160.95px",
                }}
                id="elyp6n08f13c"
                className="animable"
              />
              <path
                d="M279,159.1l-.17-.12s0,.06.06.1S279,159.14,279,159.1Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.915px 159.051px",
                }}
                id="elcox33pzdr6t"
                className="animable"
              />
              <path
                d="M279.33,159.7a.15.15,0,0,1,0-.11l-.08.08Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "279.29px 159.645px",
                }}
                id="eluh0q1awtg0k"
                className="animable"
              />
              <path
                d="M278.86,159.31s-.28-.14-.08,0C278.81,159.34,279,159.46,278.86,159.31Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "278.809px 159.316px",
                }}
                id="el6jhiyai368x"
                className="animable"
              />
              <polygon
                points="276.61 157.09 276.65 157.13 276.76 156.99 276.61 157.09"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "276.685px 157.06px",
                }}
                id="eli237jny8o6h"
                className="animable"
              />
              <path
                d="M274.26,155.54c.16.2.12,0,.25.15.07,0-.11-.16,0-.14C274.33,155.48,274.29,155.41,274.26,155.54Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "274.393px 155.579px",
                }}
                id="elkd6r642hdee"
                className="animable"
              />
              <path
                d="M274.47,155.55l.08,0Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "274.51px 155.55px",
                }}
                id="el2mdm5nf92ex"
                className="animable"
              />
              <polygon
                points="251.69 133.91 251.6 133.78 251.64 133.89 251.69 133.91"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "251.645px 133.845px",
                }}
                id="eltywnc4xczrp"
                className="animable"
              />
              <path
                d="M251.38,133.8l.05-.11h-.14S251.37,133.76,251.38,133.8Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "251.36px 133.745px",
                }}
                id="elc2vylogop0j"
                className="animable"
              />
              <path
                d="M267,149.11h0s0-.07-.07-.12Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "266.965px 149.05px",
                }}
                id="el3c4vbn14ino"
                className="animable"
              />
              <polygon
                points="265.4 147.6 265.42 147.62 265.44 147.5 265.4 147.6"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "265.42px 147.56px",
                }}
                id="elgum65dpdob"
                className="animable"
              />
              <path
                d="M264,146.47a1.39,1.39,0,0,0,.19.15C264,146.53,264,146.37,264,146.47Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "264.095px 146.529px",
                }}
                id="el5fm4e26jv8t"
                className="animable"
              />
              <path
                d="M418.48,150.34l1.16,23.24L405.44,192a23.27,23.27,0,0,1,13-41.68Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "408.001px 171.16px",
                }}
                id="els7dxxv3ymj"
                className="animable"
              />
              <path
                d="M447.74,174.55a23.27,23.27,0,0,1-37.44,19.6l14.19-18.44-1.16-23.24a23.26,23.26,0,0,1,24.41,22.08Z"
                style={{
                  fill: "rgb(235, 235, 235)",
                  transformOrigin: "429.035px 175.712px",
                }}
                id="eldow7l90vzys"
                className="animable"
              />
              <path
                d="M447.74,174.55a23.27,23.27,0,0,1-37.44,19.6l14.19-18.44-1.16-23.24a23.26,23.26,0,0,1,24.41,22.08Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "429.035px 175.712px",
                }}
                id="eli2iux6m2ije"
                className="animable"
              />
              <path
                d="M381.11,148.15c2.64,19.47-8.62,26.06-9.36,31.62a90.11,90.11,0,0,0-.05,10.32L352,195.15a87.4,87.4,0,0,0-5-9c-3.32-4.52-16.36-4.88-23.41-23.22-4.64-12-.33-34.06,20.58-39.42S379.37,135.36,381.11,148.15Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "351.705px 158.827px",
                }}
                id="eloqz8o2vyb5"
                className="animable"
              />
              <g id="ely0e1yllqwab">
                <rect
                  x="349.85"
                  y="163.7"
                  width={1}
                  height="32.99"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    transformOrigin: "350.35px 180.195px",
                    transform: "rotate(-24.94deg)",
                  }}
                  className="animable"
                  id="elrxyn660vkf"
                />
              </g>
              <g id="elhljvpiexptd">
                <rect
                  x="365.42"
                  y="159.7"
                  width={1}
                  height="32.99"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    transformOrigin: "365.92px 176.195px",
                    transform: "rotate(-3.81deg)",
                  }}
                  className="animable"
                  id="el3kuvynbzhbt"
                />
              </g>
              <path
                d="M356,173c-1.37,0-2.94-.84-4.49-2.43l-.27-.28a2.46,2.46,0,0,1-1.45,1.38c-3,.75-5.7-5-5.81-5.28l.45-.21c0,.06,2.69,5.66,5.23,5a2.08,2.08,0,0,0,1.22-1.29c-1.91-2.24-3.47-5.42-3-7.18a1.55,1.55,0,0,1,1.15-1.14c1.3-.34,2.31.51,2.77,2.32a10.74,10.74,0,0,1-.36,5.9c.13.15.27.29.41.43,1,1,2.95,2.67,4.79,2.2,1.23-.31,2.22-1.6,2.78-3.62.05-.17.09-.34.14-.52a8.39,8.39,0,0,1-3.17-4.82c-.56-2.19-.09-3.76,1.23-4.09a1.42,1.42,0,0,1,1.44.44c1.26,1.29,1.71,5,1.06,8.24a2.94,2.94,0,0,0,2.2.32c1.82-.47,2.31-5.2,2.35-7h.5c0,.28-.17,6.81-2.72,7.46a3.39,3.39,0,0,1-2.44-.31c0,.14-.07.27-.11.4-.43,1.55-1.33,3.5-3.13,4A3,3,0,0,1,356,173Zm-6.42-11a1.9,1.9,0,0,0-.4,0,1.06,1.06,0,0,0-.8.8c-.39,1.39.84,4.25,2.71,6.51a10.29,10.29,0,0,0,.25-5.34C351.14,163.25,350.65,162,349.58,162Zm8.43-2.18a1,1,0,0,0-.24,0c-1.23.31-1.24,2.08-.88,3.49a8.45,8.45,0,0,0,2.79,4.43c.56-3,.16-6.46-1-7.61A1,1,0,0,0,358,159.84Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "354.575px 166.161px",
                }}
                id="elhd87i8xxohc"
                className="animable"
              />
              <g id="elpuiaw0cdoto">
                <rect
                  x="349.65"
                  y="191.69"
                  width="25.16"
                  height="4.92"
                  style={{
                    fill: "rgb(69, 90, 100)",
                    transformOrigin: "362.23px 194.15px",
                    transform: "rotate(-14.38deg)",
                  }}
                  className="animable"
                  id="elb80ggwc00lb"
                />
              </g>
              <g id="eltzpbgagczxp">
                <rect
                  x="351.07"
                  y="197.23"
                  width="25.16"
                  height="4.92"
                  style={{
                    fill: "rgb(69, 90, 100)",
                    transformOrigin: "363.65px 199.69px",
                    transform: "rotate(-14.38deg)",
                  }}
                  className="animable"
                  id="elameolkmvtbl"
                />
              </g>
              <g id="elmob0yyf2gtf">
                <rect
                  x="352.48"
                  y="202.76"
                  width="25.16"
                  height="4.92"
                  style={{
                    fill: "rgb(69, 90, 100)",
                    transformOrigin: "365.06px 205.22px",
                    transform: "rotate(-14.38deg)",
                  }}
                  className="animable"
                  id="el2ppruoebuoo"
                />
              </g>
              <path
                d="M342.91,120.19a14.1,14.1,0,0,1-2-7.13,12.3,12.3,0,0,1,1.47,3.43A12.62,12.62,0,0,1,342.91,120.19Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "341.91px 116.625px",
                }}
                id="elcu95iz34kha"
                className="animable"
              />
              <path
                d="M327.72,129a12.64,12.64,0,0,1-3-2.24,12.81,12.81,0,0,1-2.29-2.95,12.13,12.13,0,0,1,3,2.24A12.4,12.4,0,0,1,327.72,129Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "325.075px 126.405px",
                }}
                id="elwr1m22vkbno"
                className="animable"
              />
              <path
                d="M318.94,144.13a14.08,14.08,0,0,1-7.17-1.86,14.08,14.08,0,0,1,7.17,1.86Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "315.355px 143.2px",
                }}
                id="elad6nw2ap35l"
                className="animable"
              />
              <path
                d="M318.92,161.67a13.84,13.84,0,0,1-7.13,2,13.84,13.84,0,0,1,7.13-2Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "315.355px 162.67px",
                }}
                id="elyqlsigku5jb"
                className="animable"
              />
              <path
                d="M327.68,176.87a12.52,12.52,0,0,1-2.24,3,12.34,12.34,0,0,1-2.95,2.29,12.13,12.13,0,0,1,2.24-3A12.77,12.77,0,0,1,327.68,176.87Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "325.085px 179.515px",
                }}
                id="elvx8jcg624jl"
                className="animable"
              />
              <path
                d="M384.38,161.72a12.38,12.38,0,0,1,3.71.45,12.24,12.24,0,0,1,3.45,1.41,12.28,12.28,0,0,1-3.7-.45A12.42,12.42,0,0,1,384.38,161.72Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "387.96px 162.65px",
                }}
                id="els2akxv2w7i"
                className="animable"
              />
              <path
                d="M384.39,144.18a13.91,13.91,0,0,1,7.14-2,13.91,13.91,0,0,1-7.14,2Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "387.96px 143.18px",
                }}
                id="elkhath35wepd"
                className="animable"
              />
              <path
                d="M375.63,129a12.56,12.56,0,0,1,2.25-3,12.15,12.15,0,0,1,2.95-2.29,13.8,13.8,0,0,1-5.2,5.27Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "378.23px 126.355px",
                }}
                id="elml2uoz1u56"
                className="animable"
              />
              <path
                d="M360.45,120.2a14.07,14.07,0,0,1,1.86-7.16,12.38,12.38,0,0,1-.44,3.71A12.58,12.58,0,0,1,360.45,120.2Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "361.383px 116.62px",
                }}
                id="el3zw5yvbyg8i"
                className="animable"
              />
              <path
                d="M308,226.81c-4.82.24-11.35-7.37-15.46-18.36-4.48-12-4.16-22.81.73-24.64s12.23,6.13,16.71,18.11a44.76,44.76,0,0,1,3.14,15.87c-.08,4.75-1.46,7.86-3.87,8.76A4.42,4.42,0,0,1,308,226.81Zm-13.43-42.25a3,3,0,0,0-.95.19c-4.22,1.58-4.29,12.27-.15,23.35s11.21,19.09,15.43,17.51c2-.73,3.15-3.59,3.22-7.84a43.84,43.84,0,0,0-3.07-15.5C305.21,192,298.85,184.34,294.58,184.56Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "301.25px 205.182px",
                }}
                id="el9bnk8g7asad"
                className="animable"
              />
              <path
                d="M290.26,223.79a5,5,0,0,1-3.5-1c-2-1.64-2.27-5-.81-9.55a50,50,0,0,1,20.3-24.63c4.16-2.3,7.54-2.67,9.53-1s2.27,5,.81,9.54a44.93,44.93,0,0,1-8.13,14C302.16,218.75,295,223.55,290.26,223.79Zm22.08-36.21a13.61,13.61,0,0,0-5.6,1.9,49.16,49.16,0,0,0-19.84,24.07c-1.31,4-1.13,7.13.5,8.47,3.47,2.86,12.76-2.43,20.29-11.55a43.92,43.92,0,0,0,7.95-13.66c1.31-4,1.13-7.12-.5-8.46A4,4,0,0,0,312.34,187.58Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "301.268px 205.195px",
                }}
                id="elnc1jiehtugq"
                className="animable"
              />
              <path
                d="M310.68,215.07a51.56,51.56,0,0,1-10.95-.7h0a45.37,45.37,0,0,1-15.31-5.21c-4.07-2.45-6.08-5.2-5.65-7.74.86-5.15,11.42-7.53,24-5.42a44.86,44.86,0,0,1,15.31,5.21c4.07,2.45,6.08,5.2,5.65,7.73s-3.22,4.49-7.86,5.48A32.92,32.92,0,0,1,310.68,215.07ZM292,196.31c-6.75.34-11.7,2.27-12.21,5.28-.34,2.07,1.55,4.52,5.19,6.71a49,49,0,0,0,30.76,5.14c4.16-.89,6.74-2.59,7.08-4.66s-1.54-4.52-5.18-6.71a44,44,0,0,0-15-5.09A50.64,50.64,0,0,0,292,196.31Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "301.25px 205.175px",
                }}
                id="elr8fyyzrh74d"
                className="animable"
              />
              <path
                d="M305.11,203.75a4.1,4.1,0,1,1-5.27-2.4A4.1,4.1,0,0,1,305.11,203.75Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "301.272px 205.192px",
                }}
                id="el918cmnksper"
                className="animable"
              />
              <path
                d="M373.76,226.56l-.13.12-.05-.2s-.05,0-.08,0l-.08,0s0,0-.05-.06,0-.07-.06-.08h-.09l-.08.06a.22.22,0,0,0-.07.07H373l-.08,0s0,0,0,0l0,.06a.22.22,0,0,1,0,.09,0,0,0,0,0,0,0v0s0,0,0,0l-.16.05-.15.05-.2,0-.15,0L372,227l-.2-.09.06.23-.47,0-.06-.23,0,0-.06,0h-.09L371,227a.24.24,0,0,1-.1.07s0,0,0,.08a.3.3,0,0,0,0,.1s0,0,0,.05l0,0-.05,0h-.13l-.06,0h-.11a.36.36,0,0,0-.1.11c-.05.07-.09.11-.11.11l-.08,0-.08,0-.21,0-.23,0,0-.11,0-.13,0,0,0,0,0,0,0-.09,0-.08-.07,0-.05,0h-.08l-.1,0,0-.14a.27.27,0,0,1,0-.15.35.35,0,0,1,0-.12,1.48,1.48,0,0,0,0-.19,1.11,1.11,0,0,1,0-.17c0-.06,0-.09,0-.1l0-.12-.05-.21-.06-.2,0-.13s0-.08,0-.2a.82.82,0,0,0,0-.22s0-.1,0-.22-.05-.23-.08-.32l0-.08a.83.83,0,0,0,0-.15.88.88,0,0,1-.05-.15l0-.11-.1-.06-.08-.06,0,0s0,0,0,0a.08.08,0,0,1,0-.07,0,0,0,0,1,0,0,.52.52,0,0,0,0-.13.45.45,0,0,0,0-.14v0h0a.1.1,0,0,0-.09,0h-.1l-.08.36h0l-.06,0,0,0a0,0,0,0,1,0,0s-.05,0-.1,0a.19.19,0,0,1-.1,0l0-.1,0-.13a.63.63,0,0,1,0-.14,1.16,1.16,0,0,1,.07-.15l0-.09a.3.3,0,0,0,.06-.09.36.36,0,0,1,0-.11.22.22,0,0,1,0-.09s0,0,0,0a0,0,0,0,0,0,0l-.17-.66a1.86,1.86,0,0,0,0-.28,1,1,0,0,0,0-.26v-.07s0-.07-.11-.14l-.14-.14-.06-.11a.41.41,0,0,1-.05-.11s0-.07-.05-.13a2,2,0,0,0-.1-.21l-.09-.22-.06-.15,0-.14,0-.09.06-.08.08-.13.07-.13a.18.18,0,0,0,0-.08.32.32,0,0,0,0-.08.15.15,0,0,0,0-.07s.06,0,.14.07a.31.31,0,0,0,.15,0s0,0,.06-.07l.07-.13a.51.51,0,0,1,0-.09v-.08a.15.15,0,0,0,.05.08s0,.05.07.05a.1.1,0,0,0,.1-.05s.06-.06.08-.06,0-.08,0-.17a.64.64,0,0,0-.05-.2.56.56,0,0,0-.1-.13.61.61,0,0,0-.13-.14v-.06a1.13,1.13,0,0,0,0-.14c0-.07,0-.11,0-.14a.41.41,0,0,0,0-.17c0-.1,0-.17,0-.19l.08-.07a.82.82,0,0,0,0-.14.35.35,0,0,0,0-.12l.09,0v.06h0a.3.3,0,0,1,.08.08l0,.07.17,0,.2,0h0l.06,0a.43.43,0,0,1,.07-.07l.1-.11.1-.1s.05-.05,0-.07,0,0,.09-.06a.27.27,0,0,1,.07-.06h.07l.16.05.12.06a.1.1,0,0,1,.07.06s.06.07.08.07a.19.19,0,0,0,.09,0h.09s0,0,.06-.1l0-.15a.2.2,0,0,1,0-.11s0-.07,0-.08l0,0H370l.16.1.14.06h0l0,0,.07,0,.09,0a.27.27,0,0,1,.11-.08l.1-.07.08,0,.09,0-.07.11a.14.14,0,0,1-.07.08s0,0,0,0a0,0,0,0,0,0,0H371a.21.21,0,0,0,.08-.06l.07-.1h0l0,0,0,0v-.14c0-.08,0-.12,0-.13v0h.08a.05.05,0,0,1,0,0l.05,0a.62.62,0,0,1,.11-.22l.13-.18s0,0,.05,0l.07,0h.07s0,0,.05,0,.06,0,.12.08a1,1,0,0,0,.11.11l.15.1.15.09a1,1,0,0,0,0-.1.41.41,0,0,0,0-.1s0,0-.05,0a.09.09,0,0,0-.07,0,.09.09,0,0,1,0-.05.1.1,0,0,0,0-.07h.08a.19.19,0,0,0,.13,0h.21l.09,0s0,0,0,0l0-.07H373l.44.29.52-.43.36,1-.33.32s0,.05,0,.12a.52.52,0,0,1-.07.15s0,0,0,0h-.08l-.09,0h-.1l-.05.08,0,.07s0,.07,0,.08a.08.08,0,0,1,0,.07s0,0,0,0l-.08,0-.09,0h-.1l-.08,0-.09-.06s-.07-.06-.09-.06l-.22-.06-.14,0a.3.3,0,0,0-.1.14c0,.08-.06.13-.08.14h-.08l-.14.08-.24.12-.25.12-.14.06-.23.06-.07.06-.07.06a.28.28,0,0,1-.11.05l-.17.07-.18.07-.1,0-.06,0-.12.06-.13.07-.07,0h-.1l-.15,0h-.16l-.1,0-.08-.31a.12.12,0,0,0-.05,0l0,0s0,0,0,0l-.05,0,.12.5-.15,0-.17,0-.05,0s0,0,0,0l0,.19-1.06.27-.07,0s0,0,0,0l0,0,0,.08,0,.1.06,0s0,0,0,0a.33.33,0,0,0,.14,0h.11l.09.34s0,0,0,0h.07l.08-.07.06-.05.06,0h0l.07,0s0,0,0,0l.11.41h.08c.05,0,.08,0,.09,0h0v0s0,.1,0,.19V221l.06.05a.22.22,0,0,1,.07.06l.06,0,0,0v.08l-.07.1s-.06.09-.05.11l.08.31a.35.35,0,0,1-.05.13l-.05.15,0,.09,0,.06.14-.1.06.11a.48.48,0,0,0,.09.1l.07,0,.11,0,.15-.07a.39.39,0,0,0,.14-.08.13.13,0,0,1,0-.06l0-.06a.19.19,0,0,0,0-.09.41.41,0,0,1,0-.1s0,0,0,0,0,0,.05,0l.25-.06,0,0,0,0h.16s0,0,.07-.1a1.15,1.15,0,0,1,.07-.11l0,0h0l.14.06.13,0s0,0,.09,0l.08,0,.07.07.06.08.65-.17s0,0,0-.08a.54.54,0,0,0,0-.11l-.08-.31s0,0,0,0,0,0,.05,0l.15.07a.36.36,0,0,1,.13.05l.06,0s0,0,.05,0a.57.57,0,0,0,.07.13c0,.06.06.1.07.1l.08,0a.15.15,0,0,1,.1,0v.08s0,0,0,0l0,0a.22.22,0,0,0,0,.1c0,.06,0,.09,0,.09s0,0-.08,0-.05,0-.06,0v.11c0,.06,0,.1,0,.12l.1.05a.37.37,0,0,0,.11,0l-.07.08s-.06.06-.08.06a.2.2,0,0,1-.11,0l-.12,0a.15.15,0,0,0-.07.06l-.07.06-.33.08-.15.07-.16.1-.13.1-.22.16-.23.17-.11.09a.61.61,0,0,1-.16.09.92.92,0,0,0-.17.08,2,2,0,0,0-.23.14,1.46,1.46,0,0,0-.19.14.19.19,0,0,0-.07.11s0,0,0,.11v.3c0,.1,0,.16,0,.18a.16.16,0,0,0,0,.06l0,0a.28.28,0,0,0,0,.09l0,.11a.23.23,0,0,0,0,.09.33.33,0,0,1,0,.1h.1l.1,0,.29.17H371l0,0,.05-.05s0,0,.05,0h.06l.11,0,.21.06.2,0,.14,0,.08,0c.05,0,.08,0,.08,0a.22.22,0,0,1,.09.1l.07.13a.41.41,0,0,1,0-.12c0-.06,0-.09.05-.09a.6.6,0,0,1,.18,0h.15v-.12l-.12-.07-.14,0c-.08,0-.12,0-.15,0a.24.24,0,0,1,.06-.09l.09-.07.09,0,.11,0h.08a.25.25,0,0,1,.09-.07l.08-.09.08.3h.05s.07,0,.08,0A.38.38,0,0,0,373,225c0-.06.05-.1.07-.1l.08,0,.07,0s0,0,.06,0,0,0,0,0l0,.05a.14.14,0,0,0,0,.06.22.22,0,0,0,0,.08s0,.07,0,.08l0,.11,0,.06.1.06h.07l0-.06,0,0a1.39,1.39,0,0,0,0-.17.49.49,0,0,1,0-.16.19.19,0,0,1,.08-.08c.05,0,.07,0,.07-.06s0-.07,0-.15a.5.5,0,0,0,0-.15l.72-.18.12-.25.66-.17,0,.11a.38.38,0,0,0,.08.11.31.31,0,0,0,.13.06l.15.05.17,0s0,0,0,0l.05,0s0,0,0,.09,0,.08,0,.08h.26l.08.15.09.15.09.07.11.07.06-.1-.12-.11-.12-.1v-.06a2.54,2.54,0,0,0,0-.26c0-.11,0-.17,0-.19a.44.44,0,0,0,.06-.1.54.54,0,0,1,.06-.11l0,0,.08,0a.25.25,0,0,0,0,.09.51.51,0,0,1,0,.11s0,0,0,0l0,0h.05l.06,0a1.25,1.25,0,0,1,.07-.21,1.54,1.54,0,0,1,.09-.24c0-.07.07-.09.09-.07s0,.12,0,.3l.12.49c.06-.09.11-.14.14-.14s.07,0,.09.09a.62.62,0,0,1,0,.23,1,1,0,0,1-.1.29,1.31,1.31,0,0,0,0,.17c0,.08,0,.14,0,.16a.25.25,0,0,1-.05.11.51.51,0,0,1-.09.16,1.19,1.19,0,0,0-.09.15l-.06.09,0,.08.17,0-.09.37-.66.17s0,0-.09-.08a.27.27,0,0,0-.11-.05l-.31.08s-.06,0-.11-.05-.09-.06-.1-.06l-.08,0,.09.36-.16,0-.19,0a.62.62,0,0,1-.23,0l-.28,0s0,0-.05,0,0,0,0,0,0,0,0-.07a.39.39,0,0,1,0-.09s-.06,0-.13,0-.05,0-.09,0l-.07,0h-.05l-.05,0a.72.72,0,0,1,0,.17.58.58,0,0,1,0,.16l0,0s0,0,0,0l-.15.14-.13.12-.07-.07-.07-.08,0,0,0,0-.06,0Zm-6.7-7.37a.7.7,0,0,1,0-.14c0-.07.05-.12.07-.13s.06.06.09.14a.64.64,0,0,0,.1.16l0,.09h-.13l-.11-.06A.31.31,0,0,1,367.06,219.19Zm.33-.66v-.07a.11.11,0,0,0,0-.05l.18.08.19.09.08.1.06.11,0,.13h-.06s0,0,0,0l0,0-.05,0a.11.11,0,0,1-.07-.05s-.06-.06-.08-.06,0,0-.09-.07l-.09-.07s0,0,0,0Zm2.22,7.48s0,0-.06,0-.06,0-.05.06,0,0,.06,0S369.61,226,369.61,226Zm.47,1.25s-.06,0,0,.09,0,.08.08.06.06,0,0-.09S370.12,227.25,370.08,227.26Zm-1.08-5.9s-.05,0,0,.1,0,.08.08.07.06,0,0-.1S369,221.35,369,221.36Zm-.09-3.39,0-.09-.09,0Zm.07.66,0-.06,0-.07,0-.06,0-.08-.05,0a.09.09,0,0,1-.06,0Zm0-1.65s0,0,0,0a.07.07,0,0,1,0,.06s-.06,0-.07,0S369,217,369,217Zm.38.52a.07.07,0,0,0-.06.09.08.08,0,0,0,.1,0s.05,0,0-.09A.06.06,0,0,0,369.39,217.5Zm3.27,7.73a.07.07,0,0,0,0,.1c0,.06,0,.08.08.07s0,0,0-.09S372.69,225.22,372.66,225.23Zm-.69-2.92.1,0,0-.14-.09,0Zm-.36-5.5a.08.08,0,0,0,.05.05l.06,0a.5.5,0,0,0,0-.13l0-.06v-.08l-.1,0a.09.09,0,0,1,0,0,.07.07,0,0,0,0,.06v.05Zm1.5,5.21c.06,0,.09,0,.1.07a.07.07,0,0,1,0,.1s-.07,0-.09-.07S373.07,222,373.11,222Zm-.21-.79s0,0,0-.08,0-.08,0-.1l.21.07.17.08a1,1,0,0,1,.1.18,1.1,1.1,0,0,1,.08.19l-.12,0h-.14s0-.05-.06-.09a.5.5,0,0,1-.05-.07l-.05,0-.06,0a.2.2,0,0,0,0-.08A.31.31,0,0,1,372.9,221.23Zm-.72-3.43h0l0,0,.06,0a.19.19,0,0,0,0-.09.17.17,0,0,0,0-.1s0-.06-.1,0l0,0a0,0,0,0,0,0,0l0,0v.1Zm0-1.13s0,0,0,0-.05,0,0,0a0,0,0,0,0,0,0A.06.06,0,0,0,372.17,216.67Zm1.15,4.18-.07,0,0-.08Zm-.75-2.9a.07.07,0,0,1,.1.06c0,.06,0,.1-.06.11s-.05,0-.06-.07S372.54,218,372.57,218Zm.94,2.94s0,0,0,0a.08.08,0,0,1,0,0s0,0,0,0S373.48,220.88,373.51,220.89Zm.1.4a.11.11,0,0,1,.05.08s0,.07,0,.09,0,0,0-.07S373.59,221.29,373.61,221.29Zm-1-4.79s0,0,0,.09,0,.07.06.07a.08.08,0,0,0,.06-.1A.08.08,0,0,0,372.63,216.5Zm1.3,4.28s0,0,.05.08,0,.08,0,.09-.08,0-.1-.07A.08.08,0,0,1,373.93,220.78Zm1.4,5.47.08,0a.1.1,0,0,0-.05-.07l-.07-.07Zm.57-1.43s0,0,0,0,0-.05,0-.05v0h0s0,0,0,0v0l-.09,0Zm.28.79s-.05,0,0,.09,0,.08.08.07a.07.07,0,0,0,0-.1S376.22,225.6,376.18,225.61Zm-.27-1.73a0,0,0,0,1,0,.07s0,0-.08,0,0,0,0-.06S375.89,223.87,375.91,223.88Zm-1.8-6.68.14-.14a.87.87,0,0,0,.11-.17.08.08,0,0,0,.05,0l0-.05a.2.2,0,0,0,0-.1v-.1l-.13-.11-.09-.11a.18.18,0,0,1-.05-.11c-.13.1-.16.11-.09,0a1.58,1.58,0,0,1,.29-.3l-.28,0s0,0,.07-.06a.18.18,0,0,0,.08-.07.51.51,0,0,1,0-.14.87.87,0,0,1,.06-.13.22.22,0,0,1,0-.09.22.22,0,0,0,0-.09l.24-.06v.06a.7.7,0,0,1,0,.14.37.37,0,0,0,0,.14s.05.06.13.07h.06l0-.05s0,0,0-.07a.16.16,0,0,1,0-.08.32.32,0,0,0,0-.08l.06.06.07.06c0,.06,0,.09.09.08a.58.58,0,0,0,.12-.08l.1-.08.06.09s0,.06.06.06l.08,0h.07l.15-.14c.08-.08.12-.12.14-.12l.28.05.27.07-.4.11a.91.91,0,0,1,0,.16,1.87,1.87,0,0,1,0,.25q0,.13,0,.24c0,.08,0,.13,0,.16s0,.05,0,.1a.19.19,0,0,1,0,.1s0,0,0,0a.07.07,0,0,1,0-.05s0,0,0-.11a.61.61,0,0,0,0-.14.6.6,0,0,1-.08-.13.45.45,0,0,1-.07-.15.3.3,0,0,1-.07-.08c0-.05-.06-.07-.09-.07h0a.45.45,0,0,0,0,.14c0,.07,0,.12,0,.13a.54.54,0,0,1-.08.16.81.81,0,0,1-.09.14l0,.15.05.19.05,0,.05,0-.06.1-.07.11-.05,0h0l-.11-.07-.11-.06-.07,0s-.06,0-.05.05a.53.53,0,0,0-.06.16l0,.15h-.09s0,0-.05,0l0,0-.17,0c-.08,0-.13,0-.15,0h-.08a.15.15,0,0,1-.1,0l-.06,0A.13.13,0,0,1,374.11,217.2Zm0-.76s0,0,0,.07,0,0,.05,0,0,0,0-.06S374.12,216.45,374.08,216.44Zm2.06,7.38a.08.08,0,0,1,.1.07c0,.06,0,.09-.06.11s-.05,0-.06-.08S376.11,223.83,376.14,223.82Zm-1.51-7.27s0,0,.05,0l.07,0s0-.08,0-.18,0-.16,0-.21v-.06l-.07,0-.06,0a.75.75,0,0,0,0,.17.93.93,0,0,1-.05.2v0Zm2.16,8,0-.09-.08-.07Zm.23-.06.06-.1-.11.15Zm.46.75s0,0,.07.06,0,.09,0,.09a.07.07,0,0,1-.1-.05C377.41,225.32,377.43,225.29,377.48,225.28Zm-.06-1.54s0,0-.06-.06-.06-.07-.06-.09,0-.05-.06-.12l0-.12.17,0,.16,0a.2.2,0,0,0,.08,0s.06,0,.06,0,.07.18,0,.23-.11.08-.12.07v.05l0,.06.19.05a.39.39,0,0,1,.14,0l.08,0a.38.38,0,0,0-.09.09l-.08.08v0a.79.79,0,0,1,0,.13c0,.05,0,.07,0,.07a.23.23,0,0,1-.12,0h-.11Zm-1.83-7.43c.06,0,.09,0,.1.06a.08.08,0,0,1-.05.11s-.07,0-.09-.07S375.55,216.32,375.59,216.31Zm.09-.27c.05,0,.08,0,.1.07s0,.1-.06.11,0,0-.05-.08S375.65,216,375.68,216Zm2.46,6.82v0a.06.06,0,0,1,0,.06V223a.11.11,0,0,0,0,.06s0,0,0,0v.07l0,.13,0,.13,0,.08a.31.31,0,0,1-.05.11.31.31,0,0,1-.08.1.48.48,0,0,0-.06-.14l-.11-.2-.11-.21-.07-.12,0-.11Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "372.597px 221.445px",
                }}
                id="elubsak86grl"
                className="animable"
              />
              <path
                d="M382.34,224.06a0,0,0,0,0,0,0s0,0,0,0v0l-.05-.09a.38.38,0,0,0-.09-.09s0,0-.08-.09l-.08-.08v-.05l-.07-.12-.06-.08-.12-.09a1.06,1.06,0,0,1-.13-.11l0-.06,0-.09,0-.11v-.07l0-.11.05-.12a.07.07,0,0,0,0-.05v0a.41.41,0,0,1,.16-.09l.15-.07.1-.06.09,0,.19-.05.09-.06.09-.06.05-.08.05-.07a.47.47,0,0,1,.15-.08.61.61,0,0,0,.16-.09l.08-.05.09-.06.23-.06.16,0,.09.09c0,.05.08.08.1.08l.06,0,.08,0,0-.07a.16.16,0,0,0,0-.09.25.25,0,0,0,0-.1.34.34,0,0,1,0-.11l.11-.09.08-.1a.28.28,0,0,1,.09-.08l.14,0,.05,0s0,0,0,0l.06,0,0-.05.1-.06.09-.06s0,0,.1-.11l.13-.09.12,0a.24.24,0,0,0,.13,0l0,0a.06.06,0,0,0,0-.05h.06l.08,0,.07,0,.14,0,.06,0h.1l.32-.09.3-.07.16-.1.15-.13s0,0,0-.05v-.08l.07,0,.07,0,0,0h0A.28.28,0,0,1,387,220a.18.18,0,0,0,0-.08s0,0,0,0,0,0,0,0,0,.07.05.14a1.11,1.11,0,0,0,.07.13.23.23,0,0,0,.07.08s.07,0,.08,0l.79-.2c0-.05,0-.13.05-.24a.51.51,0,0,1,.06-.2s0-.05,0-.09h.08a.85.85,0,0,0,.23,0l.18,0h.2l.35-.09a.09.09,0,0,0,0,.06l0,0,.08.16a1,1,0,0,1,.07.17l.13.06c.07,0,.11.07.11.08l.12.08c.06,0,.09.07.09.08l-.05.14s0,0,.1.05h.11l.11.42c.05,0,.08,0,.08.06s0,0,.06.06l.05,0h0l0,0a.9.9,0,0,1,0-.15c0-.07,0-.12,0-.15a.44.44,0,0,1,.05-.09l.06-.07h.05l.08,0v.14c0,.08,0,.12,0,.15a.61.61,0,0,0,0,.15.87.87,0,0,0,0,.13.68.68,0,0,1,0,.12c0,.05,0,.09,0,.1a.14.14,0,0,1-.08.07l-.1,0a.25.25,0,0,1-.08-.05l-.09-.06h-.13l-.11,0a.33.33,0,0,0-.06-.08s0,0,0,0l0,0h-.08l-.06,0s0,.05-.05.05h-.28a.06.06,0,0,1-.08-.05s0,0,.07,0,0,0,0-.06l0-.08s0-.05,0-.07l0,0,0,0-.08,0-.06,0a.85.85,0,0,0,0,.13c0,.08-.05.14-.07.18v.07l0,.07,0,0a.08.08,0,0,0,0,0l-.08-.07c-.05,0-.08,0-.1,0h0l0,0a.58.58,0,0,1,0,.1c0,.05,0,.09,0,.1s0,.07-.08.13l-.06.14a.17.17,0,0,0-.09-.06l-.08-.05h-.1l-.05,0,0,0,0,.08-.05.1,0,.07a0,0,0,0,0,0,0s0,0,0,0h-.06l-.08.07-.09.05s0,0-.08,0l-.08,0-.12,0-.14,0-.05,0,0-.05c0-.06,0-.08-.09-.07h-.06s0,0-.05,0l-.05.06a.31.31,0,0,0-.1.05l-.1,0-.15.06-.25.12-.25.11-.14.06a.79.79,0,0,0-.15.11.45.45,0,0,1-.14.1.29.29,0,0,1-.14,0l-.13,0-.1.18-.08,0-.11,0a.14.14,0,0,0-.06.07l0,.07-.14.05-.15,0-.17,0a.25.25,0,0,1-.12,0h-.36l-.07.09-.08.08h-.14l-.08,0h-.27l0,0,0,0a.13.13,0,0,0,0,.08l0,.08s0,0,0,0a0,0,0,0,0,0,0l0,0a.53.53,0,0,0-.15,0l-.13.06-.1,0h-.06l-.07,0H383a.14.14,0,0,0-.06.07.26.26,0,0,0,0,.1l-.64.16s0-.08,0-.16,0-.13,0-.15v-.06A0,0,0,0,0,382.34,224.06Zm-.74-3.95a.68.68,0,0,0,0-.12l0-.19a1.18,1.18,0,0,0,0-.2,1.25,1.25,0,0,1,0-.15l-.05-.06-.07-.11a1,1,0,0,1-.08-.11s0-.06,0-.07a.31.31,0,0,1,0-.11c0-.05,0-.09,0-.11l.06,0,.05-.05.08-.08a.22.22,0,0,1,.1-.08l0,0h0l.15.08s.07,0,.17,0l.19,0h0s0,0,0,0a.49.49,0,0,1,0-.12.53.53,0,0,1,0-.11s.06,0,.12,0h.13a.08.08,0,0,0,0,0l0-.08a.28.28,0,0,1,0-.11s0-.08,0-.09l.06,0h.07l.08,0a.14.14,0,0,0,.09,0s0,0,0,0h.11l.05,0,.06,0s0,0,.05,0a.58.58,0,0,1,0-.1.15.15,0,0,1,0-.08s0,0,0-.07h.08l0,.06.06.06.07.07a.26.26,0,0,1,0-.1.31.31,0,0,0,0-.09.15.15,0,0,0,0-.07.13.13,0,0,1,0-.08l0,.07.05,0h.05s0,0,0,0,.06,0,.05-.08v-.07s0,0-.08,0l.11-.11.11-.09h.07s0,0,.05.06,0,.06.05.06a.06.06,0,0,1,0,0h.09l.23-.13.19-.11s0,0,.06-.05.06,0,.06,0l.07,0,.08,0L385,217a1.12,1.12,0,0,1,.09-.17l.06,0h.08l.05,0,.07-.06h.13l.08,0h.06l.08-.06.15-.12.14-.12a.54.54,0,0,0,.08-.08l.16-.08.21-.1.08,0h0l.09,0h.07a.69.69,0,0,0,.23,0,.92.92,0,0,0,.22-.07s0,0,0,0l.05,0h.08a.56.56,0,0,0,0-.07V216l0,0s0,0,0,0l0,0h0s0,0,0,0l0,0,.05.06s.05,0,.05.06h.06l0-.06a.14.14,0,0,0,0-.06l.25-.06s0,0,0,.08a.2.2,0,0,0,0,.11h0v0s0,0,.07.06l.09,0h.08s.07,0,.08,0a.25.25,0,0,0,0,.09l.07.1,0,0h.05l0,.06.05.06.11-.1s0,0,0,.07l.07.05.07,0,.08,0s0,.06,0,.13,0,.11,0,.13v0l0,0h0a.13.13,0,0,0,.08,0,.23.23,0,0,0,.07-.07l.17.24a.34.34,0,0,0,.06-.06l.07,0h.06l0,.14a.36.36,0,0,0-.13,0,.54.54,0,0,0-.13.06l-.15.26v.07s0,.07,0,.08l-.14,0c-.08,0-.11,0-.11,0l-.11.06-.09.06s0,0,0,.07v.07l-.05.08h-.06l-.07,0h-.07a.52.52,0,0,0-.21,0h-.2l-.13-.06-.12-.08,0,0,0,.05h-.06l-.06,0-.12,0h-.1l-.14.24,0,0H387l-.07,0-.06,0-.08,0,0,.06,0,0h-.16l-.07,0a.12.12,0,0,0-.08,0h-.11l-.08,0a.28.28,0,0,0-.09.08c0,.05-.06.08-.08.08l-.09,0-.15,0-.14,0-.08,0-.14,0-.24.09-.23.08-.15.06-.2.05-.12.06c-.06,0-.1.05-.11.07s0,0-.06,0l-.05,0a.14.14,0,0,1-.1,0l-.11,0s0,0-.08.06l-.1.06-.19,0-.19,0a.65.65,0,0,0-.09.11l-.08.09-.09,0-.19.06-.2.07-.14.05-.41.1s0,0-.05.06l-.05,0-.13,0-.17,0-.14,0a.11.11,0,0,1-.08,0Zm.33-1.1.08,0,0-.08-.08,0Zm.28,0,.06,0v0h-.06Zm.62-.58s0,0,0,0a.52.52,0,0,0,0,.11.23.23,0,0,0,0,.1s0,0,0,0a.2.2,0,0,0,0-.13A.23.23,0,0,0,382.83,218.41Zm1.51,3.23,0-.08-.06,0Zm.77,0s0,0,0,0,.05,0,.06,0l0,0s0,0,0,0l0,0,0,0v0l0,0v0s0,0,0,0l0,0h-.05a.2.2,0,0,0,0,.08A.11.11,0,0,0,385.11,221.66Zm0-.49s-.05,0,0,0,0,0,.05,0a0,0,0,0,0,0,0S385.09,221.17,385.07,221.17Zm.42.11s0,0,0,.07,0,.06.06.05a.06.06,0,0,0,.05-.08A.05.05,0,0,0,385.49,221.28Zm.13-.59s-.05,0,0,.08a.06.06,0,0,0,.08,0s0,0,0-.08S385.66,220.68,385.62,220.69Zm2.42-1.11a1.24,1.24,0,0,0-.1.17c-.05.09-.09.14-.11.14l0,.05,0,0v-.05s0,0,0,0a.69.69,0,0,0,.07-.13s.06-.1.08-.12l.06,0Zm-.05.36,0,.06-.08-.05Zm-.75-3.21s0,0,0,0,0,0,0,0,0,0,0,0S387.26,216.73,387.24,216.73Zm1.2,3.64s0,0,0,.08,0,.07.05.06,0,0,0-.09A.06.06,0,0,0,388.44,220.37Zm-.74-4.24s0,0,0,0,0,0,0,0a.06.06,0,0,0,.05,0S387.71,216.15,387.7,216.13Zm.58-.2h0l0,0s0,0-.05,0S388.26,215.92,388.28,215.93Zm1.52,5.14s0,0,0,0,0,0,0,0l.05,0S389.81,221.09,389.8,221.07Zm-.16-1.41,0,.07h-.05l0-.06Zm.58,1.44V221h0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "385.954px 219.993px",
                }}
                id="elmiuqahj3g1i"
                className="animable"
              />
              <path
                d="M396.7,220s0-.08,0-.19a1.11,1.11,0,0,0,0-.18s-.05-.06-.12-.1a.32.32,0,0,1-.13-.09.26.26,0,0,1,.07-.12l.08-.09,0-.15a1.76,1.76,0,0,0-.37-.7,2.37,2.37,0,0,1-.42-.7c-.15-.35-.28-.69-.4-1s-.22-.68-.32-1.06l-.26-1a1.51,1.51,0,0,1,0-.37.58.58,0,0,1,.09-.33,1,1,0,0,1,.27.21c.1.11.19.14.27.1l.1-.1a.72.72,0,0,1,.11-.12l.11,0a.48.48,0,0,1,.29,0,2.21,2.21,0,0,1,.29.13,1.35,1.35,0,0,1,.1.28c0,.13.08.27.13.42a2.65,2.65,0,0,0,.18.42.76.76,0,0,0,.22.29h0l.08,0,.1,0s.06-.06,0-.13a.51.51,0,0,0-.22-.28.58.58,0,0,1-.23-.3.35.35,0,0,1,0-.15,2,2,0,0,1,.1-.21,1,1,0,0,1,.11-.2c0-.06.07-.09.1-.09h0l0,0,.05,0h0c.07,0,.1-.05.11-.1a.43.43,0,0,0,0-.16l0-.17a.23.23,0,0,1,0-.14l.13-.17.15-.18.05,0c.06,0,.1,0,.13.09s.09.09.22.06a.47.47,0,0,0,.3-.25,2,2,0,0,0,.16-.4c0-.13.07-.26.09-.38s.07-.18.12-.19a.49.49,0,0,1,.24,0l.28.07.27.08a.53.53,0,0,0,.24,0,.84.84,0,0,0,.23-.05l.21-.06.14.06.27.15.27.15.14.08.32,1,0,.11a2,2,0,0,1,.32.18.44.44,0,0,1,.15.28.34.34,0,0,1,0,.22.44.44,0,0,0,0,.27,1.16,1.16,0,0,0,.14.34,1.2,1.2,0,0,1,.13.33l0,.07a.27.27,0,0,1,0,.08s0,0,0,0a.41.41,0,0,0,.06.1s.06.06.08.06a.85.85,0,0,0,.22-.13s0-.1,0-.2-.05-.1-.09-.11a.15.15,0,0,1-.08-.11s0-.08.07-.18l.2-.34c.08-.12.15-.23.21-.34a1.33,1.33,0,0,0,.12-.22c0-.08.08-.2.16-.37s.18-.34.29-.51a2.8,2.8,0,0,1,.33-.47.72.72,0,0,1,.31-.25l.09,0,.09,0,.1,0a.41.41,0,0,0,.18-.13.89.89,0,0,0,.16-.22,1.09,1.09,0,0,0,.11-.27.54.54,0,0,0,0-.25.44.44,0,0,0,.19,0l.22,0a.18.18,0,0,0,.05.07l0,0a.1.1,0,0,0,.07-.06.25.25,0,0,0,.05-.08v0l0,0-.08,0a.92.92,0,0,1,0-.56,1,1,0,0,1,.46-.36.71.71,0,0,1,.28.11l.3.2a2.11,2.11,0,0,0,.27.18.27.27,0,0,0,.22.05s0,0,.07-.07,0-.06,0-.07l.08,0a.18.18,0,0,1,.19.08,1.58,1.58,0,0,1,.18.27,2.39,2.39,0,0,1,.16.32,2.27,2.27,0,0,1,.08.22,2.93,2.93,0,0,1,.09.84,2.93,2.93,0,0,0,.09.84s.06.07.14.08l.14,0-.05.14a.38.38,0,0,0,0,.14l0,0s0,0,0,0a0,0,0,0,0,0,0l.09.07c.05,0,.08.05.1,0a.23.23,0,0,1,0,.11c0,.06,0,.09,0,.11l.11.44a3.09,3.09,0,0,0,.08.3,2.17,2.17,0,0,0,.07.24l.11.23a2.63,2.63,0,0,0,.18.3s0,.06.11.11l.11.1a.68.68,0,0,1,0,.12.13.13,0,0,1-.1.11.88.88,0,0,1,0,.15,1.64,1.64,0,0,0,0,.18.67.67,0,0,0,.16.29,1.84,1.84,0,0,0,.22.17l.21.13a.35.35,0,0,1,.13.18l-.11,0a.68.68,0,0,0-.19-.05.55.55,0,0,0-.2,0l-.14.1c-.08.07-.12.11-.13.14l-.09,0a.59.59,0,0,1-.58-.22,3.75,3.75,0,0,1-.57-.78,9.51,9.51,0,0,1-.54-1.06c-.17-.38-.33-.74-.47-1.07s-.28-.61-.39-.83-.2-.31-.26-.3h0a1.25,1.25,0,0,1-.17.28c-.09.14-.16.24-.19.3a1.78,1.78,0,0,0-.13.21,2.12,2.12,0,0,0-.1.22l-.08,0a.25.25,0,0,1-.15,0,.3.3,0,0,0-.19,0,1.69,1.69,0,0,0,.11.32,1.06,1.06,0,0,1,.1.26.74.74,0,0,1,0,.26,1,1,0,0,1-.12.33h-.09s0-.05-.08-.12l-.09-.13,0-.1c0-.05,0-.07-.08-.06a.25.25,0,0,0-.08.05s-.05,0,0,.07l.1.4.17.08a.13.13,0,0,1,0,.06.08.08,0,0,1-.05.05.21.21,0,0,1-.13,0h-.12a.33.33,0,0,0-.17.11.23.23,0,0,0-.06.18,0,0,0,0,0,0,0s0,0,0,0h.12a.51.51,0,0,1,.16,0l0,0,0,0c0,.22,0,.36-.12.44a3.05,3.05,0,0,1-.44.19.61.61,0,0,0-.11-.23.16.16,0,0,0-.18-.08.3.3,0,0,0-.22.26.84.84,0,0,0,0,.35.32.32,0,0,0,.19.22.38.38,0,0,1,.2.27.61.61,0,0,1,0,.55,1,1,0,0,1-.36.28l-.43.25a.59.59,0,0,0-.25.43h0l-.05,0-.11-.07-.12-.06-.05,0s0,0,0,.06,0,0,0,.07h-.08l-.07-.08,0-.05s0,0,0-.12a.43.43,0,0,1,0-.11V219a.06.06,0,0,0,0-.06c0-.15-.08-.32-.14-.53s-.12-.4-.19-.6-.16-.4-.24-.58a1.59,1.59,0,0,0-.26-.44l-.21-.23a.93.93,0,0,1-.16-.18,1.1,1.1,0,0,1-.14-.21,1.83,1.83,0,0,1-.12-.34l-.11-.42a.65.65,0,0,1,0-.1s0-.09,0-.1a.85.85,0,0,0-.13-.25.63.63,0,0,0-.12-.15l-.23.06a.26.26,0,0,0,0-.17l0-.17-.05-.17-.07.07a.23.23,0,0,1-.08.07s-.07,0-.1,0l-.1-.15a1.63,1.63,0,0,0-.11-.17.18.18,0,0,0-.15-.07c-.06,0-.13.05-.2.17a2.2,2.2,0,0,0-.18.41c0,.16-.1.31-.14.47a2.49,2.49,0,0,1-.1.3,1,1,0,0,1-.09.17c0,.08-.07.12-.06.13l-.12.08c-.07,0-.11.07-.12.1h0l0,.05.1,0,.08,0-.23.39a.4.4,0,0,1,.15,0,.13.13,0,0,1,.1.1.17.17,0,0,1,0,.18.19.19,0,0,0,0,.19c0,.08.06.11.1.09s.08,0,.09.07v0a.56.56,0,0,1-.17.24.31.31,0,0,0-.1.32.24.24,0,0,0,.09.15.38.38,0,0,1,.09.15.87.87,0,0,1-.07.71,1,1,0,0,1-.59.36s0,.09.05.18a1.73,1.73,0,0,1,.08.27,2,2,0,0,0,.08.26c0,.08,0,.14.05.17s0,0,0,.1a.61.61,0,0,1,0,.14.33.33,0,0,1,0,.15.12.12,0,0,1-.09.08h-.24a.39.39,0,0,0-.23.16.89.89,0,0,1-.17.17h0a.47.47,0,0,1-.45-.06,1,1,0,0,1-.23-.44Zm-1.08-4.85c.06.23.12.44.17.64s.11.29.14.28,0-.06,0-.17a2.91,2.91,0,0,0-.12-.35l-.13-.31C395.63,215.15,395.62,215.12,395.62,215.15Zm1.45,5s0,0,0,.09,0,.09.06.07a.12.12,0,0,0,0-.1S397.1,220.19,397.07,220.19Zm.05-.48a.62.62,0,0,0,.12.2.22.22,0,0,0,.24.07s.07-.06.06-.16a1.23,1.23,0,0,1,0-.18s0,0,0-.1a.36.36,0,0,1,0-.11.48.48,0,0,1-.13.09l-.12.07s0,0-.05.06A.39.39,0,0,0,397.12,219.71Zm.54-2.7.1,0,0-.12-.1,0Zm-.37-2.3a1.49,1.49,0,0,0,.16.35.57.57,0,0,0,.25.26,1.12,1.12,0,0,1,.12-.17.5.5,0,0,0,.11-.16h-.16a.5.5,0,0,0-.19-.23A.37.37,0,0,0,397.29,214.71Zm.25-.85a.59.59,0,0,0,.06.16.26.26,0,0,0,.13.12.1.1,0,0,1,0-.06l0-.06v0l0-.06a1.65,1.65,0,0,0-.08-.17,1,1,0,0,1-.09-.17.14.14,0,0,0-.08.13Zm.68-.19s0,0,0,.05a.18.18,0,0,0,.06.09l.09.07a.11.11,0,0,0,.1,0s0,0-.07-.13S398.25,213.66,398.22,213.67Zm0-.13.09,0,0-.11-.09,0Zm3.6,2.57a.38.38,0,0,1,0,.16.74.74,0,0,0,0,.12h.66L402,216a.24.24,0,0,0-.09.06A.12.12,0,0,0,401.86,216.11Zm-.25-.86s0,0,0,.05,0,.06.05.06A.13.13,0,0,0,401.61,215.25Zm.65.28a.08.08,0,0,1,.1.06.08.08,0,0,1-.06.1s-.07,0-.08-.07S402.22,215.54,402.26,215.53Zm1-1.89s0,0,0,.1,0,.08.07.07.07,0,.06-.1A.08.08,0,0,0,403.26,213.64Zm.38.84s0,0,0,0,0,.05,0,0,0,0,0,0S403.67,214.47,403.64,214.48Zm-.11-1.29s0,0,0,.07a.77.77,0,0,0,.09.13l.13.13.1,0a.27.27,0,0,0,.09-.21.28.28,0,0,0-.08-.15.29.29,0,0,0-.16-.07A.24.24,0,0,0,403.53,213.19Zm.57,1.53s0-.06-.07,0-.06,0,0,.07a.06.06,0,0,0,.07,0S404.11,214.75,404.1,214.72Zm.81-1.22c-.05,0-.06.05-.05.1s0,.07.09.06.05,0,0-.1S405,213.49,404.91,213.5Zm.72-.22-.11.05-.15.07-.11.06s0,0,0,0a.18.18,0,0,0,.08,0l.14-.07.13-.06Zm1.32-.52a.61.61,0,0,0,.28-.1.19.19,0,0,0,.08-.15s-.05-.08-.12-.11a.7.7,0,0,0-.31,0s0,0,0,.07,0,.08.05.13l0,.12S407,212.76,407,212.76Zm.66,1.88a.21.21,0,0,0,.06.08.14.14,0,0,0,.09.05l-.08-.33a.31.31,0,0,0-.05.1A.24.24,0,0,0,407.61,214.64Zm1.16,2.72,0-.08-.07,0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "402.049px 215.641px",
                }}
                id="eliea5o236l3o"
                className="animable"
              />
              <path
                d="M415.3,216.07a.58.58,0,0,1-.07-.14.52.52,0,0,0-.07-.15.11.11,0,0,0-.09-.05h-.26s-.05,0-.05-.09l-.14.19-.13.18-.06-.21a.18.18,0,0,0-.11.1.33.33,0,0,0-.05.16,1,1,0,0,0,0,.16s0,.07,0,.07-.11,0-.13-.12a.34.34,0,0,1,0-.2.3.3,0,0,0,0-.21c-.05-.17-.14-.24-.27-.2l0,0s0,0,0,.06a.56.56,0,0,1,0,.08l-.18,0-.18.05s0,0,0,.07l0,.08-.19.17L413,216h-.15a.51.51,0,0,0-.18.09.3.3,0,0,1-.17.08c-.06,0-.09,0-.11,0s-.06,0-.13,0h-.06a.79.79,0,0,0-.08.13.59.59,0,0,0-.05.14l-.06-.07a.2.2,0,0,1-.05-.09.49.49,0,0,0,0-.12.37.37,0,0,0,0-.14V216c0-.08-.06-.12-.12-.1a.08.08,0,0,0-.07.09l0,.14q-.12,0-.12-.12c0-.1,0-.16-.06-.17h-.27a.1.1,0,0,1-.06-.08.23.23,0,0,1,0-.14.49.49,0,0,0,0-.12l0-.08s0,0-.06,0h-.06c-.08,0-.12,0-.13.08a.32.32,0,0,0,0,.09s0,.07,0,.1-.05,0-.11.07l0-.13-.05-.05-.05-.06a.2.2,0,0,1,0-.14l0-.06a.33.33,0,0,0,0-.1.31.31,0,0,1,0-.09.13.13,0,0,0-.07,0s-.05,0-.08,0,0,0-.06-.06a.15.15,0,0,0-.08,0l-.06,0h-.06a.24.24,0,0,0,0,.07.09.09,0,0,0,0,.08s.06.08.09.08a.22.22,0,0,0,.11,0h.08s0,0,.06.08l-.39.1a.53.53,0,0,0-.12-.25.33.33,0,0,0-.14-.1l-.13-.06a.19.19,0,0,1-.08-.11v0l0,0,0-.05v0s-.05-.09-.1-.09a.13.13,0,0,1-.1-.1s0,0,0-.05,0,0,0,0a.18.18,0,0,1,0-.06.18.18,0,0,0,0-.06s0,0-.09,0-.09,0-.1-.06,0,0,0,0v0a.17.17,0,0,1,0-.17.69.69,0,0,1,.17-.1.82.82,0,0,0,.19-.09.2.2,0,0,0,.11-.13c0-.05-.08-.07-.13-.05s0,0,0,0h0l-.07.07c0,.05-.06.08-.07.08h-.08l-.06,0-.06,0,0,0a.16.16,0,0,1,0-.06l0-.07a.42.42,0,0,1,0-.22c0-.07.06-.15.1-.23l.11-.21a.5.5,0,0,0,0-.14.31.31,0,0,0-.12-.19.34.34,0,0,1-.11-.18.35.35,0,0,1,0-.26.48.48,0,0,0,.06-.3l-.06-.24a.09.09,0,0,1,0-.06.09.09,0,0,0,.06,0,.11.11,0,0,0,.05-.08.44.44,0,0,0,0-.18l.11,0,.08.3.07-.12a.36.36,0,0,1,.09-.09l.21,0,.15,0,.08,0s0,0,0,0v0a0,0,0,0,0,0,0l-.22-.11a.39.39,0,0,0-.25,0l-.05-.2h.08a.08.08,0,0,1,.06,0h.06a.26.26,0,0,1,0-.07.18.18,0,0,0,0-.06h.06a.51.51,0,0,0,.09.18.27.27,0,0,0,.19.13l.05,0,0,0-.13-.51-.24-.07q-.18-.06-.18-.09v0a1.3,1.3,0,0,1,.08-.29c0-.08.12-.13.25-.17l.09,0a.09.09,0,0,1,.06,0l0,0s0-.06.06-.13l.06-.25c0-.09.05-.17.07-.24s.06-.11.09-.11l0,.15.06.28c0,.1,0,.2.05.29a.63.63,0,0,0,0,.15s0,0-.08,0l-.09,0a.28.28,0,0,0,0,.11.22.22,0,0,1,0,.09s0,0,0,0,0,0,0,0l.33-.09,0,0a.11.11,0,0,1,0,.05s0,0,0,0,0,0,0,0h.22l0-.1-.09-.37.2-.05.18.05.13,0,.05,0-.08-.06-.13-.1-.14-.1-.07,0s0-.06.05,0l.1,0s.05,0,.07,0,0-.09,0-.21a.39.39,0,0,0-.09-.17.63.63,0,0,1-.1-.23h.11c.07,0,.11,0,.11,0h.06l.06,0h.05a.06.06,0,0,0,0-.07s0-.05,0-.06l.11-.15A.46.46,0,0,1,412,209a.57.57,0,0,0,.13-.08l.09-.06.05,0h.46s0,0,0,0a1.17,1.17,0,0,0-.05.11l-.05.09s0,0,0,0a.42.42,0,0,0,.18-.11.81.81,0,0,0,.1-.18c0-.06.05-.12.08-.17a.18.18,0,0,1,.11-.1l.12,0,.15.1a.83.83,0,0,0,.16.09.15.15,0,0,0,.12,0s0,0,0,0,0-.05,0-.1a.33.33,0,0,0,0-.1s0,0,0,0h-.15s-.05,0-.06-.1,0,0,.06-.06l.14,0h.06l.41.25s0,0,.06,0l0-.08v-.18l.08,0a.43.43,0,0,0,.15,0,.4.4,0,0,1,.16,0s0,.07.06.17a1,1,0,0,0,.07.16.05.05,0,0,0,0,0l0,0,0-.07a.18.18,0,0,0,0-.06l-.15-.59.06,0,.39.76.27-.37v0c0-.06-.05-.07-.13-.05a.14.14,0,0,1-.16,0c0-.05,0-.08.12-.1l.13,0,.14,0s0,0,.08.09a.91.91,0,0,1,.1.13v0a.23.23,0,0,1,0,.16,1.09,1.09,0,0,1-.08.15s.06-.06.08,0l.08,0,.06.09s0,0,.06,0,0,0,.06,0,.05,0,.05-.05,0,0,0,.05a.19.19,0,0,0,.1,0l.11,0,.08,0a.42.42,0,0,0,.06.14.42.42,0,0,1,.06.14l.12.47c0,.05,0,.08.06.08h.2a.15.15,0,0,1,.09,0s0,0,0,0l0,.05h0l-.28,0a1.19,1.19,0,0,1-.25-.07.12.12,0,0,0-.07.11.74.74,0,0,1,0,.12.52.52,0,0,1-.25,0,.69.69,0,0,1-.23-.09.11.11,0,0,0,.05-.11l0-.1,0-.07a.17.17,0,0,1,0-.05l-.1,0-.13-.06h0a.27.27,0,0,0-.18.13l-.13.2,0,0h0s0,0-.08-.05l-.06,0a.24.24,0,0,0-.15,0l-.15,0-.08,0s-.06,0-.07,0h0a.15.15,0,0,0-.08.08l-.07.15c0,.06-.05.11-.07.17a.22.22,0,0,1-.11.12.42.42,0,0,0-.14.06l-.23.1-.23.09-.14,0-.1.06-.1.06-.41.11a.34.34,0,0,1,0,.12.28.28,0,0,1-.11,0q-.14,0-.15-.06t-.15-.06c-.05,0-.09,0-.1.06a.27.27,0,0,1-.11.06.16.16,0,0,1-.12,0,.1.1,0,0,0-.12,0,.57.57,0,0,0,.1.22l.17.17,0,0,0,0a.09.09,0,0,1,0,.05.17.17,0,0,1,0,.14.4.4,0,0,1-.12.1l-.13.08a.41.41,0,0,1-.12,0v.05a.25.25,0,0,0,0,.09s.07,0,.13,0a2,2,0,0,1-.08.3.64.64,0,0,0,0,.29l.09.35a.72.72,0,0,1,0,.17,2.09,2.09,0,0,0,0,.25,1.79,1.79,0,0,1,0,.32l-.48-.19s0,0,0,0,0,0,0,.06a.54.54,0,0,0,0,.08l.05.09v.28a.41.41,0,0,0,.08.1.21.21,0,0,0,.08.06v0s0,0,0,0a.32.32,0,0,0,.17-.1.48.48,0,0,1,.19-.09l.1,0,0,.31.11,0,.08.32.16,0a.44.44,0,0,0,0,.19l.08,0a.43.43,0,0,1,.17.08l.2.14.08.08s.07.07.08.07l.15,0,.14,0,.1,0,.11,0s.05,0,.12,0l.11,0h.31l.24-.06.36-.09.36-.08.23-.06.06.2a.15.15,0,0,0,.09-.19.26.26,0,0,1,.08-.24,1.82,1.82,0,0,1,.48-.28,3.67,3.67,0,0,0,.48-.25l.12-.09c.06,0,.09-.08.11-.08H417l0,0h.07l.23-.06.22,0,.18,0a.47.47,0,0,1,.11.14.42.42,0,0,1,.08.14l.08,0a.08.08,0,0,0,0,0s0,0,0,0a.26.26,0,0,1,0,.07.23.23,0,0,0-.05.07l0,.06s0,0,0,.06a.27.27,0,0,1-.08.16.51.51,0,0,1-.16.09l0,0a0,0,0,0,1,0,0l.06-.11a.2.2,0,0,1,.07-.12l0-.07c0-.06-.07-.1-.17-.12a.17.17,0,0,0-.05.12v.13a1,1,0,0,1-.23.09l-.22.09a.27.27,0,0,0-.09.06l-.07.05,0,.11a.5.5,0,0,0,0,.13.15.15,0,0,0,.12.07.24.24,0,0,0-.11,0,.38.38,0,0,0-.11.08s0,0,0,.11,0,.1,0,.11a.23.23,0,0,1-.11.15c-.07,0-.09.11-.06.23h0l-.06-.15a.64.64,0,0,0-.08-.14.17.17,0,0,1-.14,0l0,0,0,0,0,0v.13a.28.28,0,0,1,0,.13h-.05l-.1.1-.11.09-.08,0-.1,0-.12.1-.14.09,0,0h0a.5.5,0,0,1-.21,0A.36.36,0,0,0,415.3,216.07Zm-5.62-1.91s-.05,0,0,.07,0,.05.07,0,0,0,0-.07A.05.05,0,0,0,409.68,214.16Zm-.13-.52s0,0,0,0,0,0,0,0,0,0,0,0S409.57,213.64,409.55,213.64Zm.48.45a.15.15,0,0,0,.1-.09l-.11,0Zm.38,0a.1.1,0,0,0-.09.09h0a.15.15,0,0,0,.11-.1Zm-.56-2.31a.52.52,0,0,0,.07.15.1.1,0,0,0,.13.05.13.13,0,0,0,.08,0s0,0,0-.08a.13.13,0,0,0-.13-.11Zm.18.56h.05l0-.11H410Zm.74,2.16s0,0,0,0,0,0,0,0,0,0,0,0S410.77,214.57,410.77,214.55Zm0-1.09c-.06,0-.1,0-.15.12a.3.3,0,0,0,0,.19.81.81,0,0,0,.21-.09.15.15,0,0,0,.11-.17v0A.13.13,0,0,0,410.74,213.46Zm-.31-.49s0,0,0-.05a.11.11,0,0,0,0-.08s0-.05-.08,0Zm.75,2.24s0,0,0,0,0,.05.06,0,0,0,0-.06S411.21,215.2,411.18,215.21Zm-.51-2.48s-.05,0,0,.07,0,.06.07,0,.05,0,0-.08S410.71,212.72,410.67,212.73Zm.07.29.08,0,0-.06-.07,0Zm-.5-2.47h.06l0-.08Zm.51,1.47a.17.17,0,0,1,0,.1.14.14,0,0,1,0,.09h.06l0,0,0,0a.61.61,0,0,1,0-.14c0-.08,0-.13,0-.15l-.09,0a.08.08,0,0,0,0,.09l0,0Zm-.06-.42s0,0,0,0,0,0,0,0a0,0,0,0,0,0,0A0,0,0,0,0,410.69,211.6Zm.23,1a.08.08,0,0,0,0,0,.17.17,0,0,1,0,.05l.07,0,.07.05s0,0,.09.09.07.09.09.09l.07,0s.06,0,.06,0,0,0,0,0a.13.13,0,0,1,0,.07.18.18,0,0,0,.11-.12.3.3,0,0,0,0-.16,2.08,2.08,0,0,0-.14-.32.26.26,0,0,0-.3-.14l-.2.07A.11.11,0,0,0,410.92,212.56Zm.91,3s0,0,0,0,0,0,0,0v.06l0,0s0,0,0,0v0Zm-.91-4.53,0,.06.17,0h0l0,0h0Zm.41.93a.16.16,0,0,0,0,.09.15.15,0,0,0,.08.08.22.22,0,0,0,.05-.11V212Zm.65-1.93c.08,0,.11-.08.09-.17l-.13,0Zm.14-.62s0,.06.06.05l.07,0s.05,0,.06-.06Zm.38,0a.18.18,0,0,1,0,.06.2.2,0,0,0,0,.07,3.05,3.05,0,0,0,0,.35l.06.34h.06c0-.11,0-.21.05-.3a.67.67,0,0,0,0-.31c0-.05,0-.1,0-.14S412.57,209.48,412.5,209.48Zm.13-.2a.4.4,0,0,0,.19.25.32.32,0,0,0,.07-.22l.26.06s0,0,0-.07a.51.51,0,0,0-.09-.18c0-.06-.1-.09-.15-.07l-.23.06C412.63,209.14,412.61,209.19,412.63,209.28Zm.19.73s0,0,0,0a.05.05,0,0,1,0,0H413v-.11c0-.05,0-.07,0-.08a.44.44,0,0,1,.12-.11l.12-.1h0v0l-.11-.06-.1-.05a.67.67,0,0,1-.12.26A.32.32,0,0,0,412.82,210Zm.2.26,0-.09-.06,0Zm.08-.4s-.05,0,0,.08,0,.06.06.05,0,0,0-.07S413.12,209.87,413.1,209.87Zm.2.58,0-.06h0Zm.2-.23a.21.21,0,0,0,0-.16l-.06,0Zm-.18-1.05s0,0,0,0,0,0,0,0v0h0l0-.07v0s0,0,0,0Zm0-.26s0,0,0,0,0,0,0,0a0,0,0,0,0,0,0S413.29,208.93,413.28,208.91Zm.41.08s-.05.06,0,.12h.06Zm1.71,6.93a.62.62,0,0,0,.22-.1.57.57,0,0,1,.22-.11.18.18,0,0,0,0-.08l-.07-.14a.64.64,0,0,0-.1-.13c0-.05-.05-.06-.06-.06l0,0v.07a.13.13,0,0,0,0,.05l0,.05s0,0,0,0l-.08,0-.08,0Zm-1.49-6.07s-.05,0,0,.06,0,.06.06.05a.05.05,0,0,0,0-.07S413.94,209.84,413.91,209.85Zm.07-1.07s0,0,0,.07,0,.06,0,.07a.09.09,0,0,0,0,.06s0,0,0,.05v0a1.16,1.16,0,0,0,0,.19c0,.1,0,.15,0,.17H414l0,0s0,0,0-.06a.21.21,0,0,1,0-.06l.12-.06.12,0,.08-.05.07,0-.2-.14-.17-.15H414Zm0,.87a.37.37,0,0,0,.07.14l.11.11s.05,0,0-.06a.88.88,0,0,0,0-.18,1.63,1.63,0,0,1,0-.18.31.31,0,0,0-.12.05A.11.11,0,0,0,414,209.65Zm2.16,5.82s0,0,0,.07,0,.06,0,0a.07.07,0,0,0,0-.06S416.15,215.48,416.13,215.47Zm-1.68-6.56.06,0-.08,0Zm0-.42.07,0,0-.13Zm2.31,5.66s0,0,0,.1,0,.11,0,.15a.15.15,0,0,1,0,.07s0,.05,0,.06.11-.06.09-.1a.4.4,0,0,1,0-.17l.05-.06v0s0,0,0,0Zm-1.24-5.62a0,0,0,0,0,0,.06s0,.06.06,0,0,0,0-.07S415.5,208.52,415.48,208.53Zm.29.59c-.09,0-.11.08-.09.19v0A.23.23,0,0,0,415.77,209.12Zm.18.52s0,0,0,0,0,0,0,0l.05,0S416,209.66,416,209.64Zm1.37,4.09s-.05,0,0,.08,0,.06.07.05,0,0,0-.07S417.34,213.73,417.32,213.73Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "413.704px 212.185px",
                }}
                id="eluagvk76lw4a"
                className="animable"
              />
              <path
                d="M420.36,209.27a.12.12,0,0,0,0-.07.09.09,0,0,0,0-.06h-.17l0,0-.08,0-.09,0h-.28a.53.53,0,0,1-.2-.05l-.19-.09h0s0,0,0,.07a.54.54,0,0,1,0,.08c-.05,0-.08,0-.09,0L419,209v0a.08.08,0,0,0,0,.07.15.15,0,0,0,0,.07h0l0,0a.11.11,0,0,1,0-.08s0,0-.05,0a0,0,0,0,0,0,0h0l0,0,.05.2a.22.22,0,0,0-.07.09.13.13,0,0,1-.08.07l-.19,0v0s0,0,0,0l0,0-.13,0h-.1l0,0,0,0a.53.53,0,0,1-.13-.23c0-.1,0-.18-.08-.26s0-.14,0-.23a2.45,2.45,0,0,1,0-.27c0-.1,0-.19,0-.28a1.2,1.2,0,0,1,0-.23.18.18,0,0,1,.09-.13.38.38,0,0,0,.09-.13s0,0,0-.09a.44.44,0,0,0,0-.09s0,0,0,0h0s0,0,0,0l0,0,0,0s0,0,0,0l0,0,0,0s0,0,0,0l0,0h.05a.11.11,0,0,0,0-.05.36.36,0,0,0,0-.1.44.44,0,0,0,0-.1V207l.08,0,.09,0,.07-.06a.16.16,0,0,0,0-.09l.16,0v0s0,0,0-.05H419l.07,0,.13-.17.1-.12s0-.06.05-.06l0,0s0,0,0,0l0,0,0,0s0,0,0,0a0,0,0,0,1,0,0l0-.05,0,0s0-.06,0-.12l0-.1v-.05a.19.19,0,0,1,0-.08s0-.07,0-.1v-.08s0,0,0,0a.12.12,0,0,0,0-.07v-.05h-.33l0-.08-.06,0-.06,0s0,0-.07-.05l-.05,0h-.3l-.1,0a1.42,1.42,0,0,0-.34.15.53.53,0,0,0-.23.28v0a.19.19,0,0,0,0,.09l.16,0,.24.1.21.14V206s0,.05,0,.11,0,.09,0,.09-.05,0-.07,0l-.05-.08a.32.32,0,0,1-.18.1s0-.06,0-.07a0,0,0,0,0,0-.06v0l-.11.08-.13.05-.08,0s0-.07-.06-.06h-.1a.24.24,0,0,1-.14-.12.49.49,0,0,0-.13-.12l-.08,0h-.24s-.05,0-.05,0l-.16-.6a.05.05,0,0,1,0,0s0,0,0,0a.2.2,0,0,0,.11-.09c0-.05,0-.1.05-.14l.07,0,.1,0,.1,0,.06,0s0,0,0,0l0,0a.28.28,0,0,1,.1-.06.15.15,0,0,0,.08-.09l.28-.07s0-.07,0-.08l-.05,0h0a1.36,1.36,0,0,1,.3,0,.4.4,0,0,1,.25.18h0s0,0,0-.07a.07.07,0,0,1,.05-.07h.19l.05,0,.08,0H419a.07.07,0,0,1,.06,0,.41.41,0,0,1,0,.09.49.49,0,0,1,0,.1.57.57,0,0,1,.21.06.27.27,0,0,1,.16.13s0,0,0,.06a.18.18,0,0,1,0,.06.11.11,0,0,0,.07,0l.07,0h.14a.12.12,0,0,1,0,0s0,.05,0,.06,0,0,.06.05l.07.06a.53.53,0,0,1,.06.13,1,1,0,0,1,0,.14,1.62,1.62,0,0,1,0,.19.69.69,0,0,0,0,.2l0,.16a.51.51,0,0,0,.07.15l-.06.08-.07.07-.08,0-.09,0,0,0,0,.06,0,.07s0,0,0,0l0,0s0,0,0,0l0,.06a.17.17,0,0,1,0,.1s0,0-.06.06a.08.08,0,0,1-.09,0,.05.05,0,0,0-.06,0h0a.19.19,0,0,1,0,.13.36.36,0,0,1,0,.11s0,.08,0,.12a.28.28,0,0,0,0,.13.09.09,0,0,0,0,0s0,0,0,0h0l-.06,0s0,0-.08,0l0,.18s0,0,0,0l0,0a.1.1,0,0,1-.07,0l-.07,0,0,.06h0s0,0,0,0,0,0,0,0v0h0v0s0,0-.06,0h0a.08.08,0,0,1,0,0s0,0,0,0h0v0l0,0,0,0-.1.12-.08.09,0,.05,0,0,0,.16a.61.61,0,0,0-.05.11.79.79,0,0,0,0,.12s0,0,.07,0l.08,0h0s0,0,0,0v0h.06v-.06h.07s0,0,0,0a.12.12,0,0,0,.12,0,.26.26,0,0,1,.12,0s0,0,0,0l0,0a.18.18,0,0,0,.16,0l.16,0a.08.08,0,0,0,0,0v0h0s0,0-.06.06l-.07.05.09.36s0,0,0,0,0,0,0-.08a.78.78,0,0,1,0-.16.43.43,0,0,1,0-.16c0-.05,0-.08.07-.08l.12.11s0,0,0,.06a0,0,0,0,0,.05,0,.06.06,0,0,0,.06,0l.05,0a.11.11,0,0,1,.08,0l.05.06.16,0h-.06l-.06,0h0v0a0,0,0,0,1,0,0s0,0,0,0h.21l.11,0,.1,0H421l0,.06s0,0,0,0,0,0,0,0l0,0s0,0,0,0,0,0,0,0a.05.05,0,0,0,0,0l0,0v0h.14v0a.16.16,0,0,1,0,.14.41.41,0,0,0-.07.11v0a.22.22,0,0,1,0,.1l-.06,0H421l.09.09s0,0,0,0h0s-.06,0-.08,0l0-.06a.14.14,0,0,0,0-.06,0,0,0,0,0-.06,0s0,0,0,.06a.36.36,0,0,1,0,.1.08.08,0,0,1,0,.08.28.28,0,0,1-.2,0l0-.21h0v.07a.43.43,0,0,0,0,.14c0,.07,0,.12.05.14h0v-.08l.09,0a.35.35,0,0,1,.05.08.07.07,0,0,1,0,.05v0a.2.2,0,0,1,0-.07s0,0,0,0l.07-.08.05,0s.06,0,.06,0l0,.4a.54.54,0,0,0-.24.13.37.37,0,0,1-.22.13l-.12,0ZM418,208.16h0a.18.18,0,0,0,0-.06.1.1,0,0,1,0-.08H418Zm.08-.3s0,0,0,0,0,0,0,0,0,0,0,0Zm.12.11s0,0,0,0,0,0,0,0,0,0,0,0S418.25,208,418.23,208Zm0-.17h0l0,0Zm0-.18h.21s0-.06-.05-.06h-.13S418.22,207.6,418.22,207.62Zm.38,1.36s0,0,0,0l0,0s0,0,0-.05S418.61,209,418.6,209Zm-.82-3.43s0,0,0,0,0,0,0,0,0,0,0,0S417.8,205.55,417.78,205.55Zm.88,2.61a.37.37,0,0,0,0,.11.1.1,0,0,0,.11,0h0v0a.27.27,0,0,1,0-.08v0h-.11Zm-.13-.87a0,0,0,0,0,0,0h0l0,.06,0,.1a.94.94,0,0,0,0,.09v.06s0,0,0,0,0,0,0-.05v-.06l0,0,0,0,0-.06v-.07s0-.07,0-.07,0,0,0,0h-.13s0,0,0,0,0,0,0,.05v.06Zm.47,1.61s0,0,0,0,0,0,0,0,0,0,0,0S419,208.92,419,208.9Zm-.41-1.91s0,0,0,0v0h0l0,0h0Zm-.63-2.41s0,0,.05,0h.08l0-.12-.05,0-.05,0Zm1.1,4.28.05,0,.05,0s0,0,0,0v0h0v0s0,0,0,0,0,0,0,0h0s0,0,0,0h0Zm-.14-.7,0-.07a.14.14,0,0,0,0-.06V208s0,0-.05,0l0,.06Zm-.11-.59s0,0,0,.05,0,0,0,0,0,0,0,0A0,0,0,0,0,418.81,207.57Zm.08.3s0,0,0-.08h0Zm.46.16a.06.06,0,0,0-.05.06v.07s.06,0,.06,0A.22.22,0,0,0,419.35,208Zm.29.92c0,.05,0,.07.08.06l.14,0a.08.08,0,0,0-.1,0Zm-.17-1v.11s0,.06,0,.1v.13l0,0,.06,0s0,0,0,0a.37.37,0,0,0,0-.16,0,0,0,0,1,0,0v0l0,0a.09.09,0,0,0,0-.07l0-.07a.06.06,0,0,0,0,0Zm-.21-1.18a.1.1,0,0,0,.06.05l0-.06Zm.55,1.84,0,0,0,0Zm.24-.13s0,0,0,0h.05l.07,0a.17.17,0,0,1,.07.09v-.06a.06.06,0,0,1,0-.05h0s0,0,0,0l0,.07s0,0,.07,0l-.05-.15a.14.14,0,0,0-.1-.11l0,0,0,0,0-.12-.11.21Zm-.31-1.8.05.08h0l0,0s0,0,0,0v-.18s0,0,0,0-.05.06-.07.06Zm.54,1.55s0,0,0,0,0,0,0,0S420.3,208.21,420.28,208.22Zm.05,0s0,0,0,0,0,0,0,0,0,0,0,0A0,0,0,0,0,420.33,208.25Zm0,0s0,0,0,0,0,0,0,0,0,0,0,0S420.38,208.27,420.37,208.28Zm.05.09,0,0h0Zm-.53-2.43s0,0,0,0a.28.28,0,0,0,0,.09.09.09,0,0,0,0,.07s0,0,0,0,0,0,0,0a.24.24,0,0,0,0-.08.22.22,0,0,0,0-.08S419.9,205.94,419.89,205.94Zm.8,2.5,0,0h0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "418.998px 207.145px",
                }}
                id="eleivdxpokgn6"
                className="animable"
              />
              <path
                d="M318.56,243.87l-.72-.21c.13-.43,3.13-10.56,8.53-12.29,2.8-.89,5.72,0,8.55.95,2.37.76,4.61,1.47,6.37.9a5.48,5.48,0,0,0,3.35-3.78,6.84,6.84,0,0,0-.68-5.88l.6-.46a7.58,7.58,0,0,1,.79,6.56,6.17,6.17,0,0,1-3.82,4.27c-2,.65-4.35-.1-6.84-.9-2.72-.87-5.52-1.77-8.09-.94C321.57,233.69,318.59,243.77,318.56,243.87Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "331.801px 233.485px",
                }}
                id="el6hnu9vdkkp"
                className="animable"
              />
              <path
                d="M344.13,224a6.47,6.47,0,0,1-3-.74,21.72,21.72,0,0,1-4-3.59l.56-.5a21.76,21.76,0,0,0,3.78,3.43,5.93,5.93,0,0,0,2.69.65l0,.75Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "340.645px 221.585px",
                }}
                id="elwvk663bdff"
                className="animable"
              />
              <path
                d="M344.88,223.23l-.75-.1a13.83,13.83,0,0,0,.09-3c-.11-.82-1.7-3-2.74-4.28l.58-.47c.28.34,2.73,3.37,2.9,4.65A15.42,15.42,0,0,1,344.88,223.23Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "343.244px 219.305px",
                }}
                id="elq7paj98ox7m"
                className="animable"
              />
              <path
                d="M344.17,223.77a24.69,24.69,0,0,1-3.61-2.47c-.71-.77-3.22-3.57-3.22-3.57l.56-.5s2.51,2.8,3.21,3.56a22.77,22.77,0,0,0,3.44,2.33Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "340.945px 220.5px",
                }}
                id="elrby66i0f4h"
                className="animable"
              />
              <path
                d="M344.12,223.47a27.83,27.83,0,0,0-2.16-4.21c-.67-.72-2.24-3-2.31-3.07l.62-.43s1.6,2.31,2.24,3a27.48,27.48,0,0,1,2.3,4.42Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "342.23px 219.615px",
                }}
                id="el83acmug6pui"
                className="animable"
              />
              <polygon
                points="302.19 257.23 267.04 254.86 298.06 235.4 333.16 242.22 302.19 257.23"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "300.1px 246.315px",
                }}
                id="elm917kmwevdb"
                className="animable"
              />
              <g id="el89qvy4ms168">
                <polygon
                  points="302.19 257.23 267.04 254.86 298.06 235.4 333.16 242.22 302.19 257.23"
                  style={{
                    opacity: "0.1",
                    transformOrigin: "300.1px 246.315px",
                  }}
                  className="animable"
                  id="el85huabpd7xw"
                />
              </g>
              <path
                d="M285.53,263.49l-3.06-9.87s9.35-6.65,17.29-8.15,18.39.6,18.39.6l1.4,11s-11.95-.05-18.34,1.3S285.53,263.49,285.53,263.49Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "301.01px 254.21px",
                }}
                id="elsszc2ekeafm"
                className="animable"
              />
              <path
                d="M348.89,301.44l5.4-9.77-3-1.65a2.68,2.68,0,0,1-1.36-2.44,15.56,15.56,0,0,0,0-1.67,2.64,2.64,0,0,1,1.27-2.47l2.92-1.77-5.76-9.57-2.93,1.77a2.65,2.65,0,0,1-2.78,0,13.88,13.88,0,0,0-1.46-.81,2.65,2.65,0,0,1-1.52-2.34l-.06-3.41-11.17.21.07,3.43a2.65,2.65,0,0,1-1.44,2.38l-.71.41c-.24.14-.47.29-.7.44a2.64,2.64,0,0,1-2.77.16l-3-1.66-5.4,9.77,3,1.66a2.67,2.67,0,0,1,1.36,2.43,15.51,15.51,0,0,0,0,1.67,2.65,2.65,0,0,1-1.27,2.47l-2.93,1.77,5.77,9.57,2.93-1.77a2.65,2.65,0,0,1,2.78,0,15.7,15.7,0,0,0,1.45.8,2.65,2.65,0,0,1,1.52,2.34l.07,3.42,11.17-.21-.07-3.43a2.63,2.63,0,0,1,1.44-2.37l.72-.41.69-.45a2.65,2.65,0,0,1,2.78-.15Zm-9.79-6.56a9.15,9.15,0,1,1,3.12-12.56A9.16,9.16,0,0,1,339.1,294.88Z"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "334.39px 287.06px",
                }}
                id="elp3sh5f26nf"
                className="animable"
              />
              <path
                d="M373.87,248.22l-4.24-6.58-2,1.3a1.85,1.85,0,0,1-1.95,0,11.42,11.42,0,0,0-1-.54,1.86,1.86,0,0,1-1.12-1.6l-.11-2.39-7.82.39.12,2.39a1.87,1.87,0,0,1-.95,1.71,10.18,10.18,0,0,0-1,.63,1.84,1.84,0,0,1-1.94.17l-2.14-1.1-3.56,7,2.14,1.09a1.86,1.86,0,0,1,1,1.68c0,.19,0,.38,0,.57s0,.39,0,.58a1.85,1.85,0,0,1-.82,1.76l-2,1.31,4.24,6.57,2-1.3a1.87,1.87,0,0,1,1.95,0,10.24,10.24,0,0,0,1,.54,1.85,1.85,0,0,1,1.11,1.59l.12,2.4,7.81-.39-.12-2.4a1.87,1.87,0,0,1,1-1.7,10.18,10.18,0,0,0,1-.63,1.84,1.84,0,0,1,1.94-.17l2.14,1.09,3.56-7-2.13-1.09a1.86,1.86,0,0,1-1-1.68c0-.19,0-.38,0-.58s0-.38,0-.57a1.85,1.85,0,0,1,.82-1.77Zm-7.29,3.87a6.41,6.41,0,1,1-6.72-6.09A6.42,6.42,0,0,1,366.58,252.09Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "360.195px 252.4px",
                }}
                id="elu84c2gkr8wo"
                className="animable"
              />
            </g>
            <g
              id="freepik--Character--inject-2"
              className="animable"
              style={{ transformOrigin: "267.28px 295.611px" }}
            >
              <path
                d="M227.37,297.35C232.46,333,245,378.7,256.81,388.69c9.54,8.07,61.65,25.24,64.59,25.81,7.28,1.36,26.14-49.29,18.7-52-9.36-3.46-54.82-8.72-57.19-11.6S265.76,324,249.17,296.41C237.72,277.34,224.2,275.22,227.37,297.35Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "284.354px 347.977px",
                }}
                id="elze06dgq0i87"
                className="animable"
              />
              <path
                d="M315.74,411.15c.84.52,12.73,7.81,20.22,10.68,5.59,2.14,21.85,7.88,24.82,2,2-4-7.13-8.53-13.54-11.86-3.38-1.75-7.38-5.77-6.16-7.93,2.13-3.78,10.81.92,16.44,4.86,10.89,7.62,25.14,20.88,32.62,15.66,2.83-2,.09-6.24-4.07-10.53,6.91,5.18,14.36,9.58,17.59,7.51,1.85-1.17,2.08-3,1.29-5.14,2.19.92,4.19.95,5.7-.4,2.58-2.28,1.73-6-.55-10a2.85,2.85,0,0,0,2.27-1.23c5.66-7.23-17.6-31.3-26.11-34.09-25-8.22-52.4-8.29-52.4-8.29C323.15,369.51,315.72,411.14,315.74,411.15Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "364.494px 394.397px",
                }}
                id="elp669pe16lqq"
                className="animable"
              />
              <path
                d="M355.29,390.79a43.5,43.5,0,0,1,16.49,8c6.12,4.82,10,10.18,15,15.68.13.14-.09.35-.22.21-2.39-2.62-12.36-12.52-15-14.8a41.37,41.37,0,0,0-16.34-9C355.19,390.88,355.22,390.78,355.29,390.79Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "371.014px 402.758px",
                }}
                id="elojp7b6ks5wd"
                className="animable"
              />
              <path
                d="M361,380.35c6.56,2.4,13.29,4.84,19.25,8.54,6.53,4.05,11.39,10.16,16.18,16.06q4.32,5.31,8.39,10.81c.11.15-.1.36-.22.2q-7.28-9.12-15.1-17.79a54.75,54.75,0,0,0-17.12-12.92c-3.72-1.79-7.57-3.28-11.41-4.78A.06.06,0,0,1,361,380.35Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "382.887px 398.183px",
                }}
                id="elwjd5ewlv94r"
                className="animable"
              />
              <path
                d="M368.93,371.48c6.41,1.89,12,3.51,17.49,7.49,7.26,5.27,14,13.69,16.35,16.67,2.6,3.3,5.09,6.68,7.53,10.09.1.14-.14.32-.24.18a217.33,217.33,0,0,0-18.24-21.38,43.18,43.18,0,0,0-10.52-8.17,78.26,78.26,0,0,0-12.38-4.84S368.9,371.47,368.93,371.48Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "389.619px 388.72px",
                }}
                id="elbmtaevc7o0i"
                className="animable"
              />
              <path
                d="M237.65,357.8l39.51-22.13S261.78,308.34,246,287.74,223.93,279,226.2,301.33,237.65,357.8,237.65,357.8Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "251.455px 317.443px",
                }}
                id="elmbxgk5ysyr"
                className="animable"
              />
              <path
                d="M271.14,330.14a366.48,366.48,0,0,0-33,18.55c-.16.1,0,.36.17.28a365.46,365.46,0,0,0,33.07-18.52C271.5,330.34,271.32,330.05,271.14,330.14Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "254.746px 339.554px",
                }}
                id="eluhzl6dkma4e"
                className="animable"
              />
              <g id="el1sm4b28v1v7h">
                <path
                  d="M270.18,339.58,237.65,357.8s-9.19-34.13-11.45-56.47c-1.44-14.13.56-24.06,6.22-24.23Z"
                  style={{
                    opacity: "0.1",
                    transformOrigin: "247.965px 317.45px",
                  }}
                  className="animable"
                  id="eldlvzjim127o"
                />
              </g>
              <path
                d="M126.5,419.7H276C277.91,418.3,260.59,317,238.58,282c-4.12-6.54-23.86-16.54-39.31-4.69C152.64,313.08,124.75,417.46,126.5,419.7Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "201.283px 345.753px",
                }}
                id="el0blrxi6xq4l"
                className="animable"
              />
              <path
                d="M302.23,344.23A85.15,85.15,0,0,1,317.68,338c7.15-1.85,22.84-4.69,25.65-5.86l4.75-.17-30.71,81-42.08,8.64-70-16.7,25-77.08,4.82-.24S244,330,251.33,331.4,284.08,333.9,302.23,344.23Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "276.685px 374.6px",
                }}
                id="elnflng40bvve"
                className="animable"
              />
              <polygon
                points="269.05 423.68 296.52 345.61 227.26 327.04 201.24 406.74 269.05 423.68"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "248.88px 375.36px",
                }}
                id="elnrvpvk2aij"
                className="animable"
              />
              <polygon
                points="269.05 423.68 279.14 424.24 306.72 346.16 296.52 345.61 269.05 423.68"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "287.885px 384.925px",
                }}
                id="elc4muiek14pd"
                className="animable"
              />
              <polygon
                points="279.14 424.24 306.72 346.16 350.12 331.26 318.49 413.76 279.14 424.24"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "314.63px 377.75px",
                }}
                id="ela619dxmbbpm"
                className="animable"
              />
              <path
                d="M195,281.5c-35.52,40.29-80.59,115.33-72.8,130.18,9.88,18.83,78.11,15.35,94,11.64,6.79-1.58-16.69-60.44-24-58.9-6.36,1.34-11.85,2.68-12.07,1.8s18.76-28.93,30.32-69.33C218.4,269.35,202.54,273,195,281.5Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "169.365px 350.777px",
                }}
                id="elfovez9b39y6"
                className="animable"
              />
              <path
                d="M160.29,392.45s2.65-2.72,14.29-19.31c8-11.48,35-34.29,73.28-25,14.66,3.57,18.57,15.42,2.67,14.42-11.2-.71-18.89-2.16-22.61-1.36,0,0,12.74,11.51,11.57,21.39a183.35,183.35,0,0,1-4.89,25.61,39.79,39.79,0,0,1-4.88-.49,20.9,20.9,0,0,1-15.48,16Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "210.538px 384.839px",
                }}
                id="elqzes99r3b2"
                className="animable"
              />
              <path
                d="M184,363.41c7.14-1.76,22.32-5.23,35.73-3.75,14.89,1.65,43.13,8.83,41.81,19.81-1,8-12,6.26-12,6.26s11.05,5.43,8.58,12.51c-2.25,6.45-10.7,4.55-10.7,4.55s6.14,4.45,2.77,10.25c-4,7-16.24-3.49-26.45,0C221.46,413.86,184,363.41,184,363.41Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "222.792px 387.404px",
                }}
                id="el9md97mpwo76"
                className="animable"
              />
              <path
                d="M215.37,375.11c7-.55,23.16,2.26,34.06,10.33.25.18.24.47,0,.35a147.06,147.06,0,0,0-34-10.54C215.28,375.24,215.3,375.12,215.37,375.11Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "232.465px 380.431px",
                }}
                id="elmvir8eux1r"
                className="animable"
              />
              <path
                d="M214.15,390.19c6.07.14,22.62,3.79,33.38,12.35.24.19.21.47,0,.34a157.71,157.71,0,0,0-33.36-12.55A.07.07,0,0,1,214.15,390.19Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "230.894px 396.552px",
                }}
                id="elqbsbha55rfn"
                className="animable"
              />
              <path
                d="M149.66,384.72c4.28-5.54,17.23-15.63,30.53-18.28.3-.06.51.14.25.25a147.22,147.22,0,0,0-30.69,18.14C149.69,384.88,149.61,384.78,149.66,384.72Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "165.101px 375.636px",
                }}
                id="elifkzwdds22"
                className="animable"
              />
              <path
                d="M153.07,331.19l42.32,16.11s11.1-29.34,16.78-54.65-7.45-22.54-23.14-6.48S153.07,331.19,153.07,331.19Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "183.294px 310.582px",
                }}
                id="ely09wik0l8wg"
                className="animable"
              />
              <path
                d="M208.5,314.53a.09.09,0,0,0-.17-.08l-2.19,4.13q1.14-4.2,2.12-8.46c0-.09-.13-.15-.16-.06q-3.22,9.18-6.31,18.38c-2,6.15-4.38,12.21-6.36,18.38-1.22-.24-1.88-.44-2.79-.59-.21,0-.34.2-.15.3a13.86,13.86,0,0,0,3.48,1.31c2.68-5.9,4.85-12.52,6.94-18.65,1-3,2-6.05,2.82-9.11A55.19,55.19,0,0,0,208.5,314.53Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "200.453px 328.928px",
                }}
                id="elf7qmvcy1z1g"
                className="animable"
              />
              <path
                d="M195.77,339.13a364.8,364.8,0,0,0-35.43-13.45c-.18-.06-.28.24-.11.31a363,363,0,0,0,35.43,13.48C195.85,339.54,196,339.21,195.77,339.13Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "178.015px 332.576px",
                }}
                id="elzgq03ikx6dd"
                className="animable"
              />
              <path
                d="M208,279.2c0,2.45,7.69,20,18.41,21.91,3.5.61,7.68-18.3,7.68-18.3l.63-1.33,8.78-18.56L221,240.3S208,278.17,208,279.2Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "225.75px 270.712px",
                }}
                id="eldy4bjwsel8"
                className="animable"
              />
              <path
                d="M243.5,262.92l-8.78,18.55-.63,1.34s-.76,3.42-1.9,7.31c-14-5.41-18.14-17.77-19.34-25.91,3.69-11,8.14-23.9,8.14-23.9Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "228.175px 265.215px",
                }}
                id="elnz57xb54pwd"
                className="animable"
              />
              <path
                d="M279.32,233.62c-4.64,43.75-31.36,45.92-40.8,44.91-8.56-.92-37.6-5.12-37.52-49.12s21.9-55.67,41.59-54.61S284,189.87,279.32,233.62Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "240.482px 226.717px",
                }}
                id="ell22frf69tup"
                className="animable"
              />
              <path
                d="M268.16,240.19c4.82.73,3,10.33-1.46,9.65S264.11,239.58,268.16,240.19Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "267.568px 245.018px",
                }}
                id="elopz6m8icp48"
                className="animable"
              />
              <path
                d="M245.85,237c-4.8-.91-6.27,8.75-1.83,9.59S249.86,237.74,245.85,237Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "244.789px 241.791px",
                }}
                id="elfvdq9ghfdfe"
                className="animable"
              />
              <path
                d="M272.39,234.7a29.69,29.69,0,0,1-2.82-1.14,4.9,4.9,0,0,1-2.65-1.71,1.56,1.56,0,0,1,.34-1.88c1.15-.93,2.6-.71,3.92-.22a5.65,5.65,0,0,1,3.17,2.61A1.67,1.67,0,0,1,272.39,234.7Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "270.632px 232.046px",
                }}
                id="el0um0bcd6mgvk"
                className="animable"
              />
              <path
                d="M242.71,230.88c1,.06,2-.06,3-.1a4.92,4.92,0,0,0,3.08-.69,1.57,1.57,0,0,0,.33-1.88,3.88,3.88,0,0,0-3.61-1.56,5.67,5.67,0,0,0-3.88,1.36A1.67,1.67,0,0,0,242.71,230.88Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "245.208px 228.758px",
                }}
                id="el2wgdd0dd2od"
                className="animable"
              />
              <path
                d="M242.35,263.35c.54.85,1,2,2,2.3a7.86,7.86,0,0,0,3.31,0,.11.11,0,0,1,.09.18,4,4,0,0,1-4.08.69,3.18,3.18,0,0,1-1.67-3.12C242,263.24,242.28,263.23,242.35,263.35Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "244.88px 265.034px",
                }}
                id="ell9frtmx81a"
                className="animable"
              />
              <path
                d="M256.61,254.93s-.19,4.51-.57,6.59c0,.19-.55.2-1.27.11,0,0-.05,0-.08,0a10.05,10.05,0,0,1-8.92-5.43c-.1-.23.2-.39.37-.23,2.37,2.51,5.3,3.57,8.54,4.22.25-.55.09-7.82.4-7.71,1.34.49,3.66,1.54,4.52,1.93.19-2.93.32-11.28.68-16.61,0-.33.54-.35.56,0a141,141,0,0,1,.57,18.8C261.39,257.05,257.38,255.43,256.61,254.93Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "253.627px 249.615px",
                }}
                id="elkg1toe5pq7p"
                className="animable"
              />
              <path
                d="M195.73,231.61c12.26,4.55,23.17-8.47,23.76-21.34,0,0,32,14.17,41.43-12.1a27.33,27.33,0,0,0,18.13,18.34l.83,11c3.71,0,10.19-8.63,4.06-18.69,0,0,3.33-13.5-4.15-24.37A19,19,0,0,0,261,175.92a28.73,28.73,0,0,0-18.63-10.83c-12.73-2-21,7.69-21,7.69s-17.42-1.68-23.66,9.21-2.49,20.52-2.49,20.52-5.48,3.39-6.31,14C188.34,223.34,192.05,230.24,195.73,231.61Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "237.66px 198.677px",
                }}
                id="eloytm6f0ygpo"
                className="animable"
              />
              <path
                d="M260.88,195.58a33.14,33.14,0,0,1-1.84,6.55,27.28,27.28,0,0,1-3.06,6,17.55,17.55,0,0,1-10.52,7.34c-4.27,1.25-8.81.31-13.18-.51-2.14-.7-4.34-1.25-6.45-2.07s-4.19-1.76-6.34-2.58c2,1.14,4,2.19,6,3.3s4.28,1.68,6.43,2.54c4.45,1.11,9.2,2.1,13.89.88A18.36,18.36,0,0,0,257,208.77,27.88,27.88,0,0,0,260.88,195.58Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "240.185px 206.585px",
                }}
                id="el1z253tsluhs"
                className="animable"
              />
              <path
                d="M221.33,172.78a32.22,32.22,0,0,0-13.05-.94,19.35,19.35,0,0,0-11.85,5.94,21.27,21.27,0,0,0-3.41,5.8,19.7,19.7,0,0,0-1.56,6.5,18,18,0,0,0,3.72,12.43,19.75,19.75,0,0,1-2.49-12.32,22,22,0,0,1,4.95-11.36,16.57,16.57,0,0,1,4.87-3.79,25.07,25.07,0,0,1,6-2A40.66,40.66,0,0,1,221.33,172.78Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "206.368px 187.034px",
                }}
                id="eluxs44fekg4a"
                className="animable"
              />
              <path
                d="M261,175.92a32.8,32.8,0,0,1,10.34,1,19.81,19.81,0,0,1,8.78,4.83,20.12,20.12,0,0,1,5,8.71,35.52,35.52,0,0,1,1.3,10.32,26.26,26.26,0,0,0-.1-10.63,17.89,17.89,0,0,0-14.68-14.43A24.84,24.84,0,0,0,261,175.92Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "273.957px 188.01px",
                }}
                id="elavax3q9l82m"
                className="animable"
              />
              <path
                d="M273.51,216.06a35.71,35.71,0,0,1-8.5-8.4,21.45,21.45,0,0,1-2.71-5.2,27.49,27.49,0,0,1-1.32-5.88,15,15,0,0,0,.13,6.22,16,16,0,0,0,2.58,5.78,20.17,20.17,0,0,0,4.36,4.53A18.79,18.79,0,0,0,273.51,216.06Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "267.113px 206.32px",
                }}
                id="eln6vezyq09d"
                className="animable"
              />
              <path
                d="M227.55,242.41a.7.7,0,1,1-1.27-.61.7.7,0,0,1,1.27.61Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "226.915px 242.105px",
                }}
                id="elg1eyjo0zzqs"
                className="animable"
              />
              <path
                d="M241.24,248.21a.71.71,0,1,1-.33-1A.72.72,0,0,1,241.24,248.21Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "240.621px 247.859px",
                }}
                id="eldnwy6udrcm"
                className="animable"
              />
              <path
                d="M234.27,250.64a.71.71,0,0,1-1,.33A.7.7,0,0,1,233,250a.71.71,0,0,1,.95-.33A.7.7,0,0,1,234.27,250.64Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "233.629px 250.331px",
                }}
                id="eln6y4t72f6q"
                className="animable"
              />
              <path
                d="M230.45,237.3a.71.71,0,0,1-.95.33.71.71,0,1,1,.95-.33Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "229.811px 236.992px",
                }}
                id="el6hugsy5bo7k"
                className="animable"
              />
              <path
                d="M235,243.93a.71.71,0,1,1-.34-.94A.7.7,0,0,1,235,243.93Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "234.357px 243.632px",
                }}
                id="eluvsespbfvbc"
                className="animable"
              />
              <path
                d="M272.4,250.48a.71.71,0,0,1-1.28-.62.71.71,0,0,1,1.28.62Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "271.76px 250.17px",
                }}
                id="el6jv9qwb1qsw"
                className="animable"
              />
              <path
                d="M265.66,251.72a.7.7,0,0,1-.94.33.71.71,0,0,1-.33-1,.7.7,0,0,1,.94-.33A.72.72,0,0,1,265.66,251.72Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "265.023px 251.385px",
                }}
                id="elutbd5jxcwqd"
                className="animable"
              />
              <path
                d="M264.92,256.54a.72.72,0,0,1-.95.33.7.7,0,1,1,.95-.33Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "264.285px 256.242px",
                }}
                id="eltaohoopux9"
                className="animable"
              />
              <path
                d="M270.67,255.7a.71.71,0,1,1-.33-.95A.72.72,0,0,1,270.67,255.7Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "270.03px 255.389px",
                }}
                id="elrpzdxvyc7yf"
                className="animable"
              />
              <path
                d="M234.78,252.7a15.37,15.37,0,1,1,19.82-14h0a15.38,15.38,0,0,1-16,14.64A15.57,15.57,0,0,1,234.78,252.7Zm8.34-27.44a13,13,0,0,0-3.26-.56,13.31,13.31,0,1,0,3.26.56Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "239.246px 237.988px",
                }}
                id="elya7n9hl7qrs"
                className="animable"
              />
              <path
                d="M268.25,258.37c-5.63-1.71-9-9-7.56-16.91a18.35,18.35,0,0,1,5.1-10,10.17,10.17,0,0,1,8.88-3.1c3.08.55,5.64,2.69,7.24,6a18.3,18.3,0,0,1,1.29,11.14c-1.5,8.34-7.77,14.22-14,13.1A7.79,7.79,0,0,1,268.25,258.37Zm6.81-27.8a4.82,4.82,0,0,0-.75-.18,8.17,8.17,0,0,0-7.1,2.55,16.33,16.33,0,0,0-4.5,8.88c-1.3,7.23,1.77,13.85,6.88,14.77s10.29-4.22,11.59-11.44a16.24,16.24,0,0,0-1.12-9.89A8.46,8.46,0,0,0,275.06,230.57Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "271.954px 243.495px",
                }}
                id="elk6gp8kcn76g"
                className="animable"
              />
              <path
                d="M253.93,240.38l-.86-1.51c2.22-1.27,7.09-2.65,9.6,1.24l-1.45.94C259,237.56,254.13,240.26,253.93,240.38Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "257.87px 239.302px",
                }}
                id="eliikk587ay3s"
                className="animable"
              />
              <path
                d="M191.8,232.6l-1.37-1c.49-.64,4.92-6.25,8.84-6.19,3.66.08,26.12,6.33,27.07,6.6l-.46,1.66c-6.44-1.8-23.81-6.48-26.64-6.52C196.65,227.05,193,231.06,191.8,232.6Z"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "208.385px 229.54px",
                }}
                id="elo4tnijec4v"
                className="animable"
              />
              <path
                d="M208.89,234.32s-6.8-15.31-13.57-13.14-3.49,23.09,3.17,27.16a7.44,7.44,0,0,0,10.55-2Z"
                style={{
                  fill: "rgb(173, 99, 89)",
                  transformOrigin: "200.314px 235.344px",
                }}
                id="elbxbh8ts8px"
                className="animable"
              />
              <path
                d="M196.41,227.16c-.12,0-.17.12-.08.18,4.64,3,6.38,8.2,7.3,13.41-1.14-2.16-3-3.58-5.82-2.28-.15.07-.06.3.08.3a4.57,4.57,0,0,1,4.64,2.53,21.72,21.72,0,0,1,1.59,4.46c.15.49,1,.41.94-.14a.59.59,0,0,0,0-.13C206.42,239.1,203.17,229.14,196.41,227.16Z"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "200.824px 236.622px",
                }}
                id="elgisljaet0g"
                className="animable"
              />
            </g>
            <g
              id="freepik--Device--inject-2"
              className="animable"
              style={{ transformOrigin: "369.17px 389.22px" }}
            >
              <rect
                x="272.4"
                y="423.75"
                width="120.67"
                height="5.41"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "332.735px 426.455px",
                }}
                id="el1s6rgkr337w"
                className="animable"
              />
              <rect
                x="272.4"
                y="423.75"
                width="60.73"
                height="5.41"
                style={{
                  fill: "rgb(166, 166, 166)",
                  transformOrigin: "302.765px 426.455px",
                }}
                id="elpt9mapjmsrb"
                className="animable"
              />
              <polygon
                points="331.72 429.15 449.32 429.15 465.94 349.28 348.34 349.28 331.72 429.15"
                style={{
                  fill: "rgb(199, 199, 199)",
                  transformOrigin: "398.83px 389.215px",
                }}
                id="el4frm2bsecd6"
                className="animable"
              />
              <path
                d="M389.71,391a6.93,6.93,0,0,0,7,7.84,8.92,8.92,0,0,0,8.66-7.84,6.93,6.93,0,0,0-7-7.84A8.93,8.93,0,0,0,389.71,391Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "397.54px 391px",
                }}
                id="elc0jugxrtir"
                className="animable"
              />
            </g>
            <g
              id="freepik--Books--inject-2"
              className="animable animator-hidden"
              style={{ transformOrigin: "102.305px 390.34px" }}
            >
              <rect
                x="58.94"
                y="416.37"
                width="100.44"
                height="11.89"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "109.16px 422.315px",
                }}
                id="els7eyrhzfqkg"
                className="animable"
              />
              <rect
                x="78.64"
                y="416.37"
                width="4.1"
                height="11.89"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "80.69px 422.315px",
                }}
                id="elsj7nknq3w19"
                className="animable"
              />
              <rect
                x="137.75"
                y="416.37"
                width="9.81"
                height="11.89"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "142.655px 422.315px",
                }}
                id="elzbzmp4vo9h"
                className="animable"
              />
              <path
                d="M101.7,419.53l-2.4,3.12a.25.25,0,0,1-.21.11h0a.24.24,0,0,1-.22-.11l-2.4-3.11v5.77a.25.25,0,0,1-.26.24.26.26,0,0,1-.27-.24V418.8a.24.24,0,0,1,.11-.19l0,0a.29.29,0,0,1,.37.06l2.67,3.47,2.65-3.47a.29.29,0,0,1,.37-.06l.07.08a.35.35,0,0,1,0,.13v6.51a.25.25,0,0,1-.26.24.25.25,0,0,1-.26-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "99.063px 422.069px",
                }}
                id="elms5eyagtsmq"
                className="animable"
              />
              <path
                d="M108.36,423.73h-3.42l-.66,1.66a.26.26,0,0,1-.25.16h-.09a.24.24,0,0,1-.15-.32l2.6-6.52a.29.29,0,0,1,.26-.15.26.26,0,0,1,.25.15l2.61,6.52a.25.25,0,0,1-.14.32h-.1a.28.28,0,0,1-.25-.16Zm-3.23-.49h3l-1.51-3.78Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "106.649px 422.055px",
                }}
                id="elq4wyxssv1u"
                className="animable"
              />
              <path
                d="M112,419h-2.26a.25.25,0,1,1,0-.49h5a.25.25,0,1,1,0,.49h-2.26v6.3a.26.26,0,0,1-.52,0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "112.24px 422.032px",
                }}
                id="elwm6fq6vfgc"
                className="animable"
              />
              <path
                d="M121.42,422.28h-3.85v3a.26.26,0,0,1-.27.24.25.25,0,0,1-.26-.24v-6.55a.25.25,0,0,1,.26-.24.26.26,0,0,1,.27.24v3h3.85v-3a.25.25,0,0,1,.26-.24.26.26,0,0,1,.27.24v6.55a.26.26,0,0,1-.27.24.25.25,0,0,1-.26-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "119.495px 422.005px",
                }}
                id="el9lhcf5ifxk"
                className="animable"
              />
              <rect
                x="50.9"
                y="352.42"
                width="100.44"
                height="15.32"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "101.12px 360.08px",
                }}
                id="elb912fm8qbtl"
                className="animable"
              />
              <path
                d="M99.49,362.46a.24.24,0,0,1,.33,0,4.28,4.28,0,0,0,2.46.9,2.93,2.93,0,0,0,1.65-.46,1.27,1.27,0,0,0,.64-1,1.11,1.11,0,0,0-.61-1,5.07,5.07,0,0,0-1.73-.57h0a4.91,4.91,0,0,1-1.77-.59,1.45,1.45,0,0,1-.77-1.23,1.61,1.61,0,0,1,.78-1.31,3.15,3.15,0,0,1,1.81-.52,4.27,4.27,0,0,1,2.31.82.22.22,0,0,1,.07.31.24.24,0,0,1-.32.06,3.88,3.88,0,0,0-2.06-.74,2.8,2.8,0,0,0-1.53.42,1.19,1.19,0,0,0-.59,1,1,1,0,0,0,.56.86,4.53,4.53,0,0,0,1.6.53h0a5.29,5.29,0,0,1,1.91.64,1.55,1.55,0,0,1,.83,1.33,1.75,1.75,0,0,1-.85,1.4,3.38,3.38,0,0,1-1.93.56,4.87,4.87,0,0,1-2.76-1A.21.21,0,0,1,99.49,362.46Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "102.199px 360.275px",
                }}
                id="elhmlvb0qrnt9"
                className="animable"
              />
              <path
                d="M110.54,356.64a4.09,4.09,0,0,1,2.42.78.23.23,0,0,1,0,.35.26.26,0,0,1-.36.05,3.58,3.58,0,0,0-2.1-.68,3.08,3.08,0,1,0,0,6.14,3.35,3.35,0,0,0,2.08-.68.26.26,0,0,1,.37.06.22.22,0,0,1,0,.35,4.08,4.08,0,0,1-2.42.77,3.58,3.58,0,1,1,0-7.14Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "109.862px 360.21px",
                }}
                id="el74nfdmpb7yt"
                className="animable"
              />
              <path
                d="M115.3,357a.26.26,0,0,1,.26-.25.26.26,0,0,1,.27.25v6.55a.26.26,0,0,1-.27.24.25.25,0,0,1-.26-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "115.565px 360.27px",
                }}
                id="elihx5jzld2je"
                className="animable"
              />
              <path
                d="M118.49,357a.26.26,0,0,1,.26-.25h3.86a.25.25,0,1,1,0,.5H119V360h3.14a.25.25,0,1,1,0,.49H119v2.77h3.59a.24.24,0,0,1,.25.26.24.24,0,0,1-.25.24h-3.88a.25.25,0,0,1-.24-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "120.665px 360.255px",
                }}
                id="el9k2obzgges"
                className="animable"
              />
              <path
                d="M125.39,357.78v5.76a.26.26,0,0,1-.52,0v-6.45a.25.25,0,0,1,.1-.28.27.27,0,0,1,.36.06l4.55,5.91v-5.72a.26.26,0,0,1,.25-.25.27.27,0,0,1,.27.25v6.48a.26.26,0,0,1-.27.24.25.25,0,0,1-.2-.09s0,0,0,0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "127.63px 360.283px",
                }}
                id="elfk1acufk2wh"
                className="animable"
              />
              <path
                d="M136.66,356.64a4.12,4.12,0,0,1,2.43.78.24.24,0,0,1,0,.35.26.26,0,0,1-.36.05,3.59,3.59,0,0,0-2.11-.68,3.08,3.08,0,1,0,0,6.14,3.31,3.31,0,0,0,2.08-.68.28.28,0,0,1,.38.06.23.23,0,0,1,0,.35,4.11,4.11,0,0,1-2.43.77,3.58,3.58,0,1,1,0-7.14Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "135.984px 360.21px",
                }}
                id="elh288wgvnzm5"
                className="animable"
              />
              <path
                d="M141.35,357a.26.26,0,0,1,.26-.25h3.86a.25.25,0,1,1,0,.5h-3.59V360H145a.25.25,0,1,1,0,.49h-3.14v2.77h3.59a.24.24,0,0,1,.25.26.24.24,0,0,1-.25.24h-3.88a.25.25,0,0,1-.24-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "143.525px 360.255px",
                }}
                id="eleei3srsr8mk"
                className="animable"
              />
              <rect
                x="59.35"
                y="352.42"
                width="25.24"
                height="15.32"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "71.97px 360.08px",
                }}
                id="elubwptwqmmep"
                className="animable"
              />
              <rect
                x="64.1"
                y="354.55"
                width="15.75"
                height="2.69"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "71.975px 355.895px",
                }}
                id="el3a5jfsza8me"
                className="animable"
              />
              <rect
                x="64.1"
                y="358.73"
                width="15.75"
                height="2.69"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "71.975px 360.075px",
                }}
                id="eltxech9rjdmn"
                className="animable"
              />
              <rect
                x="64.1"
                y="362.9"
                width="15.75"
                height="2.69"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "71.975px 364.245px",
                }}
                id="el9kgpma9232f"
                className="animable"
              />
              <rect
                x="54.72"
                y="367.73"
                width="102.59"
                height="15.71"
                style={{
                  fill: "rgb(55, 71, 79)",
                  transformOrigin: "106.015px 375.585px",
                }}
                id="ellktybp791q"
                className="animable"
              />
              <rect
                x="114.52"
                y="372.83"
                width="24.64"
                height="5.52"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "126.84px 375.59px",
                }}
                id="elxrd9dgkqqed"
                className="animable"
              />
              <path
                d="M151.52,375.59a3.26,3.26,0,1,1-3.25-3A3.16,3.16,0,0,1,151.52,375.59Z"
                style={{
                  fill: "#00A4A8",
                  transformOrigin: "148.27px 375.849px",
                }}
                id="elxydvwxiu8fs"
                className="animable"
              />
              <path
                d="M64.29,372a.25.25,0,0,1,.26-.24H68.4a.25.25,0,1,1,0,.49H64.82v2.79H68a.25.25,0,1,1,0,.49H64.82v2.77H68.4a.25.25,0,1,1,0,.49H64.53a.25.25,0,0,1-.24-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "66.4949px 375.275px",
                }}
                id="elznrhiei9ddf"
                className="animable"
              />
              <path
                d="M71.19,372.83v5.75a.26.26,0,0,1-.52,0v-6.45a.23.23,0,0,1,.1-.27.27.27,0,0,1,.36.06l4.55,5.91V372.1a.25.25,0,0,1,.25-.24.26.26,0,0,1,.27.24v6.48a.26.26,0,0,1-.27.24.25.25,0,0,1-.2-.09l0,0Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "73.4291px 375.328px",
                }}
                id="elyk1nnh9v06d"
                className="animable"
              />
              <path
                d="M81.86,375.66a.24.24,0,0,1-.26-.24.25.25,0,0,1,.26-.25h2.35a.26.26,0,0,1,.24.25v2.45a.29.29,0,0,1-.1.18,4,4,0,0,1-2.42.77,3.58,3.58,0,1,1,0-7.13,4,4,0,0,1,2.43.8.23.23,0,0,1,0,.34.28.28,0,0,1-.37,0,3.41,3.41,0,0,0-2.09-.69,3.09,3.09,0,1,0,0,6.15,3.53,3.53,0,0,0,2-.6v-2.07Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "81.2363px 375.255px",
                }}
                id="elakq5kk9x0w"
                className="animable"
              />
              <path
                d="M86.82,378.82h0a.25.25,0,0,1-.26-.24V372a.25.25,0,0,1,.26-.24.26.26,0,0,1,.27.24v6.3h3.59a.25.25,0,1,1,0,.49Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "88.7699px 375.29px",
                }}
                id="elnkaesd8v8u9"
                className="animable"
              />
              <path
                d="M92.7,372a.25.25,0,0,1,.25-.24.26.26,0,0,1,.27.24v6.55a.26.26,0,0,1-.27.24.25.25,0,0,1-.25-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "92.96px 375.275px",
                }}
                id="eldz00d1iz6zb"
                className="animable"
              />
              <path
                d="M95.55,377.51a.24.24,0,0,1,.33,0,4.26,4.26,0,0,0,2.46.9,3,3,0,0,0,1.65-.46,1.27,1.27,0,0,0,.64-1,1.1,1.1,0,0,0-.61-1,5.07,5.07,0,0,0-1.73-.57h0a4.91,4.91,0,0,1-1.77-.59,1.47,1.47,0,0,1-.77-1.23,1.64,1.64,0,0,1,.78-1.32,3.22,3.22,0,0,1,1.81-.51,4.26,4.26,0,0,1,2.31.81.23.23,0,0,1,.07.32.24.24,0,0,1-.32.06,3.89,3.89,0,0,0-2.06-.75,2.72,2.72,0,0,0-1.53.43,1.19,1.19,0,0,0-.59,1,1,1,0,0,0,.56.85,4.52,4.52,0,0,0,1.6.54h0a5.29,5.29,0,0,1,1.91.64,1.57,1.57,0,0,1,.83,1.33,1.75,1.75,0,0,1-.85,1.4,3.46,3.46,0,0,1-1.93.55,4.87,4.87,0,0,1-2.76-1A.21.21,0,0,1,95.55,377.51Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "98.2686px 375.32px",
                }}
                id="el4q7uzpuqtuo"
                className="animable"
              />
              <path
                d="M107.41,375.55h-3.85v3a.26.26,0,0,1-.27.24.25.25,0,0,1-.26-.24V372a.25.25,0,0,1,.26-.24.26.26,0,0,1,.27.24v3h3.85v-3a.25.25,0,0,1,.26-.24.26.26,0,0,1,.27.24v6.55a.26.26,0,0,1-.27.24.25.25,0,0,1-.26-.24Z"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "105.485px 375.275px",
                }}
                id="elifzyqk3s7yj"
                className="animable"
              />
              <rect
                x="50.21"
                y="383.44"
                width="51.83"
                height="13.87"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "76.125px 390.375px",
                }}
                id="elo40heo4okoh"
                className="animable"
              />
              <rect
                x="102.04"
                y="383.44"
                width="47.91"
                height="13.87"
                style={{
                  fill: "rgb(38, 50, 56)",
                  transformOrigin: "125.995px 390.375px",
                }}
                id="el32pio9xk8uz"
                className="animable"
              />
              <rect
                x="50.21"
                y="384.88"
                width="51.83"
                height="10.99"
                style={{
                  fill: "rgb(255, 255, 255)",
                  transformOrigin: "76.125px 390.375px",
                }}
                id="el9tban84n4u"
                className="animable"
              />
              <path
                d="M54.72,387.14Q62.86,387,71,387l7.37-.05,16.29.05c5.43,0,1.94.08,7.37.16-5.43.08-1.94.13-7.37.16l-16.29.05L71,387.3Q62.86,387.26,54.72,387.14Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "78.375px 387.16px",
                }}
                id="eli4y1e4bzb"
                className="animable"
              />
              <path
                d="M54.72,389.29q8.14-.1,16.29-.16l7.37-.05,16.29.05c5.43,0,1.94.09,7.37.16-5.43.08-1.94.14-7.37.16l-16.29.05L71,389.45Q62.86,389.42,54.72,389.29Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "78.38px 389.29px",
                }}
                id="elg3fdb355zfe"
                className="animable"
              />
              <path
                d="M54.72,391.45q8.14-.12,16.29-.16l7.37-.05,16.29.05c5.43,0,1.94.08,7.37.16-5.43.08-1.94.13-7.37.16l-16.29.05L71,391.61Q62.86,391.56,54.72,391.45Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "78.38px 391.45px",
                }}
                id="el1tbd2lxxgaj"
                className="animable"
              />
              <path
                d="M54.72,393.61q8.14-.12,16.29-.17l7.37,0,16.29,0c5.43,0,1.94.09,7.37.17-5.43.07-1.94.13-7.37.16l-16.29,0-7.37,0Q62.86,393.73,54.72,393.61Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "78.38px 393.605px",
                }}
                id="elm7mrmfhkxl"
                className="animable"
              />
              <rect
                x="112.65"
                y="383.44"
                width="6.74"
                height="13.87"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "116.02px 390.375px",
                }}
                id="elmwcdluyebrm"
                className="animable"
              />
              <rect
                x="142.94"
                y="383.44"
                width="4.53"
                height="13.87"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "145.205px 390.375px",
                }}
                id="elg3yjsyc5hh"
                className="animable"
              />
              <g id="elisg4co6g8o">
                <rect
                  x="89.55"
                  y="397.3"
                  width="58.37"
                  height="19.06"
                  style={{
                    fill: "#00A4A8",
                    transformOrigin: "118.735px 406.83px",
                    transform: "rotate(180deg)",
                  }}
                  className="animable"
                  id="elv18vm5m0wks"
                />
              </g>
              <g id="el2joslr21rdy">
                <rect
                  x="45.23"
                  y="397.3"
                  width="44.32"
                  height="19.06"
                  style={{
                    fill: "#00A4A8",
                    transformOrigin: "67.39px 406.83px",
                    transform: "rotate(180deg)",
                  }}
                  className="animable"
                  id="el9vrnfjkpa9w"
                />
              </g>
              <g id="eli3lb9o8w5na">
                <rect
                  x="89.55"
                  y="399.28"
                  width="58.37"
                  height="15.1"
                  style={{
                    fill: "rgb(255, 255, 255)",
                    transformOrigin: "118.735px 406.83px",
                    transform: "rotate(180deg)",
                  }}
                  className="animable"
                  id="el4vx5aiuwjqn"
                />
              </g>
              <path
                d="M144.15,411.28q-6.83.11-13.65.16l-13.65.05-13.65-.05q-6.83,0-13.65-.16,6.83-.12,13.65-.16l13.65-.05,13.65.05Q137.33,411.15,144.15,411.28Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "116.85px 411.28px",
                }}
                id="elxr9uoding98"
                className="animable"
              />
              <path
                d="M144.15,408.31q-6.83.12-13.65.16l-13.65,0-13.65,0q-6.83,0-13.65-.16,6.83-.1,13.65-.16l13.65,0,13.65,0Q137.33,408.2,144.15,408.31Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "116.85px 408.31px",
                }}
                id="elut8py0otmoo"
                className="animable"
              />
              <path
                d="M144.15,405.35q-6.83.12-13.65.16l-13.65.05-13.65-.05q-6.83,0-13.65-.16,6.83-.12,13.65-.16l13.65-.05,13.65.05Q137.33,405.24,144.15,405.35Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "116.85px 405.35px",
                }}
                id="elo22sgx9pmh"
                className="animable"
              />
              <path
                d="M144.15,402.39q-6.83.12-13.65.16l-13.65.05-13.65-.05q-6.83,0-13.65-.16,6.83-.12,13.65-.16l13.65-.05,13.65.05Q137.33,402.28,144.15,402.39Z"
                style={{
                  fill: "rgb(219, 219, 219)",
                  transformOrigin: "116.85px 402.39px",
                }}
                id="elxapmba417f"
                className="animable"
              />
              <rect
                x="52.29"
                y="397.3"
                width="8.3"
                height="19.06"
                style={{
                  fill: "rgb(69, 90, 100)",
                  transformOrigin: "56.44px 406.83px",
                }}
                id="el8yx8x4qtmy8"
                className="animable"
              />
            </g>
            <defs>
              {" "}
              <filter id="active" height="200%">
                {" "}
                <feMorphology
                  in="SourceAlpha"
                  result="DILATED"
                  operator="dilate"
                  radius={2}
                />{" "}
                <feFlood floodColor="#32DFEC" floodOpacity={1} result="PINK" />{" "}
                <feComposite
                  in="PINK"
                  in2="DILATED"
                  operator="in"
                  result="OUTLINE"
                />{" "}
                <feMerge>
                  {" "}
                  <feMergeNode in="OUTLINE" />{" "}
                  <feMergeNode in="SourceGraphic" />{" "}
                </feMerge>{" "}
              </filter>{" "}
              <filter id="hover" height="200%">
                {" "}
                <feMorphology
                  in="SourceAlpha"
                  result="DILATED"
                  operator="dilate"
                  radius={2}
                />{" "}
                <feFlood
                  floodColor="#ff0000"
                  floodOpacity="0.5"
                  result="PINK"
                />{" "}
                <feComposite
                  in="PINK"
                  in2="DILATED"
                  operator="in"
                  result="OUTLINE"
                />{" "}
                <feMerge>
                  {" "}
                  <feMergeNode in="OUTLINE" />{" "}
                  <feMergeNode in="SourceGraphic" />{" "}
                </feMerge>{" "}
                <feColorMatrix
                  type="matrix"
                  values="0   0   0   0   0                0   1   0   0   0                0   0   0   0   0                0   0   0   1   0 "
                />{" "}
              </filter>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  );
}

export default UserSetup;
